/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W6 — FAN SELECTOR (§33.1) — the LARGEST + FINAL vendor
 * selector (fan.selector.js 6,249). React port into the 6e2c component-selector
 * chassis (§30) as the 'fan' tab body. REUSES all the mature W1-W5 infra:
 *   useRatingDispatch (abortable; keeps self.rate_request + aborts superseded),
 *   buildRatingEnvelope (FAN_MFG_MAPPING / FAN_FUNCTION_NAME / FAN_PLUG_TYPE_MFGS
 *   already built W3), useDependencyGraph (the fan graph rooted at mfg_id is the
 *   LARGEST consumer), RatedOutputsTable/ratingKit, getFromCmsData (via fanCms),
 *   getNetworkInternalAccess (the available_rating gate, built W3).
 *
 * Opened via the FAN FORM's rating label (FanForm FormHeading withRatingBlock
 * ratingBlockDataName='fan') → rating_mode:activate {name:'fan',
 * current_model:{id}, section}. The §30.4 fan-out key is `fan_<id>:selector:
 * <action>`.
 *
 * ★ DATA-SAFETY (§4 + the 6e2a userArmed gate — HARD REQUIREMENT):
 *   - RATE fires the EXTERNAL vendor POST /rating/ (×up-to-3 sites). chooseFan
 *     fires the per-fan curve POST. ISP/ESP fires POST /ahu/fans/<id>/isp.
 *     SELECT writes current_model.set (+ isolatorSaveData) via the form shim +
 *     persists curves (PUT ahu/fans/curves/<id>). ALL armed ONLY by a real
 *     #rate/#select click (userArmed ref). NOTHING fires on mount/open/browse.
 *   - A rated fan with saved curve points renders its STORED curve on open
 *     (538 fans on dev3) — a single read-only GET ahu/fans/curves/<id>, no
 *     vendor fetch. The curve canvas draws it read-only.
 *   - SMOKE = open / render / cancel → 0 mutating, 0 /rating/ calls.
 *
 * THE DEPENDENCY ENGINE: useDependencyGraph runs the legacy DependenciesHandler
 * cascade + VersionedDataRegistry last-write-wins. Option lists resolve through
 * the fans CMS doc (fanCms) for most fields and the master param lists
 * (fanParamUrl, resolved via fanMasterMap) for the rest. Read-only, the saved
 * value always renders.
 *
 * W6.5 fixes:
 *   F3 — on OPEN (cascadeArmed=false) the engine PRESERVES a saved value the
 *        narrowed list omits (isArmed gate in useDependencyGraph), and the
 *        master-list selects resolve the saved option's LABEL from the full
 *        master list (fanMasterMap). A real parent-change arms the cascade and
 *        the legacy narrow+drop resumes unchanged.
 *   F4 — the Motor OUTPUT tab renders the selected-motor row (getSelectedMotor →
 *        GET motors/all/<id>, read-only) via the rated-table kit.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetAhuBoardProjectQuery } from 'app/services/ahuBoardApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  RATING_STORAGE_KEYS,
  readRatingMap,
  findLastEntry,
  findEntryByMfg,
  writeRatingEntry,
  type IndependentRatingMap,
} from 'pages/dashboard/components/header/independentRatingStorage';
import { optionLabel, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import {
  ahuComponentFormsApi,
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
} from 'app/services/ahuComponentFormsApi';
import { useRatingDispatch } from './useRatingDispatch';
import { buildRatingEnvelope, FAN_PLUG_TYPE_MFGS } from './ratingEnvelope';
import { useGetNetworkInternalAccessQuery } from './ratingApi';
import type { RatingResponse } from './ratingApi';
import { useDependencyGraph, type LoadParamsFn } from './useDependencyGraph';
import {
  FAN_CMS_DOC_URL,
  FAN_SPRING_ISOLATOR_CMS_URL,
  FAN_MOTOR_BASE_CMS_URL,
  FAN_NOTES_URL,
  FAN_AIR_STREAMS_URL,
  fanMfgSettingsUrl,
  FAN_PRICING_METHODS_URL,
  MOTOR_CATALOG_BASE_URL,
  FAN_MOUNTINGS_URL,
  FAN_SPACING_SIDES_URL,
  FAN_SPACING_TOP_BTM_URL,
  FAN_SYSTEM_EFFECT_URL,
  FAN_DEPENDENCY_GRAPH,
  FAN_REQUIRED_FIELDS,
  FAN_REQUIRED_FIELDS_WITHOUT_ASTERISK,
  FAN_REQUIRED_FIELDS_WITHOUT_INPUT,
  FAN_QTY_UNSET,
  FAN_QTY_UNSET_MFGS,
  fanQtyDefaultsFor,
  FAN_QTY_PAIR_FIELD,
  fanQtyPairIsUnset,
  fanQtyPairValue,
  fanQtyPairParse,
  fanFieldIsFilled,
  FAN_DEFAULT_FIELDS,
  FAN_MFG_MAPPING,
  CURVE_VENDOR_MFGS,
  ALLOWED_FAN_PARAMS,
  FAN_RATE_DOC_FIELDS,
  FAN_RATE_MASTER_FIELDS,
  FAN_RATE_ISOLATOR_FIELDS,
  FAN_RATE_MOTOR_BASE_FIELDS,
  FAN_RATE_NUMERIC_FIELDS,
  FAN_SET_DEFAULTS,
  FAN_ISOLATION_DEFAULTS,
  FAN_ISOLATION_DEFAULT_FIELDS,
  FAN_SOLE_OPTION_EXCLUDE,
  FAN_RESET_OVERRIDES,
  FAN_RESET_BLOB_ONLY_MOTOR_KEYS,
  FAN_RESET_MOTOR_BASE_KEYS,
  fanMergeMfgOverrides,
  fanVoltageRateKey,
  fanOptionDataValue,
  FAN_RATE_MOTOR_NAME_FIELDS,
  fanMotorNameValue,
  jqDataValue,
  fanParamUrl,
  fanRatingTimestamp,
  buildFanRateInput,
  buildFanCurveEnvelope,
  buildFanSelectPayload,
  buildModelFanParams,
} from './fanSelectorData';
import {
  fanCmsOptionList,
  fanCmsOptionListByPath,
  fanCmsSelectedLabel,
  fanCmsSelectedLabelByPath,
  fanTypeValue,
  fanArrValue,
  fanMotorPositionPath,
  FAN_ISOLATOR_CMS_PATHS,
  FAN_MOTOR_BASE_CMS_PATHS,
  type FanCmsDoc,
} from './fanCms';
import {
  fanMasterOption,
  fanMasterOptionList,
  fanMasterOptionValue,
  fanMasterSelectedLabel,
} from './fanMasterMap';
import { FanPrintSheet } from './FanPrintSheet';
import { printSheetReady } from './printSheetReady';
import {
  useGetFanCurvesQuery,
  useGetFanNotesQuery,
  useGetFanAirStreamsQuery,
  useGetFanMfgSettingsQuery,
  useGetFanMotorCountQuery,
  useGetFanListByUrlQuery,
  usePostFanIspMutation,
  useSaveFanCurvesMutation,
  usePatchFanIsolatorsMutation,
} from './fanSelectorApi';
import { FanCurveCanvas, deriveVendorCurves, type FanChosenCurve } from './FanCurveCanvas';
import { FanVfdTab } from './FanVfdTab';
import {
  FanWideHighSelect,
  orderWideHighOptions,
  WideHighText,
  type WideHighOption,
} from './FanWideHighSelect';
import {
  buildVfdPayload,
  isVfdNone,
  useGetVfdCmsParamsQuery,
  VFD_CMS_PARAMS_URL,
} from './fanVfdApi';
import { persistVfdAccessory } from './fanVfdPersist';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { ComponentFormShim } from '../componentFormTypes';
import type { SelectorTabProps } from './selectorTypes';
import { guardSelect, type CascadeResponse } from '../cascadeCommit';
import { takeSectionPendingRerate } from '../../psychrometric/pendingRerate';
import { queueSectionPatch } from '../sectionPatchQueue';
import { cmsSeasonsByComponent } from '../cmsDoc';
import { MIN_AIR_TEMP_F } from '../SeasonGroup';
import type { ICmsAhuDoc } from 'models';

/** GLOBAL cms doc (legacy self.globalCms — air.season_by_component). */
const GLOBAL_CMS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/?construction_class_id=1';

const num = (v: string): number | null => {
  if (v === '' || v === 'None' || v === 'none' || v === '--') return null;
  const n = Number(v.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

/** The performance columns the Select PATCH persists (the getModelFanParams
 * overlay outputs). Mirrored into the RTKQ fan-detail cache at Select time so
 * a same-session reopen seeds the NEW numbers — legacy re-reads the live
 * current_model (fresh GET per open), but the React seed effect consumes the
 * cache synchronously and locks (seeded.current), so the reopen stored row was
 * rendering the PRE-select speed/bhp/efficiencies (P1b-verify fix, 2026-07-09). */
const DETAIL_CACHE_PERF_KEYS = [
  'speed', 'bhp', 'outlet_vel', 'static_efficiency', 'total_efficiency',
  'mech_efficiency', 'air_density', 'rated_fan_speed_max', 'fei', 'rpi',
  'bp_kw', 'rating_motor_fla', 'master_fan_id', 'rating_datetime',
  'dbi_63', 'dbi_125', 'dbi_250', 'dbi_500', 'dbi_1000', 'dbi_2000', 'dbi_4000', 'dbi_8000',
  'dbo_63', 'dbo_125', 'dbo_250', 'dbo_500', 'dbo_1000', 'dbo_2000', 'dbo_4000', 'dbo_8000',
] as const;

/** The model columns the seed effect reads back onto `state` on open — the
 * §33.1 model_data.model allow-list. Module scope because Select must mirror
 * the SAME set into the RTKQ fan-detail cache (see the commitFanChain mirror):
 * whatever the seed reads on reopen is exactly what the mirror has to carry. */
const SEED_MODEL_FIELDS = [
  'mfg_id', 'master_fan_id', 'master_motor_id', 'air_stream_id', 'airflow_actual',
  'airflow_per_fan', 'tsp', 'esp_desired', 'speed_condition_id', 'redundancy_id',
  'motor_type_id', 'motor_voltage_id', 'motor_position_id', 'mtr_controller_id',
  'mtr_supplied_by_id', 'mtr_base_supplied_by_id', 'fan_isolators_supplied_by_id',
  'inlet_guard_id', 'outlet_guard_id', 'protective_cage_id', 'fan_afs_sensor_id',
  'fan_afs_transmitter_id', 'housed_discharge_id', 'mounting_id', 'orientation_id',
  // Options-tab Finish. The seed already read it (the guardMap below maps the
  // select name onto the column), but it was missing from THIS list, and the
  // list is also what the Select-time detail-cache mirror carries — so a
  // same-session reopen seeded the PRE-select `finish_id` and the
  // "default to Mill" effect then filled the blank. Stored Rust Anode,
  // reopened on Mill, correct again only after F5 (box-anna, 2026-10-03).
  // Unlike the guards, its column and its select carry the SAME name, so one
  // entry covers both the seed read and the mirror write.
  'finish_id',
  'fan_spacing_sides_id', 'fan_spacing_top_btm_id', 'fan_spacing_ud_sides',
  'fan_spacing_ud_top_btm',
  // Selector-parity FAN restore — model columns the restored Performance /
  // Mounting controls read on reopen (fan.selector.tmpl:527/541/555/797-855).
  // Without seeding these, a saved fan reopens with the restored inputs blank.
  'discharge_id', 'system_effect_id', 'fan_inlet_design',
  'assy_height_from_floor', 'assy_distance_from_ceiling',
  'assy_distance_from_section_ups', 'bulkhead_wall_to_ahu_side_wall',
  // qty_wide/qty_high are persisted MODEL columns (fan_params blobs usually
  // lack them) — Backbone seeds state from data.model.qty_wide/qty_high
  // (fan.selector.js:3638-3639); without this the VFD by_motor call drops
  // fan_qty and one_to_all* configurations 400 server-side.
  'qty_wide', 'qty_high',
  // Dev-sync H — Annexair inputs read MODEL columns in legacy
  // (fan.selector.tmpl:509 value="{{model.fan_inlet_temp}}" / :1033
  // value="{{model.mtr_safety}}"); mtr_safety is a real column since
  // ahu/0133 (default 10). fan_params blobs never carry fan_inlet_temp
  // (0 of 612 rows on dev3), so the column is the only reopen source —
  // without it a saved Annexair fan reopens with the required DB-temp
  // blank and Rate permanently disabled.
  'fan_inlet_temp', 'mtr_safety',
  // Same shape as `fan_inlet_temp` above: both are real AHUFan columns with
  // a Django default (`max_peak_pressure_percent` 90, `fan_inlet_pressure`
  // 0.0 — ahu/models_component_fan.py:249-258), both are read from the
  // MODEL in legacy (fan.selector.tmpl:475 `{{model.max_peak_pressure_percent}}`
  // / :492 `{{model.fan_inlet_pressure}}`), and neither is ever written into
  // the fan_params blob (0 of 2353 rows on dev3), so the column is the only
  // source. The board seed spreads the whole model in legacy
  // (js:1590 `...self.model_data.model`) — this allow-list did not carry
  // them, so every fan opened with Max % of Peak and Inlet Pressure BLANK
  // where dev shows the saved 90 / 0.00, and the rate payload
  // (RATE_SEARCH_FIELDS) shipped them undefined.
  'max_peak_pressure_percent', 'fan_inlet_pressure',
  'pricing_method_id', 'cost_product_user',
  'cost_freight_user', 'net_price_product_user', 'net_price_freight_user',
  'motor_pricing_method_id', 'motor_cost_product_user', 'motor_cost_freight_user',
  'motor_net_price_product_user', 'motor_net_price_freight_user',
  // NB: mtr_base_mfg_id/series_id/mtl_id are NOT model columns — they are
  // seeded from motor_base_params below (legacy js:1556-1558), never here.
  'mounting_method_id',
] as const;

/** Membership form of the list above — `doReset` asks, for every fan_params
 *  member, whether a model column would restore it after dev's blob wipe. */
const SEED_MODEL_FIELD_SET: ReadonlySet<string> = new Set(SEED_MODEL_FIELDS);

/** The master-list (non-CMS-doc) fan selects — Controller / Supplied-by /
 * Guards / AFS / Mounting-method / Power. Their saved value's label is resolved
 * from the FULL master list (F3 fallback) when the cascade narrows it out. */
const MASTER_SELECT_FIELDS = [
  'fan_isolators_supplied_by_id', 'mtr_controller_id', 'mounting_method_id',
  'inlet_guard', 'outlet_guard', 'protective_cage', 'fan_afs_sensor',
  'fan_afs_transmitter', 'finish_id', 'motor_power',
] as const;

/** The Options pane's selects, in dev's own order (fan.selector.tmpl:860-962,
 * + Finish). ONE list, read by both the pane and the nav item's gate, so "which
 * rows are available" can't drift between them. */
const FAN_OPTIONS_ROWS: Array<[string, string]> = [
  ['inlet_guard', ' Inlet Guard'],
  ['outlet_guard', ' Outlet Guard'],
  ['protective_cage', 'Protective Cage'],
  ['fan_afs_sensor', 'AFS Sensor'],
  ['fan_afs_transmitter', 'AFS Transmitter'],
  ['finish_id', 'Finish'],
];

function parseParams(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Dev-sync H — the merged legacy warning normalization (fan.selector.js
 * :4269-4277): Annexair returns PLAIN-STRING warnings where the other vendors
 * return `{description}` objects; legacy folds both to `{description}` and the
 * tmpl renders `{{description}}`. React's warning state is string-based, so
 * fold BOTH shapes to the description text instead. */
function normalizeRateWarnings(warnings: unknown): string[] {
  if (!Array.isArray(warnings)) return [];
  return warnings
    .map((el) => (typeof el === 'string' ? el : String((el as any)?.description ?? '')))
    .filter(Boolean);
}

function parseCurveArr(raw: unknown): Array<[number, number]> | null {
  if (raw == null || raw === '') return null;
  try {
    const v = JSON.parse(String(raw));
    return Array.isArray(v) ? (v as Array<[number, number]>) : null;
  } catch {
    return null;
  }
}

// B4: 'vfd' sits after Motor / before Isolation, mirroring the Backbone
// output tab nav (fan.selector.output.tmpl:14-29 header / :40-56 ahu).
/** J6 — 'performance' joins the output tabs (fan.selector.output.tmpl:18-19,
 * between Fan and Motor). */
type OutputTab = 'fan' | 'performance' | 'motor' | 'vfd' | 'isolation' | 'notes';
// 5e-sync: 'pressure_drop' restored between Performance and Motor — dev commit
// 5fd0695113 re-adds the tab the VFD Frontend Additions dropped
// (fan.selector.tmpl:23-25 li + :603-627 pane on merged dev). This port
// mirrored the VFD-era tmpl, so it was missing here too.
// dev-parity FAN — `options` restored between Mounting and Pricing
// (fan.selector.tmpl:38-40 li + :860-962 pane). The port folded the Options
// pane's five controls into Mounting and dropped the nav item entirely.
type InputTab =
  | 'properties' | 'performance' | 'pressure_drop' | 'motor' | 'vfd'
  | 'isolation' | 'mounting' | 'options' | 'pricing';

const INPUT_TAB_LABELS: Record<InputTab, string> = {
  // Selector-parity FAN — the first input tab is labelled "Fan" in Backbone
  // (fan.selector.tmpl:16), not "Properties". Visible label only; the internal
  // key stays `properties`.
  properties: 'Fan',
  performance: 'Performance',
  pressure_drop: 'Pressure Drop',
  motor: 'Motor',
  vfd: 'VFD',
  isolation: 'Isolation',
  mounting: 'Mounting',
  options: 'Options',
  pricing: 'Pricing',
};

/**
 * dev-parity FAN — legacy `renderSelects.selectboxesWithLineOptions`
 * (fan.selector.js:8503-8530): these selects are rebuilt with a leading
 *   <option value="" data-value="None">--</option>
 * (js:8550) ahead of their real options. Every other params select gets no
 * placeholder row at all. The port emitted a BLANK option for every select,
 * which is why the Fan tab showed empty cells where dev shows "--".
 *
 * The same `data-value="None"` marker drives the greying rule in
 * checkSelectboxOptions (js:2899-2926): when the placeholder is the LAST child
 * — i.e. the field's params call came back with nothing — legacy puts
 * `non-clickable` on both the label span and the select. That is what greys
 * "Style" for a New York Blower fan.
 */
const LINE_OPTION_FIELDS = new Set<string>([
  'style_id', 'wheel_width_percent_id', 'wheel_type_id', 'wheel_dia_nominal_id',
  'amca_class_id', 'motor_speed_nominal_id', 'inlet_guard', 'outlet_guard',
  'protective_cage', 'fan_afs_sensor', 'fan_afs_transmitter',
  'motor_efficiency_id', 'motor_enclosure_id', 'motor_housing_id',
  'motor_power', 'wheel_mtl_id', 'mtr_base_series_id',
  'motor_shaft_grounding_id',
]);

/** GET /ahu/fans/<id>/pressure_drops/ response (legacy loadPressureDrops,
 * fan.selector.js:7723-7781 on merged dev — the JS survived the VFD drop;
 * only the tmpl pane was lost). */
interface PressureDropsResp {
  name?: string | null;
  air_stream?: string | null;
  rows?: Array<{
    tunnel_index?: string | number | null;
    section_name?: string | null;
    name?: string | null;
    tag?: string | null;
    apd?: number | string | null;
  }>;
  isp?: number | string | null;
  esp?: number | string | null;
  tsp?: number | string | null;
}

/** legacy loadPressureDrops fmt — toFixed(2) for numerics, '' for empty,
 * non-numeric strings pass through (js:7747-7752). */
const pdFmt = (v: unknown): string =>
  v === null || v === undefined || v === ''
    ? ''
    : isNaN(+String(v))
      ? String(v)
      : (+String(v)).toFixed(2);

/**
 * "Has VFD" when the fan has no saved value of its own — the seed, the
 * not-yet-rated branch, a Manufacturer change and Reset all read it, so the
 * rule lives in one place.
 *
 * Legacy defaulted it CHECKED for Annexair and only Annexair
 * (`has_vfd: default_mfg == 237`, fan.selector.js:1534-1535/4962-4970), on the
 * grounds that their fans ship with the drive in the motor housing. It was then
 * defaulted OFF everywhere (2026-10-03) because a pre-ticked box could leave the
 * dialog with no drive behind it — `AHUAccessoryVFD.check_vfd` reads this same
 * `fan_params.has_vfd`, so BOM, pricing and the selection validations all follow
 * it. Anna asked for CHECKED everywhere on 2026-10-05: a drive is the normal
 * case, and the hole that argument was about is mostly closed from the other
 * side — `#select` is DISABLED while the box is ticked with no catalog VFD
 * picked (the isVfdValidNow gate below). ⚠ That guarantee holds only WHILE THE
 * VFD TAB IS CLICKABLE: isVfdValidNow passes whenever the tab is disabled, and
 * `vfdTabDisabled` includes `ratedRows == null` and "no motor resolved" — so a
 * fan that reaches Select down one of those paths still stamps `has_vfd: true`
 * with nothing behind it. What it does NOT do any more is create a VFD
 * accessory row there: `persistVfd` requires a real `master_vfd_id` before it
 * will persist one, so the stamp is a flag with no drive attached and nothing
 * reaches BOM, pricing or the canvas. ⚠ Note the untick is NOT available on
 * those paths — the only `#has_vfd` checkbox lives in the VFD tab, and that tab
 * is precisely the one that is disabled. Wherever the tab IS clickable the user
 * can untick freely. A fan that SAVED a value of its own — either way — is
 * untouched.
 *
 * ⚠ The two backend readers DISAGREE about an absent key:
 * `AHUAccessoryVFD.check_vfd` (models_component_fan.py:6478) reads
 * `fan_params.get('has_vfd')`, so absent means NO drive for BOM, pricing, CAD
 * and reports, while `reset_vfd` (:5916) reads `get('has_vfd', True)`. This
 * default agrees with `reset_vfd` only. The doSelect no-op branch now stamps the
 * key whenever it is missing, which is what stops the dialog and the BOM
 * drifting apart on a fan that was saved without it.
 */
const DEFAULT_HAS_VFD = true;

/**
 * ★ Phase 6.5 — the HEADER-mode seed defaults, i.e. legacy `self.defaults`
 * (fan.selector.js:1579-1597 — `defaults.model` plus `defaultFanParams
 * .wheel_width_percent_id`). The header branch (js:702-704) seeds
 * `self.model_data = {...self.model_data, ...self.defaults}` when there is no
 * saved localStorage entry; there is deliberately NO `mfg_id` default, so a
 * first-ever open shows an unselected Manufacturer and `#rate` stays
 * non-clickable until the user picks one.
 *
 * `isp`/`esp` are 0 because the RENDER context coerces them
 * (js:2423-2424 `esp: model.esp?.toFixed(2) || 0`, same for isp) and
 * `collectData` then reads the rendered input — which is exactly why the LIVE
 * header-mode rate body carries `"isp":0,"esp":0` (discovery §3.2).
 * `get_isp_esp` itself never runs in header mode (`if (self.header) return;`
 * js:7678), so those zeros are never refreshed from the server.
 */
const HEADER_FAN_DEFAULTS: Record<string, unknown> = {
  mtr_controller_id: 1,
  qty_wide: 1,
  qty_high: 1,
  max_tsp: 0,
  max_peak_pressure_percent: 90,
  fan_inlet_temp: 70,
  // Dev-sync H — the two Annexair members of the legacy defaults.model
  // (fan.selector.js:1637-1642 on merged dev).
  fan_inlet_temp_wb: null,
  fan_inlet_pressure: 0.0,
  mtr_safety: 10,
  air_stream_id: 1,
  motor_voltage_id: 1,
  discharge_id: 2,
  nominal_diameter_id: 1,
  isp: 0,
  esp: 0,
};
// ★ 6.5 builder-3 fix: `wheel_width_percent_id: 0` was WRONG here.
// `defaults.defaultFanParams` (fan.selector.js:1580-1582) is a SIBLING of
// `defaults.model` inside `self.defaults`; it is persisted verbatim by
// saveInStorage (and is still written — see the model_data member below) but it
// is NEVER spread into the state model. The header state's fan-params source is
// `self.fanParams`, which is `{}` when there is no `model_data.model.id`
// (js:1365-1368) — so legacy opens with Wheel Width UNSET ('--'), confirmed live
// on /sizer/ (`wheel_width_percent_id` → value "", text "--"). Seeding 0 here
// made the React popup show "0" where legacy shows "--".

interface FanModel extends Record<string, unknown> {
  id?: number;
  master_fan_id?: number | null;
  fan_params?: string | null;
  motor_params?: string | null;
  motor_base_params?: string | null;
  si_params?: string | null;
  curve?: { bhp?: string; sp?: string; system_curve?: string } | null;
  construction_class_id?: number | null;
  // serializer-computed, "%.2f" strings (fan_serializers.py:238-243) — null
  // until the fan has a master fan/motor to compute the geometry from.
  min_aw_width?: string | null;
  min_aw_height?: string | null;
}

/**
 * A Fan-tab row in DEV's own wrapper shape (fan.selector.tmpl:61-75):
 *   <div class="col-xs-12 left-column|right-column">
 *     <div class="col-xs-5"><div class="row label-part">…</div></div>
 *     <div class="col-xs-7">[<div class="row"><div class="col-xs-6">] control
 *   </div>
 * The `left-column`/`right-column` classes and the col-xs-6 nesting are what
 * the sizing rules in common.css key on; the generic SelectRow used by the
 * other tabs has neither, which is why its controls shrink-to-fit.
 *
 * ⚠ MODULE SCOPE IS LOAD-BEARING — do not move this (or MotorRow /
 * FanNumberInput) back inside FanSelector's body. A component declared in the
 * render body is a NEW function identity on every render, so React sees a
 * different element type in the same slot and unmounts + remounts the whole
 * subtree instead of updating it. Every fan number box commits on `change`
 * (dev's `keyup .fan_field_key_up`, js:585) and each commit bumps `setVersion`,
 * so the first character typed rebuilt the row's DOM and the input lost focus.
 */
const FanRow = ({ side, label, unit, unitClass, rowClass, rowId, rowStyle, children }: {
  side: 'left' | 'right'; label: React.ReactNode; unit?: string;
  unitClass?: string; rowClass?: string; rowId?: string;
  rowStyle?: React.CSSProperties; children: React.ReactNode;
}) => (
  <div className={`col-xs-12 ${side}-column${rowClass ? ` ${rowClass}` : ''}`}
    id={rowId} style={rowStyle}>
    <div className="col-xs-5">
      <div className="row label-part">
        {label}
        {unit && <span className={unitClass ?? 'unit right'}>{unit}</span>}
      </div>
    </div>
    <div className="col-xs-7">{children}</div>
  </div>
);

/**
 * A Motor-tab row. dev puts `left-column`/`right-column` on the COLUMN there,
 * not the row (tmpl:991/1067), so the row is a plain `col-xs-12` — and the
 * RIGHT column splits col-xs-4 / col-xs-8 rather than the 5/7 used everywhere
 * else, which is why its controls start further left than the Fan tab's.
 */
const MotorRow = ({ label, unit, rowClass, narrow, children }: {
  label: React.ReactNode; unit?: React.ReactNode; rowClass?: string;
  narrow?: boolean; children: React.ReactNode;
}) => (
  <div className={`col-xs-12${rowClass ? ` ${rowClass}` : ''}`}>
    <div className={narrow ? 'col-xs-4' : 'col-xs-5'}>
      <div className="row label-part">
        {label}
        {unit && <span className="unit right">{unit}</span>}
      </div>
    </div>
    <div className={narrow ? 'col-xs-8' : 'col-xs-7'}>{children}</div>
  </div>
);

/**
 * A fan-dialog number box that survives being typed into.
 *
 * dev formats these at RENDER time (`validateNumbers()` → toFixed(2) + comma
 * separators) and never while the caret is in the box — its `keyup` handler
 * debounces the commit and explicitly re-seats the caret afterwards
 * (`fn.uni_tools.saveInputCursorPosition`, fan.selector.js:8886-8890). The port
 * put the formatter straight in `value=`, so a controlled re-render fired on
 * every keystroke: typing "1" into an empty decimal box rewrote it to "1.00"
 * mid-entry, and "1.5" was simply not typeable.
 *
 * Same shape as SelectorNumberInput's fix — the formatted text is shown only
 * while the field is NOT being edited: on focus the current text becomes a local
 * draft, the draft wins for as long as the box has focus, and blur drops it so
 * the freshly formatted model value comes back. Commit semantics are unchanged
 * (still every input event, dev's keyup).
 */
const FanNumberInput = ({ value, formatter = 'decimal', onCommit, ...rest }: {
  /** the RAW model value — formatting happens here. */
  value: unknown;
  formatter?: 'decimal' | 'integer';
  onCommit: (raw: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) => {
  /** non-null only while this field has focus. */
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (formatter === 'integer'
    ? formatIntegerDisplay(value)
    : formatDecimalDisplay(value));
  return (
    <input
      {...rest}
      type="text"
      data-formatter_type={formatter}
      value={shown}
      onFocus={(e) => setDraft(e.target.value)}
      onChange={(e) => { setDraft(e.target.value); onCommit(e.target.value); }}
      onBlur={() => setDraft(null)}
    />
  );
};

/**
 * One column of a legacy `.rate-list-inline` results table — the fan rated list
 * (fan.selector.output.tmpl:220-307) and the motor sheet (:456-537).
 *
 * dev gives the header cell and the body cell DIFFERENT class lists (the header
 * adds `left` + `sort-icon-wrapper`, the body adds `fan_field` / `params_fan`
 * and the `data-name` hook the formatter and the AW warning rules key on), so
 * both are carried per column rather than derived from one `colClass`.
 */
interface FanRateColumn {
  /** dev's `data-sort` key. Absent = the column is not click-to-sort. */
  sort?: string;
  head: React.ReactNode;
  headClass: string;
  headStyle?: React.CSSProperties;
  /** a function when the class list depends on the row (AW's loading-dots). */
  cellClass: string | ((row: Record<string, any>) => string);
  cellStyle?: React.CSSProperties;
  /** dev's `data-name` on the body cell. */
  cellName?: string;
  /** `index` is the row's position in the rated list — only the Wide x High
   * column needs it (its select has to say WHICH row it is re-configuring);
   * every other column ignores it. -1 where there is no rated list to index
   * into, e.g. the motor sheet, which reuses these cells. */
  render: (row: Record<string, any>, index: number) => React.ReactNode;
}

/**
 * ★ Does this rated row's REQUIRED AW W x H fit the SECTION's airway?
 *
 * Reads the verdict the REQUIRED AW column already renders (`aw_width_warning`
 * / `aw_height_warning`, set per row in `runRate` against the open-time section
 * AW) rather than recomputing it, so the order and the red numbers can never
 * disagree. A row with no verdict counts as fitting: the flags are only set
 * where the vendor returned `min_aw_width`/`min_aw_height`, and the header
 * popup has no section to compare against at all.
 */
const ratedRowFits = (row: Record<string, any>): boolean =>
  !(row.aw_width_warning || row.aw_height_warning);

/**
 * ★ The ARRAY a rated row is currently showing — the Qty Wide x High pick, i.e.
 * the row's own `qty_wide`/`qty_high`, which `chooseWideHigh` swaps wholesale
 * when the user picks another array (the variant carries its own pair).
 *
 * `null` where the pair is not a usable array: a vendor rated for one explicit
 * quantity still answers without these keys, and in that case EVERY row is
 * missing them, so the comparison below degenerates to "keep the order" rather
 * than to a wrong one.
 */
const ratedRowQty = (row: Record<string, any>): { wide: number; high: number } | null => {
  const wide = Number(row.qty_wide);
  const high = Number(row.qty_high);
  if (!Number.isFinite(wide) || !Number.isFinite(high) || wide < 1 || high < 1) return null;
  return { wide, high };
};

/**
 * ★ Smaller array before larger (Anna, 2026-10-14): fewest fans first, and
 * between two layouts of the same count (1 x 4 vs 2 x 2 vs 4 x 1) the narrower
 * one first.
 *
 * Fan count is the primary measure because that is what the quote is actually
 * made of — fans, motors, VFDs and the fan-array weight all scale with it — so
 * a model that does the duty with two fans is a cheaper answer than one that
 * needs four, whatever its bhp. The wide/high tiebreak is only there to make
 * the order total: the backend cannot tell the layouts of a count apart
 * (`group_layouts_by_fan_count`), so for every vendor but Annexair they are the
 * same rating and only the required airway differs.
 *
 * A row with no array (see `ratedRowQty`) sorts after the ones that have one.
 */
const compareRatedRowQty = (a: Record<string, any>, b: Record<string, any>): number => {
  const qa = ratedRowQty(a);
  const qb = ratedRowQty(b);
  if (!qa || !qb) return qa ? -1 : qb ? 1 : 0;
  return (
    qa.wide * qa.high - qb.wide * qb.high || qa.wide - qb.wide || qa.high - qb.high
  );
};

/**
 * ★ Rated models ordered FIT-FIRST, then SMALLEST ARRAY FIRST (Anna,
 * 2026-10-14).
 *
 * A model whose required airway exceeds the section's cannot be used there
 * without resizing the section, so the models that DO fit are the ones worth
 * reading first — the list used to interleave the two, which on a tall array
 * grid left the usable choices scrolled below a screenful of red ones.
 *
 * Within each group the rows are then ordered by the array they are showing
 * (`compareRatedRowQty`), because with Qty Wide x High on `--` the list mixes
 * arrays freely and the ascending-bhp order it arrives in has nothing to do
 * with array size — a `1 x 2` could land several rows below a `2 x 2` that fits
 * just as well, which is the one comparison a reader makes first.
 *
 * Both are PRIMARY sort keys, not a one-off arrangement: applied to the freshly
 * rated list and re-applied inside every column sort, so clicking MODEL or BHP
 * orders within one fit verdict and one array size rather than mixing either
 * back together.
 *
 * `filter` and `sort` are both stable, so whatever order the list is handed
 * (the ascending-bhp list at rate time, the clicked column after that) survives
 * inside each group. Vendor-agnostic by construction: every fan handler fills
 * `min_aw_*` from the shared `calc_required_aw` and every expanded rating
 * stamps `qty_wide`/`qty_high` per array in `qty_expansion._merge`, and a row
 * with neither keeps its place — so a list with no verdicts and no arrays at
 * all comes back in exactly the order it went in.
 */
const orderRatedRowsFitFirst = <T extends Record<string, any>>(rows: T[]): T[] => [
  ...rows.filter((r) => ratedRowFits(r)).sort(compareRatedRowQty),
  ...rows.filter((r) => !ratedRowFits(r)).sort(compareRatedRowQty),
];

/**
 * The Fan output pane's two columns (results table + curve), in whichever of
 * dev's two wrappers the state calls for.
 *
 * `wrapped` is dev's `{{#if selected}}` (fan.selector.output.tmpl:74): the
 * STORED-rated-fan branch nests the columns one level deeper, in a
 * `.col-xs-12.full-height` flex row, and neutralises that wrapper's LEFT gutter
 * with `no-right-padding` on the row (`.fan-rating-mode .no-right-padding > div
 * { padding-left: 0 !important }`, common.css:12802) — so the pair ends up 15px
 * narrower on the right than in the rate-list branch (:217), which has the
 * columns straight in the row.
 */
const PaneColumns = ({ wrapped, children }: { wrapped: boolean; children: React.ReactNode }) => (
  <div className={`row full-height${wrapped ? ' no-right-padding' : ''}`}>
    {wrapped ? (
      <div className="col-xs-12 full-height" style={{ display: 'flex' }}>{children}</div>
    ) : (
      children
    )}
  </div>
);

/** dev's `.rate-list-inline` table body cell. */
const FanRateCell = ({
  col,
  row,
  index = -1,
}: {
  col: FanRateColumn;
  row: Record<string, any>;
  index?: number;
}) => (
  <div
    className={typeof col.cellClass === 'function' ? col.cellClass(row) : col.cellClass}
    style={col.cellStyle}
    data-name={col.cellName}
  >
    {col.render(row, index)}
  </div>
);

// ---------------------------------------------------------------------------
// the dialog's select controls
// ---------------------------------------------------------------------------
/**
 * ★ THESE SIT AT MODULE SCOPE ON PURPOSE — same reason as `CoilFieldCtx` in
 * CoilSelector.tsx, and the same reported bug.
 *
 * Declared inside `FanSelector`, each render handed React a NEW component type,
 * and React answers a changed type by UNMOUNTING the old subtree rather than
 * updating it. Picking a value re-renders the dialog, so the commit threw away
 * the very `<select>` the user had just changed: measured on the box, one
 * manufacturer pick replaced 19 of the pane's 39 selects, focus fell to
 * `<body>`, and the following Tab went nowhere (it lands back on the same
 * control). A stable component type keeps the DOM node, and the focus, alive
 * across the commit.
 *
 * The volatile render state arrives through `FanFieldCtx`, rebuilt on every
 * `FanSelector` render.
 */
interface FanFieldCtxValue {
  /** the live selector state (`state.current`). */
  s: Record<string, any>;
  fanDoc: unknown;
  masterLists: Record<string, OptionRow[]>;
  masterFullLists: Record<string, OptionRow[]>;
  detail: unknown;
  /** the available_rating gate, or null when it does not apply. */
  allowedMfgIds: Set<number> | null;
  /** `graph.optionIds` — the cascade's narrowed option ids for a field. */
  optionIds: (field: string) => Array<number | string> | null;
  docFieldHasOptions: (name: string) => boolean;
  onSearchField: (name: string, raw: string) => void;
}

const FanFieldCtx = createContext<FanFieldCtxValue | null>(null);

function useFanFields(): FanFieldCtxValue {
  const ctx = useContext(FanFieldCtx);
  if (!ctx) throw new Error('a fan field control was rendered outside FanSelector');
  return ctx;
}

// ----- doc select (most fan params) — the bare <select>, no label row. Used
// on its own for the Type row's "Housing" partner (tmpl:92-96). -----
const BareDocSelect = ({ name, labelKey, disabled, cls }: {
  name: string; labelKey?: string; disabled?: boolean; cls?: string;
}) => {
  const { s, fanDoc, detail, allowedMfgIds, optionIds, onSearchField } = useFanFields();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  // narrow by the cascade's option ids. `optionIds` returns NULL until the
  // field's params call has ever landed (and a failed load never commits, so
  // the prior list survives) — only THAT case falls back to the full doc list.
  // An EMPTY committed list means the endpoint really said "no options", and
  // dev then shows just the "--" row; the port collapsed both cases to the
  // full list, so e.g. Style listed every style for a manufacturer that has
  // none. A saved value still renders its own option below (the W4/W5 lesson),
  // which is what that fallback was actually protecting.
  const ids = optionIds(name);
  const rows = ids
    ? fanCmsOptionList(fanDoc as FanCmsDoc, name, ids, labelKey)
    : fanCmsOptionList(fanDoc as FanCmsDoc, name, null, labelKey);
  const hasRow = rows.some((r) => String(r.id) === valStr);
  const selLabel = valStr ? fanCmsSelectedLabel(fanDoc as FanCmsDoc, name, value as any, labelKey) : '';
  const lineOption = LINE_OPTION_FIELDS.has(name);
  // greyed when the narrowed list is empty, or by an explicit per-mfg gate.
  // dev's test is `lastElementChild == null || .dataset.value == "None"`
  // (js:2899-2926): renderSelects rebuilds the innerHTML, so a field whose
  // params call returned nothing has NO option elements at all and greys —
  // whether or not it is one of the "--" placeholder fields. That is what
  // greys "Position" on the Motor tab. React always emits a blank <option> to
  // keep the select controlled, so the equivalent test is on the ROWS.
  const greyed = disabled || rows.length === 0;
  const visibleRows = rows.filter(
    (r) => name !== 'mfg_id' || !allowedMfgIds || allowedMfgIds.has(Number(r.id))
  );
  // dev emits NO placeholder row for a non-"--" select: renderSelects
  // (js:8544-8551) prepends `<option value="">--</option>` only for
  // `selectboxesWithLineOptions`, and `redundancy_id` is not one of them. So
  // when the state has no value the browser falls back to the FIRST real
  // option and the field reads "None" — which is also what the rate reads
  // (`select#redundancy_id.val() || state.get(…)`, js:5396-5399). The port's
  // "keep the select controlled" blank made that phantom row visible and
  // pre-selected, most obviously in the header's Independent Rating popup
  // where nothing seeds redundancy_id (HEADER_FAN_DEFAULTS mirrors legacy's
  // `self.defaults`, which has no such member). Mirror the browser's implicit
  // pick instead of inventing a row; state is left alone, exactly as on dev.
  const implicitFirst =
    !lineOption && valStr === '' && visibleRows.length > 0 ? String(visibleRows[0].id) : null;
  return (
    <select name={name} data-name={name}
      className={`${cls ?? 'search_field params_fan'}${greyed ? ' non-clickable' : ''}`}
      value={implicitFirst ?? valStr}
      disabled={disabled}
      data-generic={name === 'mfg_id' ? String((detail as any)?.generic_mfg ?? 'none') : undefined}
      onChange={(e) => onSearchField(name, e.target.value)}>
      {lineOption
        ? <option value="" data-value="None">--</option>
        : valStr === '' && implicitFirst == null && <option value="" />}
      {visibleRows
        .map((r) => (
          <option key={String(r.id)} value={String(r.id)} data-value={String(r.id)}
            data-generic={(r as any).generic_mfg ? 'true' : 'none'}>
            {String(r.label ?? '')}
          </option>
        ))}
      {valStr !== '' && !hasRow && <option value={valStr}>{selLabel || valStr}</option>}
    </select>
  );
};

/** FanRow + a doc select, with dev's own class list on the <select> and the
 * half-width `col-xs-6` nesting when `half`. */
const FanSelectRow = ({ side, name, label, labelKey, unit, disabled, half, rowClass, cls }: {
  side: 'left' | 'right'; name: string; label: string; labelKey?: string;
  unit?: string; disabled?: boolean; half?: boolean; rowClass?: string; cls?: string;
}) => {
  const { docFieldHasOptions } = useFanFields();
  const greyed = disabled || !docFieldHasOptions(name);
  const control = <BareDocSelect name={name} labelKey={labelKey} disabled={disabled} cls={cls} />;
  return (
    <FanRow side={side} unit={unit} rowClass={rowClass} label={
      <span data-name={name} className={greyed ? 'non-clickable' : undefined}>{label}</span>
    }>
      {half ? <div className="row"><div className="col-xs-6">{control}</div></div> : control}
    </FanRow>
  );
};

// ----- master-list dependent select (supplied-by / guards / controller / etc.) -----
// Option value/label resolve through fanMasterMap (the legacy structural
// switch — nested short-name id is the VALUE, .name/.model is the TEXT).
// F3: when the cascade narrows the saved value out, its label is resolved from
// the FULL (unfiltered) master list so the saved option renders its label, not
// a bare id. The engine's narrowing/re-pick on an ARMED parent change is
// untouched — this only adds a render option for a value already in state.
/** the bare <select> — used on its own inside dev's Motor-tab row wrappers. */
const BareMasterSelect = ({ name, disabled, cls }: {
  name: string; disabled?: boolean; cls?: string;
}) => {
  const { s, masterLists, masterFullLists, onSearchField } = useFanFields();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const rows = fanMasterOptionList(name, masterLists[name] as any[] | undefined);
  const hasRow = rows.some((r) => String(r.id) === valStr);
  // fallback label: full list (F3) first, then the narrowed list, then the id.
  const selLabel = valStr
    ? fanMasterSelectedLabel(name, value as any, masterFullLists[name] as any[] | undefined)
      || fanMasterSelectedLabel(name, value as any, masterLists[name] as any[] | undefined)
    : '';
  const lineOption = LINE_OPTION_FIELDS.has(name);
  const greyed = disabled || rows.length === 0;
  return (
    <select name={name} data-name={name}
      className={`${cls ?? 'search_field'}${greyed ? ' non-clickable' : ''}`} value={valStr}
      disabled={disabled}
      onChange={(e) => onSearchField(name, e.target.value)}>
      {lineOption
        ? <option value="" data-value="None">--</option>
        : valStr === '' && <option value="" />}
      {rows.map((r) => (
        <option key={String(r.id)} value={String(r.id)}>
          {String(r.label ?? '')}
        </option>
      ))}
      {valStr !== '' && !hasRow && <option value={valStr}>{selLabel || valStr}</option>}
    </select>
  );
};

export const FanSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
  headerMode = false,
  eventScope,
  bus: busProp,
  setPrintEnabled,
}: SelectorTabProps) => {
  const eventName = (activate.name as string) || 'fan';
  // §30.4 fan-out namespace: `fan_<id>` on the board, `fan_header` in the header
  // popup (component_selector.js:280 `(self.current_model) ? … : 'header'`).
  const scope = eventScope ?? String(modelId);
  // Isolated bus in header mode so the UN-namespaced emits below
  // (rating_mode:mounting:changed, cancel:dialog:changes, …) can never reach a
  // board rating dialog open on the same page — discovery §7.3 trap 1.
  const bus = busProp ?? ahuBoardBus;
  // Own-subtree scope for the few `#rate` / `#fan-selector` DOM reads. Legacy
  // reaches those with DOCUMENT-WIDE jQuery selectors (fan.selector.js:2604,
  // 2925, 3117, 4099, 5300, … `$('.control-buttons button[id="select"]')`); on a
  // page hosting BOTH a header popup and a board dialog those cross-contaminate
  // (discovery §7.3 trap 1). Every DOM read here is scoped to this selector's
  // own `.ahu-rate-content` ancestor.
  const rootRef = useRef<HTMLDivElement | null>(null);
  // the `.fan_rate-list-inline` scroll box — legacy resets its scrollTop after a
  // header sort (fan.selector.js:2973-2975).
  const rateListRef = useRef<HTMLDivElement | null>(null);
  const chassisEl = useCallback(
    (): HTMLElement | null => rootRef.current?.closest('.ahu-rate-content') ?? null,
    []
  );
  // ★ QTY WIDE x HIGH select width (Anna, 2026-10-08): the select is as wide as
  // the COLUMN HEADING TEXT, not as the 185px `lmedium` column, and hangs off
  // the right edge under it. The heading is uppercased by CSS and the table is
  // in the legacy font, so the width is measured from the rendered heading span
  // rather than hard-coded — a label edit or a font change keeps the two in
  // step instead of silently drifting apart. `offsetWidth` (not
  // getBoundingClientRect) because the board is CSS-zoomed in places and only
  // the untransformed layout width is meaningful as a CSS `width`.
  //
  // Measured through a ResizeObserver, not once on mount: the rating dialog's
  // panes are mounted hidden and shown later, so the heading reads 0 at commit
  // time and only takes its real width when the Fan output tab becomes
  // visible. 0 keeps the pre-change full-column width as the fallback.
  const [qtyHeadWidth, setQtyHeadWidth] = useState<number | null>(null);
  const qtyHeadObserver = useRef<ResizeObserver | null>(null);
  const measureQtyHead = useCallback((el: HTMLSpanElement | null) => {
    qtyHeadObserver.current?.disconnect();
    qtyHeadObserver.current = null;
    if (!el) return;
    const read = () => setQtyHeadWidth(el.offsetWidth || null);
    read();
    if (typeof ResizeObserver !== 'undefined') {
      qtyHeadObserver.current = new ResizeObserver(read);
      qtyHeadObserver.current.observe(el);
    }
  }, []);
  // fan.selector.js:2415 `prefix: self.header ? '' : 'ahu-'` — the tab-pane DOM
  // ids drop the prefix in header mode, which is exactly what keeps the header
  // popup from colliding with the board dialog's ids.
  const idPrefix = headerMode ? '' : 'ahu-';

  // ---- read-only loads (fire on mount; never mutate) ----
  // ★ HEADER MODE: there is no current_model, so NO component-detail read at all
  // (fan.selector.js:602 `self.fanModel = options.current_model` → undefined).
  // `modelId` is 0 there; `skip` mirrors legacy's absent fetch exactly.
  const { data: detail, isLoading: detailLoading } = useGetComponentDetailQuery(
    { plural: 'fans', id: modelId },
    { refetchOnMountOrArgChange: true, skip: !modelId }
  );
  const { data: fanDoc, isLoading: fanDocLoading } = useGetComponentCmsDocQuery(FAN_CMS_DOC_URL);
  // J6 — the GLOBAL cms doc (legacy self.globalCms): air.season_by_component
  // .Fan feeds the Performance tab's Season select (RTKQ-cached).
  const { data: globalDoc } = useGetComponentCmsDocQuery(GLOBAL_CMS_URL);
  const { data: isolatorDoc } = useGetComponentCmsDocQuery(FAN_SPRING_ISOLATOR_CMS_URL);
  // motor-base CMS doc — the mtr_base_* rate-envelope values resolve from it.
  const { data: motorBaseDoc } = useGetComponentCmsDocQuery(FAN_MOTOR_BASE_CMS_URL);
  const airStreamsQ = useGetFanAirStreamsQuery(FAN_AIR_STREAMS_URL);
  // Pricing tab — the real pricing_methods collection (legacy
  // fan/collection/pricing_methods.js). The port hardcoded By Application /
  // Cost by User / Price by User.
  const pricingMethodsQ = useGetFanListByUrlQuery(FAN_PRICING_METHODS_URL);
  // the available_rating gate (per-user vendor access — built W3).
  const { data: networkAccess } = useGetNetworkInternalAccessQuery();
  // the STORED curve for a rated fan (read-only GET; rendered on open).
  // Header mode has no fan row → no curve GET (legacy getCurves is only reached
  // from the current_model branch).
  const { data: storedCurves } = useGetFanCurvesQuery(modelId, { skip: !modelId });
  // The PROJECT's Altitude. Legacy collectData ends with
  // `self.data["elevation"] = self.model_data.project.elevation` (js:4683) — the
  // value is on the rating envelope for EVERY manufacturer, and the same field
  // backs the read-only Altitude box (fan.selector.tmpl:601
  // `value="{{project.elevation}}"`).
  //
  // `activate.project` is NEVER populated: the board's only
  // `rating_mode:activate` emitter (useComponentForm.openRatingStub) sends
  // `{name, loading, current_model, section, section2}` and no project, so this
  // read was always `undefined` — the Altitude box rendered blank and
  // `elevation` was dropped from the POST body by JSON.stringify. Fetch the
  // project the way CoilSelector (:948) and GasHeaterSelector (:1954) already
  // do; the board bootstrap has it cached, so this is a warm read.
  //
  // ★ HEADER MODE keeps the legacy no-value behaviour: there is no `ahu` there,
  // so the query is skipped and `elevation` stays undefined — legacy's
  // `self.model_data.project` is undefined in that host too (js:4683 throws and
  // the key never lands on the envelope).
  const { data: boardProject } = useGetAhuBoardProjectQuery(
    (ahu as { project_id?: number } | null | undefined)?.project_id ?? skipToken
  );
  const projectElevation =
    (boardProject as Record<string, unknown> | undefined)?.elevation ??
    (activate.project as Record<string, unknown> | undefined)?.elevation;
  // The PRINT SHEET's report furniture — legacy print() reads
  // `user.get('report_color')` and `self.project.get('report_logo'/'report_co')`
  // (fan.selector.js:8175-8177). The same three reads CoilSelector already does
  // (coil.selector port :948-959), off the project record fetched above.
  const reportColor = useAppSelector(
    (st) => (st.auth.user as { report_color?: string } | null)?.report_color
  );
  const printProject = (boardProject ??
    (activate.project as Record<string, unknown> | undefined) ??
    {}) as Record<string, unknown>;

  // ---- state model (legacy state Backbone model) ----
  const state = useRef<Record<string, unknown>>({});
  const init = useRef<Record<string, unknown>>({});
  const seeded = useRef<number | null>(null);
  // Dev-sync J6 — sectionDraft (fan.selector.js:654): season_id + the
  // per-season EADB/EAWB stage on a DETACHED section copy; commitFanChain
  // persists it on Select (:7350-7376).
  const sectionDraft = useRef<Record<string, unknown>>({});
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // ★ The manufacturer's notes — `new NotesCollection({component_mfg_id})`, whose
  // url is `…/ahu/fans/notes/?component_mfg_id=<mfg>` (fan/collection/notes.js).
  // The port dropped the query string and fetched the collection UNFILTERED, so
  // both the Notes output pane and the print sheet listed every manufacturer's
  // notes: 15 rows across 5 manufacturers on dev3 where dev shows at most 4.
  //
  // On the print sheet that is not just extra text — it is the reported garbled
  // PDF. `table.curve tr.notes td > span` is `position:absolute` and the
  // stylesheet staggers only the FIRST FOUR (`:nth-of-type(1…4)`, margin-top
  // 10/25/40/55pt — common.css:23606-23617). A fifth note and beyond have no
  // offset at all, so all eleven surplus notes painted on top of each other.
  //
  // Read off the state ref, not a hook dep: every state write goes through
  // `bump()`, so the next render passes the new url and RTKQ refetches. (Legacy
  // caches the collection on `self.notes` and never refetches on a manufacturer
  // change, js:8192 `if (!self.notes && …)` — a stale-notes bug we are not
  // reproducing; the sheet prints the notes of the manufacturer it is printing.)
  const notesQ = useGetFanNotesQuery(
    state.current.mfg_id
      ? `${FAN_NOTES_URL}?component_mfg_id=${state.current.mfg_id}`
      : skipToken
  );

  const userArmed = useRef(false);
  // W6.5 F3: armed once the user makes a real parent-change (a search-field
  // edit that drives the cascade). The initial open/seed cascade runs with this
  // false so a saved value the narrowed list omits is PRESERVED; a real change
  // arms it and the legacy narrow+drop behaviour resumes.
  const cascadeArmed = useRef(false);
  // `isRating` (the mutation's own isLoading) is deliberately NOT read here —
  // see the `.fan-preloader` note at the bottom of the render: it cannot tell a
  // rate from a per-row curve fetch, and `rating` spans the rate more faithfully.
  const { dispatchRating, abortRating } = useRatingDispatch(
    headerMode ? 'header' : 'board'
  );
  const [postFanIsp] = usePostFanIspMutation();
  const [saveFanCurves] = useSaveFanCurvesMutation();
  /** legacy `self.master_fan_id` — the catalog fan resolved for the chosen
   * rated row, stamped by the motor-list effect and read by the isolator PATCH. */
  const masterFanIdRef = useRef<number | null>(null);

  // Qty Wide x High ranges — legacy updateQty fetches the per-fan-type mfg
  // settings for the CURRENT type+arrangement and expands 1..max_qty_wide /
  // 1..max_qty_high into the two selects (fan.selector.js:3783-3849). The
  // arr=4 + dwdi combination has no settings row and legacy skips the fetch.
  const qtySettingsUrl = (() => {
    const t = fanTypeValue(fanDoc as FanCmsDoc, state.current.type_id as never);
    const a = fanArrValue(fanDoc as FanCmsDoc, state.current.arr_id as never);
    if (!t || !a || (a === '4' && t === 'dwdi')) return undefined;
    return fanMfgSettingsUrl(t, a);
  })();
  const qtySettingsQ = useGetFanMfgSettingsQuery(qtySettingsUrl ?? '', {
    skip: !qtySettingsUrl,
  });

  // "Available Motors: N" — legacy getMotorCount (js:2087-2172). Same filter
  // set, same omit-if-blank semantics as its ResultsFilter, and the motor mfg
  // 67 (Ziehl-Abegg) `motor_`-prefixed key swap at :2090-2094. The port left
  // dev's `#available_motors_count` span in the markup but never filled it.
  const motorCountUrl = (() => {
    const st = state.current;
    const pre = Number(st.motor_mfg_id) === 67 ? 'motor_' : '';
    const params: Array<[string, unknown]> = [
      [`${pre}mfg_id`, st.motor_mfg_id],
      [`${pre}series_id`, st.motor_series_id],
      ['efficiency_id', st.motor_efficiency_id],
      ['enclosure_id', st.motor_enclosure_id],
      ['housing_id', st.motor_housing_id],
      // legacy drops motor_power for a plug fan (type 2)
      ['motor_power', Number(st.type_id) === 2 ? null : st.motor_power],
      ['speed_nominal_id', st.motor_speed_nominal_id],
      ['type_id', st.motor_type_id],
      ['shaft_grounding_id', st.motor_shaft_grounding_id],
      ['fan_type_id', st.type_id],
      ['fan_arr_id', st.arr_id],
      ['fan_mfg_id', st.mfg_id],
      ['fan_series_id', st.series_id],
      ['fan_amca_class_id', st.amca_class_id],
      ['voltage_id', st.motor_voltage_id],
    ];
    const qs = params
      .filter(([, v]) => v != null && v !== '' && v !== 'None')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    if (!qs) return undefined;
    return `${MOTOR_CATALOG_BASE_URL}count/?${qs}`;
  })();
  const motorCountQ = useGetFanMotorCountQuery(motorCountUrl ?? '', {
    skip: !motorCountUrl,
  });
  const availableMotorCount = motorCountQ.data;

  const [inputTab, setInputTab] = useState<InputTab>('properties');
  const [outputTab, setOutputTab] = useState<OutputTab>('fan');
  // Selector-parity FAN restore — the Motor tab's two Backbone sub-tabs
  // (fan.selector.tmpl:979-984): "motor" (#properties_more) and "motor base"
  // (#motor_base_more).
  const [motorSubTab, setMotorSubTab] = useState<'motor' | 'motor_base'>('motor');

  // ---- Pressure Drop tab data (legacy loadPressureDrops js:7723 — re-fetched
  // on every tab activation, read-only GET; preloader while in flight). ----
  const [pressureDrops, setPressureDrops] = useState<PressureDropsResp | null>(null);
  const [pressureDropsLoading, setPressureDropsLoading] = useState(false);
  useEffect(() => {
    if (inputTab !== 'pressure_drop' || !modelId) return undefined;
    let cancelled = false;
    setPressureDropsLoading(true);
    void fetch(`/api/v1.0/ahu/fans/${modelId}/pressure_drops/`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((resp: PressureDropsResp | null) => {
        if (cancelled) return;
        if (resp) setPressureDrops(resp);
        setPressureDropsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setPressureDropsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inputTab, modelId]);

  // the rated fans from a fresh Rate (or the stored single row on open).
  const [ratedRows, setRatedRows] = useState<Array<Record<string, any>> | null>(null);
  const [chosenIndex, setChosenIndex] = useState<number | null>(null);
  // Render-time mirrors so the header-mode `saveInStorage` (legacy reads the
  // synchronous `self.rated_fans` / `self.choosen_index`) has a default source.
  // Call sites that mutate them in the SAME tick as a save pass explicit
  // overrides — see saveInStorage(over).
  const ratedRowsRef = useRef<Array<Record<string, any>> | null>(null);
  ratedRowsRef.current = ratedRows;
  const chosenIndexRef = useRef<number | null>(null);
  chosenIndexRef.current = chosenIndex;
  // ----- the rated-list sort state (legacy `self.sort_by` / `self.sort_order`).
  // Seeded to bhp/desc in initialize (fan.selector.js:620-621) and re-stamped
  // after every rate (js:4436-4437), which is why dev opens the list with the
  // Op. Pwr arrow already lit. NOTE legacy's `desc` is ASCENDING (`a[k]-b[k]`,
  // js:2959-2970) — the names are inverted in the original and are kept here so
  // the arrow classes (`sort-icon selected rotated`) map straight across.
  const [ratedSort, setRatedSort] = useState<{ by: string; order: 'asc' | 'desc' }>({
    by: 'bhp',
    order: 'desc',
  });
  const [rateErrors, setRateErrors] = useState<string[]>([]);
  const [rateWarnings, setRateWarnings] = useState<string[]>([]);
  const ratedDatetime = useRef<string | null>(null);
  // True once a Rate ran IN THIS DIALOG SESSION. ratedDatetime cannot serve
  // this role: it is re-seeded from the STORED rating_datetime on open (for
  // the payload/rated-header display), so it is non-null for every saved fan.
  const sessionRated = useRef(false);
  // ---- the RATE overlay (legacy `.fan-preloader`) ---------------------------
  // `isRating` on its own is RTKQ's isLoading for the /rating/ POST, so it drops
  // the moment the response lands — before the rows are mapped and committed.
  // Legacy holds its preloader across the WHOLE handler: appended at the top of
  // the rate (fan.selector.js:4405-4409) and only de-activated inside
  // `render_outputs`, once the outputs have been built (js:3025-3034,
  // `.find(".preloader.dotted-preloader").removeClass("active")`). `rating`
  // reproduces that span; `rateSeq` stops a superseded (aborted) rate from
  // clearing the overlay that the rate which replaced it has just raised.
  const [rating, setRating] = useState(false);
  const rateSeq = useRef(0);
  // the per-row curve cache (legacy rated_fans[i].choosen) + the active curve.
  const [chosenCurve, setChosenCurve] = useState<FanChosenCurve | null>(null);
  /**
   * dev's `self.loading` (fan.selector.js:4039) — TRUE for the whole duration of
   * the SECOND, per-row `/rating/` POST that fetches a curve vendor's curve.
   * It is not cosmetic: dev raises a preloader over the curve column AND
   * DISABLES #select / #print while it is up (js:3163-3183). Both matter, and
   * ebm-papst is where it shows — its GET_FAN_CURVE round-trip through the
   * legacy Windows box takes ~20s, so without the flag the port drew an empty
   * plot with empty sound bands and no sign anything was happening (the
   * reported "curves are not drawn"), and a Select pressed inside that window
   * persisted `sp`/`bhp`/`system_curve` as the string "null" — the rated row
   * alone carries no curve keys — losing that fan's curve for good.
   *
   * `curveSeq` is `rateSeq`'s counterpart: a superseded fetch (dispatchRating
   * aborts the in-flight one on resubmit) must not lower the flag the fetch
   * that replaced it has just raised.
   */
  const [curveLoading, setCurveLoading] = useState(false);
  const curveSeq = useRef(0);
  // selected (already-saved) read-only output row (legacy self.selected).
  const [selectedRow, setSelectedRow] = useState<Record<string, any> | null>(null);
  /**
   * dev's `choosenFanData` (fan.selector.js:3061-3067) — the render context's
   * `choosen`, which is the curve object with the CHOSEN ROW laid over it:
   * `self.choosen && self.choosen_all ? {...self.choosen, ...self.choosen_all[i]} : self.choosen`.
   * The sound bands under the curve read it (fan.selector.output.tmpl:347-371);
   * the canvas does NOT — dev draws from `self.choosen` alone.
   * `ratedRows` covers both of dev's branches: a fresh rate fills it from the
   * vendor response, and a reopen seeds it with `[buildSelectedRow(...)]`, which
   * is dev's `self.choosen = self.selected` (js:3870/3901).
   *
   * The ROW — not the curve — is what decides whether the bands render, because
   * that is what dev's `self.choosen` IS from the moment a rate lands:
   * `self.choosen = self.rated_fans[i]` (js:6038, before `chooseFan()` runs) and
   * `self.choosen = self.rated_fans?.[self.choosen_index]` on every row click
   * (js:6244). For the curve vendors `chooseFan` then fires a SECOND /rating/
   * POST, and gating on the curve would blank the sound rows for the whole of
   * that round-trip — and permanently if it comes back empty — where dev has
   * been showing them since the first response.
   */
  const choosenFanData = useMemo<Record<string, any> | null>(() => {
    const row = chosenIndex != null ? ratedRows?.[chosenIndex] : null;
    if (!row && !chosenCurve) return null;
    return { ...(chosenCurve ?? {}), ...(row ?? {}) };
  }, [chosenCurve, ratedRows, chosenIndex]);
  // F4: the saved (selected) motor row for the Motor OUTPUT tab (legacy
  // selectedMotorData via getSelectedMotor → GET motors/all/<id>, read-only).
  const [selectedMotor, setSelectedMotor] = useState<Record<string, any> | null>(null);
  /**
   * F4b — the RATED motor list (legacy `self.motorModels`, fan.selector.js
   * chooseFan :6444-6630). Populated from the motor catalogue once a rated fan
   * row is chosen IN SESSION; `null` means "no list", which is the branch that
   * falls back to the single saved-motor row (fan.selector.output.tmpl:472's
   * `{{#unless motorModels.length}}`). `motorPick` is legacy's
   * `motor_choosen` + `motor_choosen_index` pair.
   */
  const [motorModels, setMotorModels] = useState<Array<Record<string, any>> | null>(null);
  const [motorPick, setMotorPickState] = useState<{ index: number; row: Record<string, any> } | null>(
    null
  );
  /** how many motor rows the catalogue says exist for the current filters. */
  const [motorTotal, setMotorTotal] = useState<number | null>(null);
  /** the page-1 query string the scroll pager keeps requesting (page=N&…). */
  const motorQueryRef = useRef<string | null>(null);
  const motorPageRef = useRef(0);
  const motorLoadingRef = useRef(false);

  // combinations popup (available_combinations).
  const [combinationsOpen, setCombinationsOpen] = useState(false);

  // dev-sync F Stage 2 — NYB "How to" popup (fan.selector.js:6567-6589,
  // how_to.tmpl). Legacy state is "is .active on the singleton .fan-how-to-popup
  // it appended to #ksdm-content"; React mirrors FilterSelector's shipped
  // precedent (FilterSelector.tsx:167) and keeps it as component state.
  const [howToOpen, setHowToOpen] = useState(false);

  // ---- VFD accessory state (legacy self.vfd / self.vfd_position /
  // self.vfd_params — fan.selector.js _getVFD 8763-8834). Owned HERE so B3
  // (persist on Select + isVfdValid #select gating) and B4 (the VFD output
  // tab) can read them; FanVfdTab seeds + edits them and bump()s. ----
  const vfdRef = useRef<Record<string, any> | null>(null);
  const vfdPositionRef = useRef<Record<string, any>>({ mounting_plane_id: 5 });
  const vfdParamsRef = useRef<Record<string, any>>({});
  // legacy self._vfdQtyTotal — written by FanVfdTab's qty chain, read by the
  // B4 VFD output tab (render context vfd_qty, js:2835).
  const vfdQtyTotalRef = useRef<number | null>(null);
  // the vfd CMS params doc — the B4 output tab resolves the VFD-to-Motor
  // Configuration NAME from it (render context vfd_cms, js:2826-2834). Same
  // RTKQ cache entry as FanVfdTab's copy — no extra request.
  const { data: vfdCmsDoc } = useGetVfdCmsParamsQuery(VFD_CMS_PARAMS_URL);
  // render-computed vfdTabDisabled mirrored into a ref so the doSelect-time
  // isVfdValid check reads the CURRENT gating (the masterListsRef pattern).
  const vfdTabDisabledRef = useRef(false);
  // has_vfd stays in the state blob (legacy changeParams js:4621-4630).
  const setHasVfd = useCallback(
    (checked: boolean) => {
      state.current = { ...state.current, has_vfd: checked };
      bump();
    },
    [bump]
  );

  // isVfdValid (js:8649-8655): true unless "Has VFD" is checked without a
  // usable catalog VFD; a non-clickable VFD tab always passes. Shared by the
  // render (vfd-tab-invalid class) and the doSelect gate/persist so the two
  // can never disagree.
  const isVfdValidNow = useCallback((): boolean => {
    if (!state.current.has_vfd || vfdTabDisabledRef.current) return true;
    const v = vfdRef.current;
    if (!v) return false;
    return !isVfdNone(v.master_vfd_id);
  }, []);

  const dispatch = useAppDispatch();

  // ---- Dev-sync J6 — the fan season handlers ----
  // getSeasonSelectedId (fan.selector.js:2566-2577): the draft's season, else
  // the CMS Fan list's "summer_winter" (combined) option — so the Season
  // select and the Summer/Winter row gating always resolve to a real season
  // before the user picks one.
  const getSeasonSelectedId = useCallback((): number | null => {
    const fanSeasons = cmsSeasonsByComponent(
      globalDoc as ICmsAhuDoc | undefined,
      'Fan'
    );
    const combined = fanSeasons.find((s2) => s2.value === 'summer_winter');
    return (
      (Number(sectionDraft.current.season_id) || null) ??
      (combined ? Number(combined.id) : null)
    );
  }, [globalDoc]);

  // changeFanSectionField (js:4991-5045): stage on the draft; an entering
  // edit NULLS that season's ladb/lawb draft copies (a fan's real leaving
  // adds motor heat rise, computed only ever live for the psych chart —
  // never persist a phantom zero-delta override) — the output tab's mirror
  // re-derives on render (the legacy hand-patched DOM value). A season pick
  // re-renders; the Summer/Winter groups gate in JSX (legacy toggles a
  // `hide` class — js:5035-5043 — over always-rendered groups).
  const onFanSectionField = useCallback(
    (name: string, rawValue: string, minV: number, maxV: number): number | null => {
      let value: number | null;
      if (rawValue === '' || rawValue === 'None') {
        value = null;
      } else {
        let n = Number(String(rawValue).replace(/,/g, ''));
        if (Number.isNaN(n)) n = 0;
        n = Math.min(Math.max(n, minV), maxV);
        value = Number(n.toFixed(2));
      }
      sectionDraft.current = { ...sectionDraft.current, [name]: value };
      const seasonBySectionField: Record<string, 'summer' | 'winter'> = {
        eadb_temp_summer: 'summer',
        eawb_temp_summer: 'summer',
        eadb_temp_winter: 'winter',
        eawb_temp_winter: 'winter',
      };
      const season = seasonBySectionField[name];
      if (season) {
        sectionDraft.current[`ladb_temp_${season}`] = null;
        sectionDraft.current[`lawb_temp_${season}`] = null;
      }
      bump();
      return value;
    },
    [bump]
  );

  // _persistVfdAccessory call site (js:6268-6273): payload built synchronously
  // from the live refs, the async persistence NOT awaited (the legacy dialog
  // removes itself right after). SELECT path only — never Rate.
  const persistVfd = useCallback(() => {
    const v = vfdRef.current;
    // ★ `!isVfdNone(v.master_vfd_id)` (2026-10-05): never persist a VFD row with
    // no catalog drive behind it. `isVfdValidNow()` alone is not enough here —
    // it passes whenever the VFD TAB is disabled, and `vfdTabDisabled` includes
    // "no motor resolved" (the motor effect nulls `motorPick` on every rated-row
    // choice and re-resolves async, and a catalog-fan miss or an empty motor
    // list leaves it null for good). FanVfdTab is always MOUNTED, so its init
    // has already seeded `vfdRef` with configuration/section/wiring defaults and
    // NO `master_vfd_id`. With "Has VFD" now defaulted on, a Select in that
    // window used to POST /ahu/vfds/ and create an accessory with `master_vfd`
    // null: an extra VFD on the unit, an `invalid_selection` error from
    // `selection_validations`, and a drive counted in BOM and pricing that the
    // user never asked for — and could not remove, because the only #has_vfd
    // checkbox lives inside the tab they cannot open. Deleting the accessory
    // did not stick either; the next Select re-created it.
    // The flag itself is still stamped (doSelect's no-op branch), which is
    // harmless on its own: `AHUAccessoryVFD.check_vfd` is a method ON an
    // accessory row, so with no row there is nothing for BOM to count.
    // Falling through to `payload: null` with an existingId also cleans up a
    // row already created that way — it is a VFD with no drive either way.
    const payload =
      isVfdValidNow() && v && !isVfdNone(v.master_vfd_id)
        ? buildVfdPayload(v, vfdPositionRef.current, vfdParamsRef.current, modelId)
        : null;
    void persistVfdAccessory(dispatch, {
      payload,
      hasVfd: Boolean(state.current.has_vfd) && !!v, // js:8704
      existingId: v?.id != null ? Number(v.id) : null,
      fanSectionId: ((activate.section as { id?: number } | undefined)?.id as number) ?? null,
    }).then((id) => {
      // retain the persisted id (legacy self.vfdAccessory stays set) so a
      // re-Select in the same dialog session UPDATEs instead of re-creating.
      if (!vfdRef.current) return;
      if (id != null) vfdRef.current.id = id;
      else delete vfdRef.current.id;
    });
  }, [dispatch, modelId, activate, isVfdValidNow]);

  // The has_vfd stamp goes out via the shim model's auto-save PATCH, which
  // never touches the RTKQ fan-detail cache the seed effect reads — a
  // same-session reopen re-seeded has_vfd from the STALE entry (checked→
  // unchecked), and the untouched Select then DELETEd the just-created VFD.
  // Patch the cache entry in place (the TabAccessories/TabAhu updateQueryData
  // pattern) so a remount seeds current truth for BOTH stamp directions, with
  // no refetch. (The hook's refetchOnMountOrArgChange still re-pulls DB truth
  // on reopen — this only fixes the synchronous first seed.)
  const syncFanDetailCache = useCallback(
    (fanParamsJson: string, columns?: Record<string, unknown>) => {
      dispatch(
        ahuComponentFormsApi.util.updateQueryData(
          'getComponentDetail',
          { plural: 'fans', id: modelId },
          (draft) => {
            draft.fan_params = fanParamsJson;
            if (columns) Object.assign(draft, columns);
          }
        )
      );
    },
    [dispatch, modelId]
  );

  /**
   * J6 — the section persist (legacy `section.set` + `section.save` inside
   * commitFanChain, fan.selector.js:7371-7376; ONE queued PATCH here, keeping
   * the set()-path's side-effect — the `changed:section` event for a changed
   * _summer entering pair, section.js:231-234).
   *
   * season_id + all 8 per-season temps are sent explicitly (Number or null) so
   * an invalidation persists instead of leaving a stale DB value; the draft's
   * ladb/lawb were nulled per-season by any entering edit (onFanSectionField).
   *
   * ★ LIVES OUT HERE rather than inside doSelect's full-apply path, because
   * the Season / EADB / EAWB block is the one part of this dialog a user can
   * change WITHOUT re-rating, and `sectionDraft` is a detached copy that
   * nothing else ever writes back (fan.selector.js:654 — a `.set()` on the
   * live shared section model would broadcast a storage event on every
   * keystroke). Running it only on the full-apply path meant that opening an
   * already-selected fan, editing EADB/EAWB and pressing Select dropped the
   * edit in silence: the no-op path issued no request at all. Legacy has no
   * such split — it builds `section_save_data` (:7439) and saves it (:7465)
   * OUTSIDE the `self.selected` branch at :7015-7349, so both of its paths
   * persist it.
   */
  const persistSectionDraft = useCallback(() => {
    const sec = activate.section as
      | ({ id?: number; tunnel_id?: number } & Record<string, unknown>)
      | undefined;
    if (sec?.id == null) return;
    const sectionId = sec.id;
    const changes: Record<string, unknown> = {
      season_id: Number(sectionDraft.current.season_id) || null,
    };
    (
      [
        'eadb_temp_summer', 'eawb_temp_summer', 'eadb_temp_winter', 'eawb_temp_winter',
        'ladb_temp_summer', 'lawb_temp_summer', 'ladb_temp_winter', 'lawb_temp_winter',
      ] as const
    ).forEach((field) => {
      const value = sectionDraft.current[field];
      changes[field] =
        value !== null && value !== '' && value !== undefined ? Number(value) : null;
    });
    const summerEnteringChanged =
      String(changes.eadb_temp_summer ?? '') !== String(sec.eadb_temp_summer ?? '') ||
      String(changes.eawb_temp_summer ?? '') !== String(sec.eawb_temp_summer ?? '');
    queueSectionPatch({
      dispatch,
      sectionId,
      tunnelId: Number(sec.tunnel_id),
      changes,
      onSuccess: () => {
        if (summerEnteringChanged) {
          bus.emit(`changed:section:${sectionId}:eadb_temp:eawb_temp`);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, dispatch]);

  // master param lists from the dependency graph (field → narrowed raw rows).
  const [masterLists, setMasterLists] = useState<Record<string, OptionRow[]>>({});
  const masterListsRef = useRef<Record<string, OptionRow[]>>({});
  masterListsRef.current = masterLists;
  // F3: the FULL (unfiltered) master list per field — the saved-value
  // fallback-label source. Fetched once on open (read-only); used ONLY to label
  // a saved value the narrowed cascade omits (never mutates the model).
  const [masterFullLists, setMasterFullLists] = useState<Record<string, OptionRow[]>>({});
  const masterFullListsRef = useRef<Record<string, OptionRow[]>>({});
  masterFullListsRef.current = masterFullLists;
  // the spring-isolator CMS doc, readable from `applyFieldZeroState` (a
  // `useCallback([])` that runs inside the graph's load, not on render).
  const isolatorDocRef = useRef<FanCmsDoc | undefined>(undefined);
  isolatorDocRef.current = isolatorDoc as FanCmsDoc | undefined;
  const masterFullFetched = useRef<Set<string>>(new Set());
  // Per-dialog memo of the params endpoint, keyed by URL — legacy's
  // `stateStorage.models` "fan-rating-dialog" bucket (fan.selector.js:2015-2027).
  const paramsCache = useRef<Map<string, unknown>>(new Map());

  // ---- Selector-parity FAN restore: standalone master lists ----------------
  // Three option sources that are NOT dependency-graph nodes and so are fetched
  // once, directly (legacy getFanSpacings js:8861 + SystemEffect model js:625):
  //   fan_spacing_sides_id / fan_spacing_top_btm_id  → Mounting-tab spacing
  //   system_effect_id                               → Performance System Effect
  const [spacingSides, setSpacingSides] = useState<OptionRow[]>([]);
  const [spacingTopBtm, setSpacingTopBtm] = useState<OptionRow[]>([]);
  const [systemEffect, setSystemEffect] = useState<OptionRow[]>([]);
  const parityListsFetched = useRef(false);
  useEffect(() => {
    if (parityListsFetched.current) return;
    parityListsFetched.current = true;
    const load = (url: string, set: (rows: OptionRow[]) => void) => {
      void fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((body) => set(Array.isArray(body) ? body : ((body as any)?.results ?? [])))
        .catch(() => undefined);
    };
    load(FAN_SPACING_SIDES_URL, setSpacingSides);
    load(FAN_SPACING_TOP_BTM_URL, setSpacingTopBtm);
    load(FAN_SYSTEM_EFFECT_URL, setSystemEffect);
  }, []);

  // ---- ★ Phase 6.5 — HEADER-MODE STANDALONE SEED (fan.selector.js:702-733) ---
  // The header host has no current_model, so there is nothing to fetch: the seed
  // comes from `self.defaults` merged with the `last:true` entry of
  // `localStorage.fan_independent_ratings`. When that entry carries `rate:true`
  // the rate is AUTO-RE-FIRED once the dependency cascade settles (legacy does it
  // from the DependenciesHandler `finished` hook, js:1240-1244).
  /** legacy `self.storage_saved_data` — the parsed saved-inputs map. */
  const storageMapRef = useRef<IndependentRatingMap>({});
  /** legacy `self.needToRate` / `self.needToRateData` / `self.needToSearch`,
   * folded into one pending-restore slot. `applyState` distinguishes the
   * mfg-switch restore (`findFromStoraga`) from the construction seed (whose
   * state is already applied). */
  const pendingRestore = useRef<{
    entry: { state?: Record<string, unknown>; rate?: boolean; choosen_index?: number | null; motor_choosen_index?: number | null };
    applyState: boolean;
  } | null>(null);
  /** legacy `self.motor_choosen_index` (restored, then re-saved verbatim). */
  const motorChoosenIndex = useRef<number | null>(null);
  /** legacy chooseMotor's two writes in one (js:5786-5787) — the index is what
   *  `saveInStorage` round-trips, the row is what the Select payload reads. */
  const setMotorPick = useCallback((pick: { index: number; row: Record<string, any> } | null) => {
    motorChoosenIndex.current = pick ? pick.index : null;
    setMotorPickState(pick);
  }, []);
  /** legacy `self.fan_mfg_name` — the selected Manufacturer option's TEXT
   * (js:4808), persisted into the saved entry (js:2392). */
  const fanMfgNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!headerMode) return;
    if (seeded.current === modelId) return;
    // ★ 6.5-VERIFY FIX — the seed WAITS for the fans CMS doc, because the
    // `default_mfg` block below resolves `self.cms.get('fan').manufacturer`
    // (fan.selector.js:1421-1423). Legacy shows its own preloader over the same
    // window (`self.$el.html('<div class="preloader …">')`, js:738-740), and the
    // mount cascade already waits for `fanDoc` too — so seeding in the SAME
    // commit is what lets the cascade actually kick off the seeded mfg_id.
    if (!fanDoc) return;
    const map = readRatingMap(RATING_STORAGE_KEYS.fan);
    const entry = findLastEntry(map);
    const s: Record<string, unknown> = { ...HEADER_FAN_DEFAULTS };
    if (entry) {
      // js:713-721 — `{...defaults, ...model.model_data}` then
      // `model_data.model = {...model_data.model, ...model.state}` then
      // `self.state.set(model.state)`: `state` is what actually lands in the
      // state model, so it is the only member we consume.
      Object.assign(s, entry.state ?? {});
      fanMfgNameRef.current = (entry.fan_mfg_name as string | null) ?? null;
      motorChoosenIndex.current = entry.motor_choosen_index ?? null;
      // js:726 `self.needToRate = model.rate` → the auto-re-fire.
      if (entry.rate) pendingRestore.current = { entry, applyState: false };
    } else {
      // ★ 6.5-VERIFY FIX — the `default_mfg` preset, fan.selector.js:1417-1447:
      //     if (!self.model_data.model.master_fan_id && !self.defaults_already_exist) {
      //       const default_mfg = (
      //         self.cms.get("fan").manufacturer.find((e) => e.id == 209) ||
      //         self.cms.get("fan").manufacturer.find((e) => e.id == 64)  ||
      //         self.cms.get("fan").manufacturer[0]
      //       )?.id;
      //       self.state.set({ …, mounting_method_id: 3, …self.model_data.model,
      //                        mtr_controller_id: 1, …, mfg_id: default_mfg });
      //       self.setDefaults();
      //     }
      // Header mode NEVER has a `master_fan_id`, and `defaults_already_exist` is
      // set ONLY when a saved storage entry seeded (js:724) — so on a first-ever
      // open this branch ALWAYS runs. It was missing from the port, which left
      // the popup's primary tab with an UNSELECTED Manufacturer; the mount
      // cascade then bails on `mfg_id == null`, so nothing narrowed and EVERY
      // dependent select rendered blank. Live /sizer/ seeds
      // 209 New York Blower → Plenum / FA / Arr 4 / CW / II / Aluminum /
      // Baldor / AC / Any Speed / VMC AMSR-2D Spring 1.5 / VFD / Horizontal /
      // Direct-Mount, i.e. ready to rate as soon as airflow + TSP are typed.
      // `mtr_controller_id: 1` is already in HEADER_FAN_DEFAULTS; `mfg_id` is
      // last in the legacy `state.set` literal so it wins, and
      // `mounting_method_id: 3` survives because `model_data.model` (the header
      // `defaults.model`) carries no such member.
      const mfgs = fanCmsOptionList(fanDoc as FanCmsDoc, 'mfg_id');
      const dfltMfg =
        mfgs.find((m) => Number(m.id) === 209) ??
        mfgs.find((m) => Number(m.id) === 64) ??
        mfgs[0];
      if (dfltMfg != null) s.mfg_id = dfltMfg.id;
      s.mounting_method_id = 3;
      // ★ 6.5 builder-3 — `self.setDefaults()` (js:1448), the per-manufacturer
      // preset literals. This is the call the port was missing: legacy runs it
      // right after the default_mfg block, so a first-ever open lands on
      // NYB + Plenum / FA / Arr 4 / CW / Baldor / Super-E / Premium instead of a
      // Manufacturer with a dozen blank children.
      Object.assign(s, FAN_SET_DEFAULTS[Number(s.mfg_id)] ?? {});
      // ★ …and Qty Wide x High opens on `--` (fanQtyDefaultsFor). Only on this
      // FIRST-open branch: the branch above restores the popup's last session
      // from storage, where the array the user left behind is theirs, not a
      // default to overwrite.
      Object.assign(s, fanQtyDefaultsFor(s.mfg_id) ?? {});
    }
    // "Has VFD" — the board seed applies DEFAULT_HAS_VFD whenever the fan has no
    // saved value of its own (:1676/:1720) and the header popup is the SAME Fan
    // Rating Dialog, but neither branch above seeds the key: HEADER_FAN_DEFAULTS
    // has no `has_vfd` member and the storage entry only carries one if a
    // previous session wrote it. So the popup opened with the box unticked for
    // every manufacturer. Applied after both branches so a value the user left in
    // their own storage entry still wins, exactly like a saved `fan_params`.
    if (s.has_vfd == null) s.has_vfd = DEFAULT_HAS_VFD;
    storageMapRef.current = map;
    state.current = s;
    init.current = { ...s };
    seeded.current = modelId;
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMode, modelId, fanDoc]);

  /**
   * `saveInStorage` (fan.selector.js:2378-2397). Guarded exactly like legacy:
   * `if (!self.storage_saved_data || !self.header) return;`.
   *
   * ★ Legacy keys the entry by `self.mfg_id || self.state.get('mfg_id')`, so a
   * field edit made BEFORE any Manufacturer is picked writes the literal key
   * `"null"`. Mirrored verbatim (String(null) === 'null') rather than "fixed" —
   * the same entry appears in legacy storage today.
   *
   * Deviation (timing only, not observable): legacy debounces the two airflow
   * writes by 1 s (calcAirflowPerfan/Total js:4991-4996, 5056-5061) and skips
   * the write inside `changeField` for those two names (js:4720-4721). We write
   * synchronously on every change; the persisted value is identical.
   */
  const saveInStorage = useCallback((over?: { rate?: boolean; choosenIndex?: number | null }) => {
    if (!headerMode) return;
    storageMapRef.current = writeRatingEntry(
      RATING_STORAGE_KEYS.fan,
      state.current.mfg_id ?? null,
      // Member ORDER is byte-parity with js:2385-2393 (state, rate, last,
      // model_data, choosen_index, motor_choosen_index, fan_mfg_name) —
      // JSON.stringify preserves insertion order.
      {
        state: JSON.parse(JSON.stringify(state.current)),
        // legacy `rate: !!self.rated_fans`
        rate: over?.rate ?? !!ratedRowsRef.current,
        last: true,
        // legacy stores the whole `model_data`. The seed READER spreads it under
        // its own defaults and IS tolerant of a partial shape (js:713-716) —
        // but `model_data.project` is later dereferenced UNGUARDED at
        // js:4369 `self.data["elevation"] = self.model_data.project.elevation;`
        // (collectData, i.e. every Rate). ★ 6.5-VERIFY FIX: omitting `project`
        // made the LEGACY popup on /sizer/ throw
        // `TypeError: Cannot read properties of undefined (reading 'elevation')`
        // when it restored a React-written entry — the fan tab then rendered
        // Manufacturer/Type/Series EMPTY and the `rate:true` auto-re-fire never
        // ran (verified live: 2 pageerrors, 0 rating POSTs, 0 rated rows).
        // Legacy's own header-mode entry carries `project: {}`,
        // `defaultFanParams: {wheel_width_percent_id: 0}` and
        // `defaultMotorsParam: {}` (captured from /sizer/), so emit all six
        // members in the legacy INSERTION ORDER.
        model_data: {
          model: JSON.parse(JSON.stringify(state.current)),
          params_fan: JSON.parse(JSON.stringify(state.current)),
          params_motor: {},
          // `self.project ? self.project.toJSON() : {}` — `{}` in header mode.
          project: {},
          // legacy `self.defaults.defaultFanParams` / `defaultMotorsParam`
          // (js:1580-1583) — persisted verbatim by saveInStorage.
          defaultFanParams: { wheel_width_percent_id: 0 },
          defaultMotorsParam: {},
        },
        // `in` (not `??`) — an explicit `choosenIndex: null` override must WIN.
        choosen_index:
          over && 'choosenIndex' in over ? over.choosenIndex ?? null : chosenIndexRef.current,
        motor_choosen_index: motorChoosenIndex.current,
        fan_mfg_name: fanMfgNameRef.current,
      },
      storageMapRef.current
    );
  }, [headerMode]);

  // ---- seed state once GET resolves (BOARD host) ----
  useEffect(() => {
    if (headerMode) return;
    if (!detail) return;
    if (seeded.current === modelId) return;
    const m = detail as FanModel;
    // A never-rated fan resolves its default manufacturer out of the fans CMS
    // doc below (js:1578-1582), so that case has to WAIT for the doc — legacy
    // runs the whole seed inside the callback that already has the CMS loaded.
    // A rated fan needs nothing from it, so it still seeds on the first commit.
    if (!m.master_fan_id && !fanDoc) return;
    // Dev-sync J5+J6 — sectionDraft seed (fan.selector.js:654: a DETACHED
    // copy of the section — a .set() on the live shared model would
    // broadcast storage events on every keystroke) + the chart's pending
    // re-rate handoff (:663-694): section-scoped gate; the bare entering
    // pair renames to `_summer` UNCONDITIONALLY (both-seasons-stale payloads
    // carry bare + _winter together), merges onto the draft, and the dialog
    // lands on the Performance input tab (:692 — legacy sets activeTabId
    // directly for the same reason).
    sectionDraft.current = {
      ...((activate.section as Record<string, unknown> | undefined) ?? {}),
    };
    const pendingSectionFields = takeSectionPendingRerate(
      (activate.section as { id?: number } | undefined)?.id
    );
    if (pendingSectionFields) {
      const f = pendingSectionFields as Record<string, number | undefined>;
      if (f.eadb_temp !== undefined) {
        f.eadb_temp_summer = f.eadb_temp;
        f.eawb_temp_summer = f.eawb_temp;
        delete f.eadb_temp;
        delete f.eawb_temp;
      }
      Object.assign(sectionDraft.current, f);
      setInputTab('performance');
    }
    const fanParams = parseParams(m.fan_params);
    const siParams = parseParams(m.si_params);
    const motorParams = parseParams(m.motor_params);
    // Selector-parity FAN fix — the motor-base ids (mfg/series/mtl) have NO
    // top-level columns; they live ONLY inside the motor_base_params blob
    // (buildFanSelectPayload writes {mfg_id, series_id, mtl_id}). Legacy seeds
    // them by prefixing each blob key with `mtr_base_` (fan.selector.js:1534-
    // 1537 parse, :1556-1558 `state.set(\`mtr_base_${i}\`, …)`). Reading the
    // top-level m.mtr_base_* columns (below) always returns undefined, so a
    // saved motor base reopened blank and was overwritten on re-Select.
    const motorBaseParams = parseParams(m.motor_base_params);

    const s: Record<string, unknown> = {};
    // params_fan blob (filtered by the allow-list, legacy 627-631).
    for (const [k, v] of Object.entries(fanParams)) {
      if (ALLOWED_FAN_PARAMS.has(k)) s[k] = v;
    }
    // Selector-parity FAN fix — the two wheel SEARCH FILTERS are force-cleared
    // when an already-selected fan is reopened. Legacy's rated-fan seed branch
    // (fan.selector.js:1537-1543) spreads the saved blob and then overrides:
    //
    //     self.state.set({ ...self.model_data.model, ...self.fanParams,
    //                      wheel_width_percent_id: null, //force reset Wheel width
    //                      wheel_dia_nominal_id: null, … });
    //
    // Both keys ARE persisted into fan_params on Select — getModelFanParams
    // value→id remaps the chosen vendor row's `wheel_width_percent` /
    // `wheel_dia_nominal` (js:5597/:5623), and dev's own DB carries them on
    // 2243 of 2263 selected fans — but legacy never seeds them BACK into the
    // selects, so dev reopens with Wheel Diameter and Wheel Width at '--'. The
    // port dropped the override, so dev3 reopened with the previous rate's
    // filter values pre-applied and the next Rate went out narrowed by them.
    //
    // Same reasoning as the `pf.wheel_type_id = null` stamp in
    // buildFanSelectPayload (js:7222-7226): "a rating search filter, not a
    // property of the selected fan". Wheel Type is cleared on the WRITE side,
    // these two on the READ side — each mirrored where legacy does it.
    //
    // Gated on `master_fan_id` exactly like legacy: the never-rated branch
    // (js:1574-1614, mirrored below) spreads `...self.fanParams` with no
    // override, so a fan that has never been Selected keeps what the blob has.
    if (m.master_fan_id) {
      s.wheel_width_percent_id = null;
      s.wheel_dia_nominal_id = null;
    }
    // model columns the selects/inputs read — hoisted to SEED_MODEL_FIELDS
    for (const f of SEED_MODEL_FIELDS) if (m[f] != null) s[f] = m[f];
    // motor_base_params blob → mtr_base_* state (legacy js:1556-1558 prefixes
    // every blob key with `mtr_base_`, so blob `mfg_id`→`mtr_base_mfg_id` etc.).
    for (const [k, v] of Object.entries(motorBaseParams)) {
      if (v != null) s[`mtr_base_${k}`] = v;
    }
    // mfg lives in fan_params — keep that as the authoritative root.
    if (fanParams.mfg_id != null) s.mfg_id = fanParams.mfg_id;
    // isolator ids from si_params (legacy 1300-1305).
    s.isolator_type_id = siParams.isolator_type_id ?? s.isolator_type_id;
    s.isolator_series_id = siParams.series_id ?? s.isolator_series_id;
    s.isolator_mfg_id = siParams.mfg_id ?? s.isolator_mfg_id;
    // motor params blob ids (motor mfg/series/etc.).
    // motor_type_id roots the motor cascade — it lives in motor_params.type_id
    // (NOT a top-level column), so seed it here or the whole motor branch
    // (controller/speed/efficiency) renders blank on open (W6 verify).
    //
    // `motor_power` is deliberately NOT in this list, and the `mk` fallback is
    // why it must not be. Dev does not read the raw blob: it reads the
    // `master_motor_id` catalogue field's `results_filter`, a ResultsFilter
    // built with `allowed_fields = paramsFields`
    // (ahu/mvc/components/fan/view/form.js:271-287), whose `set` DELETES every
    // key outside that list (universals_js/filtration/results_filter.js:39-42).
    // The list names `motor_power`, not `power` — so the blob's `"power": 5`
    // (the chosen motor's actual rating, which `doSelect` writes into
    // motor_params wholesale, js:7283 `params_motor: motor_choosen`) is dropped
    // before the loop ever sees it. And the loop PREFIXES: dev's
    // `state.set('motor_' + i, …)` (js:1554) would turn an allowed
    // `motor_power` key into `motor_motor_power`, which no select reads. So
    // there is no blob path to `state.motor_power` in dev, by either key.
    //
    // The port parsed the raw column instead, so `k.replace(/^motor_/, '')`
    // resolved `motor_power` → the blob's `power` and re-filled the search
    // filter on every reopen of a SELECTED fan — surviving the model-column
    // gate below, because doSelect writes the blob and the column together.
    for (const k of [
      'motor_type_id', 'motor_mfg_id', 'motor_series_id', 'motor_enclosure_id',
      'motor_efficiency_id', 'motor_housing_id', 'motor_speed_nominal_id',
      'motor_shaft_grounding_id',
    ]) {
      const mk = k.replace(/^motor_/, '');
      if (motorParams[k] != null) s[k] = motorParams[k];
      else if (motorParams[mk] != null) s[k] = motorParams[mk];
    }
    // motor_power is the top-level component column (NOT in the motor_params
    // blob) — seed it so the Power select renders its saved value (W6.5 F3).
    //
    // …but ONLY for a fan whose motor has never been selected. Dev wipes the
    // column off the seed as soon as a motor exists: initialize stashes it in
    // `init_motor_power` and then, `if (self.model_data.model.master_motor_id)`,
    // sets `self.model_data.model.motor_power = null` (fan.selector.js:743-765)
    // — and it is that same `model_data.model` the board spread seeds state
    // from (js:1539 `self.state.set({...self.model_data.model, …})`). So dev
    // reopens a rated fan with Power BLANK. The stash is only ever read back
    // on the OUTPUT side (js:2994-2997 render_outputs, js:3992 the print/output
    // row) — never into state — which is what the port already does by reading
    // `detail.motor_power` for the selected-motor row below, so the Output pane
    // still shows the saved power. This only blanks the INPUT select.
    //
    // Power is a motor SEARCH filter, not a property of the chosen motor: it
    // carries `getMotorCount` and rides the rate payload, so a pre-filled 5 HP
    // silently narrows the next Rate to 5 HP motors and drops the Available
    // Motors count to match. Same reasoning as the wheel_width/wheel_dia wipe
    // above, and gated the same way — on a column that says "already rated".
    if (m.motor_power != null && s.motor_power == null && !m.master_motor_id) {
      s.motor_power = m.motor_power;
    }
    // Guard/AFS selects are named WITHOUT the `_id` suffix (in_guard, etc.) but
    // the saved values arrive on the detail as `in_guard_id`/`afs_*` columns AND
    // the selector reads `s.<selectName>`. Map the saved ids onto the select
    // names so the saved option renders on open (W6 verify — these were blank).
    const guardMap: Record<string, string> = {
      inlet_guard: 'in_guard_id',
      outlet_guard: 'out_guard_id',
      protective_cage: 'protect_cage_id',
      fan_afs_sensor: 'afs_sensor_id',
      fan_afs_transmitter: 'afs_xmitter_id',
      finish_id: 'finish_id',
    };
    for (const [selName, col] of Object.entries(guardMap)) {
      const v = m[col] ?? m[selName] ?? m[`${selName}_id`];
      if (v != null) s[selName] = v;
    }
    // si_mounting_method_id is the saved column; the Isolation/Mounting select
    // reads `mounting_method_id` (W6 verify — blank without this alias).
    if (m.si_mounting_method_id != null && s.mounting_method_id == null) {
      s.mounting_method_id = m.si_mounting_method_id;
    }
    // specified_deflection select reads `specified_deflection`; saved as
    // si_specified_deflection_id (W6 verify).
    if (m.si_specified_deflection_id != null && s.specified_deflection == null) {
      s.specified_deflection = m.si_specified_deflection_id;
    }
    // legacy seeds speed_condition / redundancy from the *_id (collectData echoes).
    s.speed_condition = fanParams.speed_condition_id ?? s.speed_condition_id;
    s.redundancy = fanParams.redundancy_id ?? s.redundancy_id;
    // has_vfd lives in the fan_params blob but OUTSIDE ALLOWED_FAN_PARAMS —
    // seed it explicitly (legacy changeParams keeps it in state, js:4621-4630).
    // Dev-sync I (fan.selector.js:1534-1535 on merged dev): with NO saved
    // value, has_vfd takes DEFAULT_HAS_VFD; the saved blob wins (the legacy
    // state spread is `has_vfd: default_mfg == 237, …, ...self.fanParams`).
    s.has_vfd = fanParams.has_vfd != null ? Boolean(fanParams.has_vfd) : DEFAULT_HAS_VFD;
    // ---- fan.selector.js:1574-1614 — the NOT-YET-RATED first-open defaults ---
    // Legacy's board seed is an if/else on `master_fan_id`: the branch above
    // (js:1537-1573) restores a RATED fan, and this one re-defaults a fan that
    // has never been rated. `defaults_already_exist` is set ONLY in the header's
    // localStorage branch (js:818), so on the AHU board this always runs.
    //
    //     const default_mfg = (
    //       self.cms.get("fan").manufacturer.find((e) => e.id == 209) ||
    //       self.cms.get("fan").manufacturer.find((e) => e.id == 64)  ||
    //       self.cms.get("fan").manufacturer[0]
    //     )?.id;
    //     self.state.set({ …, mounting_method_id: 3, has_vfd: default_mfg == 237,
    //                      ...self.model_data.model, mtr_controller_id: 1,
    //                      ...self.fanParams, mfg_id: default_mfg });
    //     …motorParams / motorBaseParams / siParams…
    //     self.setDefaults();
    //
    // This is tracker item C.9 — "Fan selector on open shows the SAVED values;
    // the old app overrode them with a per-manufacturer default on every open.
    // Which is wanted?" — answered in the thread with "do the same as it is done
    // on dev". So `mfg_id` is LAST in the legacy literal and deliberately wins
    // over a saved fan_params.mfg_id, and `setDefaults()` then re-stamps that
    // manufacturer's whole preset over the saved Type/Series/Arr/motor ids.
    // A fan that HAS been rated keeps everything it saved except the two wheel
    // search filters (see the master_fan_id force-clear above) — the branch is
    // mutually exclusive with the one at :1179 below.
    //
    // Precedence is the legacy literal's, member by member: `mounting_method_id`
    // and `mtr_controller_id` are both beaten by the fan_params blob (which is
    // spread after them) and by nothing else — `mounting_method_id` has no model
    // column of that name (it is stored as `si_mounting_method_id`), and
    // `mtr_controller_id`'s model column is spread BEFORE the literal 1, so the
    // literal wins over it.
    if (!m.master_fan_id) {
      const mfgs = fanCmsOptionList(fanDoc as FanCmsDoc, 'mfg_id');
      const dfltMfg =
        mfgs.find((x) => Number(x.id) === 209) ??
        mfgs.find((x) => Number(x.id) === 64) ??
        mfgs[0];
      if (dfltMfg != null) {
        s.mounting_method_id = fanParams.mounting_method_id ?? 3;
        s.mtr_controller_id = fanParams.mtr_controller_id ?? 1;
        s.mfg_id = dfltMfg.id;
        s.has_vfd = fanParams.has_vfd != null ? Boolean(fanParams.has_vfd) : DEFAULT_HAS_VFD;
        // js:1607 `self.setDefaults()`. Its two early exits are both inert here:
        // `defaults_already_exist` is false on the board, and the
        // `last_changed === "mfg_id"` wheel-width clear needs a change event
        // that construction has not fired yet.
        Object.assign(s, FAN_SET_DEFAULTS[Number(dfltMfg.id)] ?? {});
      }
    }
    // ★ Qty Wide x High default `--` on the board (fanQtyDefaultsFor — every
    // manufacturer now, Anna 2026-10-08). The mfg-change fill below only fires
    // off a genuine mfg_id CHANGE event, and a fan slot opened straight on its
    // manufacturer never fires one — it seeds qty_wide/qty_high from the AHUFan
    // columns, whose DB default is 1. So a fan that has NEVER been rated opens
    // on 1x1 where it should open on `--`. Same self-heal shape as Sync L's
    // fan_inlet_temp_wb fill.
    //
    // Gated on `!m.master_fan_id`: once a fan has been selected, its stored
    // array IS the answer and the selects must show it (the dialog reopens
    // read-only on that rated row), not throw it away.
    //
    // ★ Runs BEFORE the per-fan derive below (it used to run after): the derive
    // divides by the array, so with the order the other way round it divided by
    // an array this line was about to replace with `--`.
    if (!m.master_fan_id) Object.assign(s, fanQtyDefaultsFor(s.mfg_id) ?? {});
    // calcAirflowPerfan mirror (js:4814-4868; legacy runs it at render time
    // from the fan-settings load, js:3512): saved fans persist airflow_actual
    // but often NO airflow_per_fan, leaving the TCF required field empty and
    // #rate non-clickable. Derive airflow_actual / effective qty (qty defaults
    // 1×1 like the legacy qty selects, js:3492-3497; redundancy 2 subtracts a
    // standby fan). ONLY when the field is absent — never overwrite a stored
    // value.
    if (
      (s.airflow_per_fan == null || s.airflow_per_fan === '') &&
      s.airflow_actual != null &&
      s.airflow_actual !== ''
    ) {
      const qtyWide = Number(s.qty_wide) || 1;
      const qtyHigh = Number(s.qty_high) || 1;
      const qty =
        Number(s.redundancy ?? s.redundancy_id) == 2 ? qtyWide * qtyHigh - 1 : qtyWide * qtyHigh;
      if (qty > 0) s.airflow_per_fan = Number(s.airflow_actual) / qty;
    }
    // ★ …and with Qty Wide x High on `--` there is no per-fan figure at all
    // (Anna, 2026-10-12). Not just the derive above: `airflow_per_fan` is one
    // of SEED_MODEL_FIELDS and the AHUFan serializer COMPUTES it as
    // `airflow_actual / fan_qty` from the STORED array (models_component_fan.py
    // `airflow_per_fan`), so the box opened holding a figure that belongs to a
    // layout the dialog is no longer offering — the stored one for a fan that
    // has never been rated, and after an `mfg_id` change the previously
    // SELECTED fan's. Blank it here, where the array has just been decided, so
    // "no array ⇒ no per-fan duty" holds from the first paint; `onSearchField`
    // keeps it true for every later change.
    if (fanQtyPairIsUnset(s.qty_wide, s.qty_high)) s.airflow_per_fan = '';

    state.current = s;
    init.current = { ...s };
    seeded.current = modelId;

    // render the STORED rated state read-only (legacy selectedCurves on open).
    if (m.master_fan_id) {
      const selected = buildSelectedRow(m, fanParams, activate.section as Record<string, any> | null);
      setSelectedRow(selected);
      setRatedRows([selected]);
      setChosenIndex(0);
      ratedDatetime.current = (m.rating_datetime as string) ?? null;
    }
    bump();
  }, [detail, modelId, activate, bump, fanDoc]);

  // ---- REQUIRED AW W x H on the STORED row, once the open GET lands ---------
  // The seed above runs ONCE per fan and consumes whatever the RTKQ entry holds
  // at that instant — on a reopen that is the cached (pre-Select) response,
  // where the serializer-computed `min_aw_*` pair can still be null. The
  // refetch (`refetchOnMountOrArgChange`) does deliver the real numbers a
  // moment later, and the seed lock was throwing them away, so the cell stayed
  // on its `loading-dots` placeholder for the whole dialog session. Legacy has
  // no cache to go stale: setOutputs reads `current_model` on every open
  // (fan.selector.js:3919-3926) and Backbone keeps that model in step with the
  // server. Re-stamp the pair — and only the pair — when a fresher `detail`
  // arrives, so open-time truth wins exactly like dev's.
  //
  // Never touches an in-session rated table: those rows carry the vendor
  // response's OWN min_aw_* (runRate, KA-5254) and `sessionRated` gates them
  // out, matching legacy's "the rating response already seeded
  // aw_width_height" guard in getRequiredHeightWidth (js:6952-6959).
  const detailMinAwWidth = (detail as FanModel | undefined)?.min_aw_width ?? null;
  const detailMinAwHeight = (detail as FanModel | undefined)?.min_aw_height ?? null;
  useEffect(() => {
    if (headerMode) return;
    if (seeded.current !== modelId) return;
    if (sessionRated.current) return;
    if (detailMinAwWidth == null || detailMinAwHeight == null) return;
    const sectionAwWidth = parseFloat(String((activate.section as any)?.aw_width));
    const sectionAwHeight = parseFloat(String((activate.section as any)?.aw_height));
    const aw = {
      aw_width: detailMinAwWidth,
      aw_height: detailMinAwHeight,
      aw_width_height: `${detailMinAwWidth} x ${detailMinAwHeight}`,
      aw_width_warning:
        !isNaN(sectionAwWidth) && parseFloat(String(detailMinAwWidth)) > sectionAwWidth,
      aw_height_warning:
        !isNaN(sectionAwHeight) && parseFloat(String(detailMinAwHeight)) > sectionAwHeight,
    };
    const merge = (row: Record<string, any> | null) =>
      row && row.aw_width_height !== aw.aw_width_height ? { ...row, ...aw } : row;
    setSelectedRow((prev) => merge(prev));
    // the seeded stored row is the ONLY row on this path (rows.length === 1).
    setRatedRows((prev) => {
      if (!prev || prev.length !== 1) return prev;
      const next = merge(prev[0]);
      return next === prev[0] ? prev : [next as Record<string, any>];
    });
  }, [detailMinAwWidth, detailMinAwHeight, modelId, headerMode, activate]);

  // render the STORED curve on open (read-only — the single GET, no vendor fetch).
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const m = detail as FanModel | undefined;
    if (!m?.master_fan_id) return;
    // prefer the dedicated curves model GET; fall back to the model's curve blob.
    const c = storedCurves ?? (m.curve as any) ?? null;
    if (!c) return;
    const curve: FanChosenCurve = {
      bhp_curve: parseCurveArr(c.bhp),
      rpm_curve: parseCurveArr(c.sp),
      system_curve: parseCurveArr(c.system_curve),
      airflow_actual: Number((m as any).airflow_actual) || 0,
      bhp_total: Number((m as any).bhp_total_all_fans) || 0,
    };
    setChosenCurve(curve);
  }, [storedCurves, detail, modelId]);

  // F4: fetch the SELECTED motor row on open (legacy getSelectedMotor — GET
  // motors/all/<master_motor_id>?voltage_id=…, mapToCMS resolves the
  // mfg/series/model/speed/frame/enclosure/housing/fl_eff/rotor/ip labels).
  // Read-only catalog GET (no vendor call); augmented with the stored fla/power.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const m = detail as FanModel | undefined;
    const motorId = (m as any)?.master_motor_id;
    if (!m || motorId == null) {
      setSelectedMotor(null);
      return;
    }
    let cancelled = false;
    const voltage = (m as any).motor_voltage_id;
    const url = `/api/v1.0/cms/1/v1.0.0/ahu/motors/all/${motorId}${
      voltage != null ? `?voltage_id=${voltage}` : ''
    }`;
    void fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((row) => {
        if (cancelled || !row) return;
        // augment with the stored fan-level fla/power totals (legacy 639-642).
        setSelectedMotor({
          ...row,
          fla: (m as any).rating_motor_fla ?? row.fla,
          fla_total: (m as any).rating_motor_fla_total ?? row.fla_total,
          power: (m as any).motor_power ?? row.power,
          power_total: (m as any).motor_power_total ?? row.power_total,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [detail, modelId]);

  /**
   * ★ Phase 6.5 (builder 3) — the per-field zero-state rules, applied AS EACH
   * OPTION LIST LANDS (which is where legacy applies them: `renderSelects` runs
   * once per committed list and calls `mergeIdsWithCMS(paramName)` at its top,
   * fan.selector.js:7435). Timing is load-bearing, not cosmetic: legacy's
   * `specified_deflection` request carries `&mtr_supplied_by_id=1
   * &isolator_type_id=1&isolator_mfg_id=99&isolator_series_id=178
   * &fan_isolators_supplied_by_id=1` — those parents are only populated because
   * the SHALLOWER layers already committed their own defaults. A pass that only
   * ran after the whole cascade settled requested the UNFILTERED deflection list
   * (15 ids, incl. the isolation default 19) instead of legacy's narrowed
   * [29,34,39,44,49], so the popup showed 1.0 where legacy shows 1.5.
   *
   * Rules, in legacy's own order:
   *   (a) mergeIdsWithCMS per-mfg overrides      js:2182-2220 (unconditional)
   *   (b) isolation defaults                     js:2222-2239
   *   (c) mounting_method_id                     js:2240-2246
   *   (d) renderSelects UNGATED sole-option      js:7644-7654 (+ the identical
   *       motor_position_id rule js:2247-2255)
   *
   * Runs in BOTH hosts, as legacy does — `mergeIdsWithCMS` (js:2222) and
   * `renderSelects` (js:8501) have no `self.header` branch. It was previously
   * header-gated, which left the board dialog with: no per-mfg forced
   * Controller / Supplied-by / Motor Position (a), the four Isolation selects
   * and Mounting Method blank on a fan with no saved isolation (b, c), and no
   * sole-option auto-select (d).
   *
   * (b)/(c)/(d) key off `ids`, the loaded option list. The five isolation
   * fields and `mounting_method_id` are NOT in FAN_CMS_PATHS, so they take the
   * master-param branch and arrive server-narrowed in both hosts — the same
   * list legacy applies these rules to. (d) additionally sees the full doc list
   * for doc-resolved fields on the board (the pre-existing Phase-6 deviation
   * noted in loadParams); a full list is not a sole option, so that combination
   * is inert rather than wrong.
   */
  const applyFieldZeroState = useCallback(
    (field: string, ids: Array<number | string>) => {
      const s = state.current;
      const patch: Record<string, unknown> = {};
      // (a)
      for (const [k, v] of Object.entries(fanMergeMfgOverrides(s))) {
        if (s[k] !== v) patch[k] = v;
      }
      const cur = () => patch[field] ?? s[field];
      // (b) — legacy's `!self.state.get(key)` is a FALSY test, mirrored.
      //
      // The pick is made against `result[key]`, and for the four
      // CUSTOM_COLLECTIONS fields (isolator type/mfg/series + specified
      // deflection) that is NOT the raw params list: mergeIdsWithCMS first
      // intersects the possible ids with the spring-isolator CMS doc
      // (`models[id] = e` where `e.id == id`, js:2325-2337), so an id the doc
      // has no row for can never be chosen. Two ids in `isolation_defaults` are
      // exactly that — 99 (isolator_mfg_id) and 178 (isolator_series_id) are in
      // the params response but absent from this CMS's spring-isolator doc,
      // whose manufacturers are [131 Vibro-Acoustics, 232 Dynasonics]. Picking
      // off `ids` alone put 99/178 in state, and with no doc row to label them
      // the Isolation tab's Manufacturer/Series selects rendered the RAW ID.
      // dev falls through to `Object.values(models)[0].id` instead — and since
      // `models`' keys are integer-like, that is the LOWEST surviving id, not
      // the first in the server's order. Build the same object so both the
      // membership test and the fallback order match.
      if ((FAN_ISOLATION_DEFAULT_FIELDS as readonly string[]).includes(field) && !cur() && ids.length) {
        const def = FAN_ISOLATION_DEFAULTS[field];
        const docPath = FAN_ISOLATOR_CMS_PATHS[field];
        let pool = ids;
        if (docPath) {
          const rows = fanCmsOptionListByPath(isolatorDocRef.current, docPath);
          const models: Record<string, number | string> = {};
          for (const id of ids) {
            if (rows.some((r) => r.id == id)) models[String(id)] = id;
          }
          pool = Object.values(models);
        }
        patch[field] = pool.some((i) => i == def) ? def : (pool[0] ?? null);
      }
      // (c) — STRICT `arr_id === 1`, so a string arr_id yields 2 as in legacy.
      if (field === 'mounting_method_id' && !cur() && ids.length) {
        patch[field] = (s.arr_id as unknown) === 1 ? 3 : 2;
      }
      // (d) — dev-sync H: the sole-option auto-select SKIPS
      // motor_shaft_grounding_id for Annexair (fan.selector.js:8329-8336 on
      // merged dev — the renderSelects `!(mfg==237 && paramName ===
      // 'motor_shaft_grounding_id')` guard).
      if (
        !FAN_SOLE_OPTION_EXCLUDE.has(field) &&
        !(Number(s.mfg_id) == 237 && field === 'motor_shaft_grounding_id') &&
        ids.length === 1 &&
        ids[0] != null &&
        (cur() == null || cur() === '')
      ) {
        patch[field] = ids[0];
      }
      if (!Object.keys(patch).length) return;
      // No bump(): we are inside the engine's load, whose own commit re-renders.
      state.current = { ...state.current, ...patch };
    },
    []
  );

  /** the `fan_mountings` collection, fetched once (legacy fetches it once into
   * `self.mountings`, js:591-594). Both hosts — legacy's CUSTOM_COLLECTIONS is
   * not host-dependent. */
  const fanMountingsRef = useRef<Array<Record<string, unknown>> | null>(null);
  const fetchFanMountingRows = useCallback(
    async (idListUrl: string): Promise<OptionRow[] | null> => {
      const get = async (u: string) => {
        const res = await fetch(u, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) return null;
        const body = await res.json();
        return Array.isArray(body) ? body : ((body as any)?.results ?? null);
      };
      try {
        if (!fanMountingsRef.current) {
          const all = await get(FAN_MOUNTINGS_URL);
          if (!all) return null;
          // the collection may arrive keyed by id (`{ "1": {...} }`) — legacy
          // takes `Object.values(self.mountings.toJSON())` (js:2099).
          fanMountingsRef.current = Array.isArray(all) ? all : Object.values(all);
        }
        const ids = await get(idListUrl);
        if (!ids) return null;
        const wanted = new Set(ids.map((v: any) => String(v?.id ?? v)));
        return (fanMountingsRef.current as Array<Record<string, unknown>>)
          .filter((r) => wanted.has(String(r.id)))
          .map((r) => ({ ...r, id: r.id as number })) as OptionRow[];
      } catch {
        return null;
      }
    },
    []
  );

  // ---- DEPENDENCY ENGINE (the cascade) ----
  const loadParams = useCallback<LoadParamsFn>(
    async (field, parents) => {
      // EVERY cascade field — doc-resolved or not — goes through the params
      // endpoint and is narrowed SERVER-side by its parent filters, which is
      // what legacy does: `loadParams` (fan.selector.js:1904-2028) has no doc
      // short-circuit, and `mergeIdsWithCMS` then uses the fans CMS doc purely
      // as the LABEL source, intersecting it with the returned id array
      // (`get_from_cms_data(path, parameterPossibleIDs.get(key).data, …)`,
      // js:2313-2318). Captured live from /sizer/ on a first-ever open
      // (mfg 209 / type 1 / series 1391):
      //   GET /cms/1/v1.0.0/ahu/fans/params/amca_class_id/?mfg_id=209&type_id=1&series_id=1391      → [2]
      //   GET /cms/1/v1.0.0/ahu/fans/params/wheel_mtl_id/?…                                        → [3583]
      //   GET /cms/1/v1.0.0/ahu/fans/params/motor_type_id/?…                                       → [1]
      //   GET /cms/1/v1.0.0/ahu/fans/params/speed_condition_id/?fan_mfg_id=209&fan_type_id=1&fan_series_id=1391 → [3]
      //
      // The port used to answer doc-resolved fields from the CMS doc's FULL
      // list instead, so every one of those selects offered the whole catalogue
      // rather than the manufacturer's own rows — Type listed Plenum/Plug/Cube/
      // DWDI for New York Blower where dev lists Plenum/Cube (the endpoint
      // answers `[1, 3]`), Series 39 rows where dev has 3. Phase 6.5 narrowed
      // four of them in header mode only (where the sole narrowed option IS the
      // zero-state pick); this restores it for all of them, in both hosts.
      // `fanParamUrl` already builds exactly these URLs (including the `fan_`
      // prefix redundancy/speed_condition/motor_position need), and the doc
      // selects already narrow their rows by `graph.optionIds`, so the LABELS
      // still come from the doc.
      //
      // Tracked as item (3) of "Phase 6.5 header rating: parked residuals" in
      // implementation-notes.html.
      const url = fanParamUrl(field, parents, state.current);
      if (!url) return masterListsRef.current[field] ?? [];
      // ★ `mounting_id` — the params endpoint answers a BARE id list (`[1]`),
      // and legacy pairs exactly that id list with the `fan_mountings`
      // collection to build the options (mergeIdsWithCMS
      // `CUSTOM_COLLECTIONS.mounting_id = Object.values(self.mountings.toJSON())`,
      // js:2099 — the `models[id] = e` intersection at js:2156-2164). Without
      // the collection the option text is the raw id ("1" instead of
      // "Horizontal") AND the rate envelope carries `mounting: 1` instead of
      // legacy's `"mounting":"horizontal"` (discovery §3.2) — the latter is the
      // load-bearing half. `mergeIdsWithCMS` runs in ONE code path for both
      // hosts, so this is not header-only: Phase 6.5 scoped it to the header
      // and left the board showing the bare id.
      if (field === 'mounting_id') {
        const rows = await fetchFanMountingRows(url);
        if (rows) {
          setMasterLists((prev) => ({ ...prev, mounting_id: rows }));
          applyFieldZeroState('mounting_id', rows.map((r) => r.id as number));
          return rows;
        }
      }
      try {
        // Legacy memoises every params response by URL for the dialog's
        // lifetime (`stateStorage.models.get/set("fan-rating-dialog", url, …)`,
        // js:2015-2027) and re-runs mergeIdsWithCMS off the cached value. Now
        // that the doc-resolved fields go down this path too, a parent change
        // fans out ~35 GETs and the cascade revisits the same URLs — so keep
        // the same memo. Everything after it is unchanged: a cache hit still
        // commits the list and runs the zero-state rules.
        const cached = paramsCache.current.get(url);
        const body = cached ?? (await (async () => {
          const res = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!res.ok) throw new Error(String(res.status));
          const parsed = await res.json();
          paramsCache.current.set(url, parsed);
          return parsed;
        })());
        const rows: OptionRow[] = Array.isArray(body) ? body : ((body as any)?.results ?? []);
        setMasterLists((prev) => ({ ...prev, [field]: rows }));
        // the engine compares the saved value against the RESOLVED option ids
        // (the nested short-name id for structural fields — NOT the raw row pk),
        // so a saved value still in the narrowed list is correctly kept. Falls
        // back to the raw id for plain fields (fanMasterOption handles both).
        const out = rows
          .map((r: any) => fanMasterOption(field, r) ?? { ...r, id: r.id ?? r.value ?? r })
          .map((r: any) => ({ ...r, id: r.id }));
        // legacy drops the `null` tail some params endpoints return
        // (renderSelects `if (!isNull(item) …)`, js:7466) before counting the
        // options, which is what makes a "[x, null]" response a SOLE option.
        applyFieldZeroState(
          field,
          out
            .map((r: any) => r.id as number | string)
            .filter((v: number | string | null) => v != null) as Array<number | string>
        );
        return out;
      } catch {
        return masterListsRef.current[field] ?? [];
      }
    },
    [applyFieldZeroState, fetchFanMountingRows]
  );

  // F3: fetch the FULL (unfiltered) master list for a field once, to resolve the
  // saved-value fallback label when the cascade narrows the option out. Pure
  // read; never feeds the engine or the model — only the render fallback.
  const fetchMasterFullList = useCallback((field: string) => {
    if (masterFullFetched.current.has(field)) return;
    masterFullFetched.current.add(field);
    const url = fanParamUrl(field, [], {});
    if (!url) return;
    void fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((body) => {
        const rows: OptionRow[] = Array.isArray(body) ? body : ((body as any)?.results ?? []);
        setMasterFullLists((prev) => ({ ...prev, [field]: rows }));
      })
      .catch(() => undefined);
  }, []);

  // Options-tab Finish select: a CASCADE field like the guards and the AFS
  // lists — its options are the MasterAHUFanMfgSeriesFinish rows for the picked
  // Mfg + Series (`fan_mfg_series/finish_id/`), loaded by loadParams. It used
  // to be the FLAT MasterAHUFanFinish list fetched once on mount, which offered
  // every finish KSDM knows for every manufacturer; a mfg/series with no row in
  // that table now offers none, and the Options pane drops the select entirely
  // (see the Options tab below).

  // Default Finish = Mill WHERE THE MFG/SERIES OFFERS IT. Re-checked on every
  // render (version) because seeding the dialog state replaces state.current
  // after the list lands; never overwrites a pick. The option list is now
  // narrowed, so this is a preference among the available finishes rather than
  // a blanket default — a mfg/series without a Mill row is left to the cascade
  // (sole option, else null).
  const finishRows = masterLists.finish_id;
  useEffect(() => {
    if (state.current.finish_id != null || !finishRows) return;
    const mill = fanMasterOptionList('finish_id', finishRows as any[]).find(
      (r) => String(r.label ?? '').toLowerCase() === 'mill'
    );
    if (!mill) return;
    state.current = { ...state.current, finish_id: mill.id };
    bump();
  });

  // The Finish the dialog is SHOWING — the ONE value the rating envelope, the
  // catalog re-match and the Select payload all read, so the finish a fan is
  // stored with is the finish it was rated and matched on.
  //
  // The rule is whether the Options tab renders the Finish row at all: it does
  // only while the narrowed `fan_mfg_series/finish_id/` list has options
  // (`masterHasOptions`), and while it does, the select shows the value in state
  // even when the list leaves it out — BareMasterSelect appends a fallback
  // <option> for it, labelled from the full master list — so that still counts
  // as on screen and still filters, as it did before.
  //
  // What must NOT act is a finish with no select at all: a Mfg/Series with no
  // MasterAHUFanMfgSeriesFinish row (and a fan with no series picked yet, whose
  // cascade never fetches the list) draws nothing, so a value seeded from the
  // stored fan — saved under the old flat finish list, or left behind when a
  // series loses its rows — would otherwise filter the next Rate invisibly
  // (fan/finish.py drops candidates stocked only in another finish, and warns
  // naming a finish nobody picked and cannot change) and then be stored again.
  const shownFinishId = useCallback((): number | null => {
    const v = state.current.finish_id;
    if (v == null || v === '') return null;
    const options = fanMasterOptionList(
      'finish_id',
      masterListsRef.current.finish_id as any[] | undefined
    );
    return options.length ? Number(v) : null;
  }, []);

  // If the Options pane is ALREADY open when a manufacturer change empties it,
  // step back to the Fan tab. The nav item's own guard only blocks a click, so
  // without this the user is left looking at a blank pane behind a greyed label
  // with nothing to click back to it. Must live up here with the other hooks —
  // the render body early-returns the preloader before `optionsTabEnabled` is
  // computed, and a hook below that return changes the hook count between the
  // two renders (React #310).
  useEffect(() => {
    if (inputTab !== 'options') return;
    const any = FAN_OPTIONS_ROWS.some(
      ([name]) => fanMasterOptionList(name, masterLists[name] as any[] | undefined).length > 0
    );
    if (!any) setInputTab('properties');
  }, [inputTab, masterLists]);

  // Same step-back for the Isolation pane, which a PLUG fan does not get (see
  // `isolationTabEnabled` below). Switching Type to Plug while that pane is
  // open would otherwise leave it on screen behind a greyed label. Hook scope
  // for the same React #310 reason as the Options one above.
  useEffect(() => {
    if (inputTab !== 'isolation') return;
    if (fanTypeValue(fanDoc as FanCmsDoc, state.current.type_id as any) === 'plug') {
      setInputTab('properties');
    }
  }, [inputTab, fanDoc, version]);

  const getValue = useCallback((field: string) => state.current[field] as any, []);
  const setStateValue = useCallback(
    (field: string, value: any) => {
      state.current = { ...state.current, [field]: value };
      bump();
    },
    [bump]
  );
  // legacy `setDefaultsIfNullChecker` (fan.selector.js:1305-1314):
  //   isNull(mfg_id) || DEFAULT_FIELDS[mfg_id].has(parameter)
  // DEFAULT_FIELDS (js:178-396) is its OWN per-manufacturer map — not
  // REQUIRED_FIELDS (js:398-521), which gates #rate. The port read the required
  // map here, and those sets are almost all number inputs (TCF/BasX/Lau/NYB =
  // airflow_actual + airflow_per_fan + tsp), so no SELECT could take its
  // default_values pick and Type/Series/Arr/Style/Wheel Mtl/Motor Type/Motor
  // Position/Redundancy/Speed Condition/Housed Discharge/Qty rendered blank
  // where dev pre-fills them. A missing entry still falls through to `true`,
  // which is what legacy's `isNull(mfg_id)` short-circuit does for a fan with
  // no manufacturer yet (an unknown non-null mfg throws in legacy).
  const shouldSetDefaultIfNull = useCallback((field: string) => {
    const dflt = FAN_DEFAULT_FIELDS[Number(state.current.mfg_id)];
    return dflt ? dflt.has(field) : true;
  }, []);

  const isArmed = useCallback(() => cascadeArmed.current, []);
  const graph = useDependencyGraph({
    spec: FAN_DEPENDENCY_GRAPH,
    loadParams,
    getValue,
    setValue: setStateValue,
    shouldSetDefaultIfNull,
    isArmed,
  });

  // mount cascade: load option sets filtered by the saved mfg/type/series so the
  // dependent selects render their LABELS. applyNewValues keeps the saved value
  // (always in its own list) → never mutates the model.
  const cascadeFired = useRef(false);
  /** true once ANY cascade has been kicked (mount seed OR a user param change).
   * The header-mode pending-restore settle detector waits on this: a first-ever
   * open has no mfg_id, so the mount cascade never fires and `cascadeFired`
   * stays false — but a subsequent Manufacturer pick DOES kick the graph and its
   * `findFromStoraga` restore must still run. */
  const graphKicked = useRef(false);
  useEffect(() => {
    if (cascadeFired.current) return;
    if (seeded.current !== modelId) return;
    if (state.current.mfg_id == null) return;
    // wait for the fans CMS doc — loadParams resolves most option lists from it;
    // cascading before it lands commits empty lists (the W6 blank-select bug).
    if (!fanDoc) return;
    // …and for the spring-isolator doc, which the isolation zero-state defaults
    // (b) now narrow against. dev awaits BOTH in the same Promise.all before the
    // view is constructed (js:1366-1370), so its CUSTOM_COLLECTIONS are never
    // empty; gating only on `fanDoc` left a race where the isolator lists could
    // land first and default the four Isolation selects to null.
    if (!isolatorDoc) return;
    cascadeFired.current = true;
    graphKicked.current = true;
    graph.onParentChange('mfg_id');
    if (state.current.type_id != null) graph.onParentChange('type_id');
    if (state.current.series_id != null) graph.onParentChange('series_id');
    if (state.current.arr_id != null) graph.onParentChange('arr_id');
    // F3: warm the FULL master list for every master-resolved field that has a
    // saved value, so its saved option renders its LABEL even when the narrowed
    // cascade drops it (read-only; the label-only fallback never re-picks).
    for (const f of MASTER_SELECT_FIELDS) {
      const v = state.current[f];
      if (v != null && v !== '') fetchMasterFullList(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, detail, fanDoc, isolatorDoc]);

  // ---- ★ Phase 6.5 (builder 3) — zero-state SETTLE sweep -------------------
  // `applyFieldZeroState` already runs per field as its list lands (legacy's own
  // timing). This is the belt-and-braces second pass on the cascade's FALLING
  // EDGE (isCascading true → false), for the fields whose list came from the
  // fallback/cache path rather than a fresh load, and to re-assert the
  // unconditional per-mfg overrides (legacy re-applies them on EVERY
  // mergeIdsWithCMS call). Every rule is null-gated or idempotent, so a pass that
  // changes nothing does not re-render.
  //
  // ★★ HEADER-ONLY. The board host returns on the first line.
  const wasCascading = useRef(false);
  useEffect(() => {
    if (!headerMode) return;
    const cascading = graph.isCascading;
    const settled = wasCascading.current && !cascading;
    wasCascading.current = cascading;
    if (!settled) return;
    if (seeded.current !== modelId) return;
    const before = state.current;
    for (const field of Object.keys(FAN_DEPENDENCY_GRAPH)) {
      const ids = graph.optionIds(field);
      applyFieldZeroState(field, (ids ?? []).filter((v) => v != null));
    }
    if (state.current !== before) bump();
  });

  // ---- Sync L — ANNEXAIR fan_inlet_temp_wb render self-heal ----------------
  // (fan.selector.js render() top, commit 77263bea90). fan_inlet_temp_wb is a
  // REQUIRED Annexair input (FAN_REQ_ANNEXAIR → requiredFilled → #rate gate) but
  // ISN'T a real AHUFan column, so a fan can carry mfg 237 with no value for it.
  // The FAN_SET_DEFAULTS[237] fill only runs off a genuine mfg_id CHANGE event
  // (onSearchField's `name === 'mfg_id'` branch) — yet mfg_id can land on 237
  // WITHOUT that event: a reopened saved Annexair fan (seeded straight from the
  // model; the seed never fills WB), the initial default-mfg guess, or a
  // dependency-cascade correction that setValue()s mfg_id. In those paths WB
  // stays null and requiredFilled disables #rate forever. Self-heal it here
  // instead of depending on the event (the React analog of dev's render()-top
  // fill; runs every render, so it also catches a post-seed cascade landing on
  // 237). Fills ONLY a still-missing value — a saved/user-typed one is preserved
  // by the early return, exactly like dev's `self.state.get('fan_inlet_temp_wb')
  // ?? 60` (which, with our unified state, collapses to the same 60). Host-
  // agnostic: legacy render runs in both hosts.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    if (Number(state.current.mfg_id) != 237) return;
    if (state.current.fan_inlet_temp_wb != null) return;
    state.current = { ...state.current, fan_inlet_temp_wb: 60 };
    bump();
  });

  // ---- Dev-sync H — the ANNEXAIR default motor-series 'AZ' fallback --------
  // (fan.selector.js renderSelects :8345-8353 on merged dev): when mfg 237's
  // cascade leaves the Motor Series select EMPTY (the setDefaults preset 116
  // "Super-E" isn't valid for Annexair, so the armed narrow drops it and no
  // default re-picks), legacy finds the option whose TEXT is 'AZ' and selects
  // it. Runs in BOTH hosts (renderSelects is host-agnostic), on every cascade
  // falling edge.
  const azWasCascading = useRef(false);
  useEffect(() => {
    const cascading = graph.isCascading;
    const settled = azWasCascading.current && !cascading;
    azWasCascading.current = cascading;
    if (!settled) return;
    if (seeded.current !== modelId) return;
    if (Number(state.current.mfg_id) != 237) return;
    const cur = state.current.motor_series_id;
    if (cur != null && cur !== '') return; // select not empty — legacy `!changeSelect.val()`
    const ids = graph.optionIds('motor_series_id');
    const rows = ids && ids.length
      ? fanCmsOptionList(fanDoc as FanCmsDoc, 'motor_series_id', ids, 'series')
      : [];
    const az = rows.find((r) => String((r as any).label ?? '').trim() === 'AZ');
    if (!az) return;
    const idNum = Number(az.id);
    state.current = {
      ...state.current,
      motor_series_id: Number.isNaN(idNum) ? az.id : idNum,
    };
    bump();
  });

  // ---- field change handlers ----
  const markChanged = useCallback(() => {
    bus.emit('rating_mode:mounting:changed');
  }, []);

  /**
   * ★ Phase 6.5 (builder 3) — `calcAirflowPerfan` / `calcAirflowTotal`
   * (fan.selector.js:4935-4998 and :5000-5062), the LIVE cross-compute between
   * "Airflow per fan" and "total". Legacy fires them from `changeFanField`
   * (js:4660-4668):
   *     if (name === "airflow_per_fan")            self.calcAirflowTotal(true);
   *     else if (name === "qty_wide" || name === "qty_high"
   *              || name === "airflow_actual")     self.calcAirflowPerfan(true);
   * and from `changeParams` for Redundancy (js:4876-4880).
   *
   * Why it matters in header mode: `airflow_per_fan` is a REQUIRED field for
   * EVERY manufacturer (REQUIRED_FIELDS, js:367-481 → FAN_REQUIRED_FIELDS), so
   * without this compute `#rate` stays `non-clickable` in the header popup until
   * the user types the per-fan figure BY HAND, where legacy fills it the moment
   * the total is typed. (The board host had a one-shot backfill at SEED time
   * only — the `airflow_per_fan == null` block in the board seed effect.)
   *
   * Legacy reads the DOM (`self.$el.find('input[name=…]').val()`); we read the
   * state the change handler has just written — same values, and it keeps the
   * read scoped to this selector (no document-wide `#airflow_actual`, which
   * would hit a board dialog's input first; discovery §7.3 trap 1).
   *
   * Runs in BOTH hosts, as legacy does — neither `changeFanField` nor
   * `changeParams` branches on `self.header` for these calls.
   *
   * ★ `typedHere` — the caller is the airflow input the USER just edited,
   * rather than a Qty Wide x High / Redundancy change.
   *
   * Legacy has one else-branch for "cannot compute" and it BLANKS the other
   * field, which conflates two different situations. That was harmless while
   * every vendor's qty selects were mandatory 1..max — the array was always a
   * number, so the branch only ever ran because the SOURCE field was empty, and
   * "clear the total, the per-fan figure goes with it" is correct. With Qty Wide
   * x High on `--` (Annexair, 2026-10-07) the array itself is unknown, and the
   * same branch then wiped a perfectly good value every time the user typed in
   * the other box: enter a total, lose the per-fan figure; enter a per-fan
   * figure, lose the total (reported 2026-10-08).
   *
   * So the two cases are split. **With no array the two boxes are independent**
   * — neither is derived from the other — so a user edit of either must not
   * write to the other AT ALL. In particular not when the edited field ends up
   * empty: a person correcting a figure selects it and deletes it first, and
   * that intermediate empty state is what blanked the other box and could never
   * be recovered (the first fix only covered "source filled", so the complaint
   * came straight back — anything verified with playwright's `fill`, which
   * replaces atomically, misses it; type the keystrokes).
   *
   * Everything else keeps legacy's behaviour: with a real array, clearing the
   * total still clears the per-fan figure, because there it IS derived. And a
   * qty/redundancy change blanks it either way (`typedHere` false) — moving the
   * array to `--` makes the derived figure meaningless, and showing a stale one
   * next to `--` is worse than showing nothing.
   */
  const calcAirflow = useCallback((from: 'per_fan' | 'total', typedHere = false) => {
    const s = state.current;
    // ★ The two legacy functions coerce DIFFERENTLY, and the difference is
    // observable at 0 — so they cannot share one helper (a single
    // `Number(v) || undefined` blanked `airflow_per_fan` and disabled `#rate`
    // when the user typed `airflow_actual = 0`, where legacy computes 0 and
    // leaves `#rate` ENABLED; `requiredFilled` above already counts 0 as filled).
    //
    // `Number(...) || undefined` — 0 AND NaN fall through to undefined.
    // Used by calcAirflowPerfan for qty_wide/qty_high ONLY (js:4949-4962).
    const numOrUndef = (v: unknown): number | undefined => {
      if (v == null || v === '') return undefined;
      const n = Number(String(v).replace(/,/g, ''));
      return n || undefined;
    };
    // `.val() ? Number(...) : undefined` — a PRESENT "0" is kept as 0. Legacy
    // applies this to airflow_actual in calcAirflowPerfan (js:4940-4948) and to
    // airflow_per_fan + BOTH qty fields in calcAirflowTotal (js:5003-5028).
    // NaN is deliberately kept (legacy's `NaN != undefined` is true, so it flows
    // into the arithmetic) — mirrored quirk, unreachable via the number inputs.
    const numKeepZero = (v: unknown): number | undefined => {
      if (v == null || v === '') return undefined;
      return Number(String(v).replace(/,/g, ''));
    };
    // js:4963-4967 / :5029-5032 — `Number(select#redundancy_id[name=redundancy]
    // .val() || state.get('redundancy')) || 1`. Our select is named
    // `redundancy_id` (the DocSelect), so read that first.
    const redundancy = Number(s.redundancy_id ?? s.redundancy) || 1;
    const qtyOf = (w?: number, h?: number) =>
      w != null && h != null ? (redundancy == 2 ? w * h - 1 : w * h) : undefined;
    if (from === 'total') {
      // calcAirflowPerfan (js:4935-4998): total (+qty) → per fan.
      const actual = numKeepZero(s.airflow_actual);
      const qty = qtyOf(numOrUndef(s.qty_wide), numOrUndef(s.qty_high));
      if (actual != null && qty != null) {
        state.current = { ...s, airflow_per_fan: actual / qty, airflow_actual: actual };
      } else if (typedHere && qty == null) {
        // ★ No array (Qty Wide x High on `--`) — see the `typedHere` note
        // above. Per-fan is NOT derived from the total here, so this edit has
        // no business touching it, whether the total ends up filled or empty.
        if (actual != null) state.current = { ...s, airflow_actual: actual };
      } else {
        // js:4988-4989 — the field is BLANKED (empty string, not null).
        state.current = { ...s, airflow_per_fan: '' };
      }
      return;
    }
    // calcAirflowTotal (js:5000-5062): per fan (+qty) → total.
    const perFan = numKeepZero(s.airflow_per_fan);
    const qty = qtyOf(numKeepZero(s.qty_wide), numKeepZero(s.qty_high));
    if (perFan != null && qty != null) {
      state.current = { ...s, airflow_actual: perFan * qty };
    } else if (typedHere && qty == null) {
      // the same, the other way round: with no array the total is not derived
      // from the per-fan figure, so leave it exactly as the user left it.
    } else {
      state.current = { ...s, airflow_actual: '' };
    }
  }, []);

  const onSearchField = useCallback(
    (name: string, raw: string) => {
      const value = name.endsWith('_id') || name === 'redundancy' || name === 'speed_condition'
        ? num(raw)
        : raw;
      // ★ The combined Qty Wide x High select (FAN_QTY_PAIR_FIELD) carries the
      // pair as one value; unpack it into the two fields everything else reads.
      // Its own name is never written to state — the rate envelope sweeps state
      // by field name, so a stray `qty_wide_high` key would ride along into the
      // vendor POST.
      state.current = name === FAN_QTY_PAIR_FIELD
        ? { ...state.current, ...fanQtyPairParse(raw) }
        : { ...state.current, [name]: value };
      // PR #773 fan-series-fix parity — Wheel Width never CARRIES OVER across a
      // Manufacturer/Series change (legacy setDefaults js:3143-3152,
      // changeParams js:4666-4671, `finished` js:1223-1234): force it unset
      // ('--') BEFORE the cascade so the old vendor's width can't survive into
      // the new one. The dependency engine still can't re-pick it (default_values
      // is [] and shouldSetDefaultIfNull is false for wheel_width_percent_id on
      // every rating mfg); the one thing that can is applyFieldZeroState's
      // sole-option rule, which since 2026-10-03 covers this field — when the
      // new vendor offers exactly ONE width the reload selects it instead of
      // leaving '--' next to the only real choice (see FAN_SOLE_OPTION_EXCLUDE).
      if (name === 'mfg_id' || name === 'series_id') {
        state.current = { ...state.current, wheel_width_percent_id: null };
      }
      // `setDefaults()` on a Manufacturer / Motor-Type change — js:4764-4766
      // (`if (name == "mfg_id" || name == "motor_type_id") { … self.setDefaults() }`)
      // plus the state listener at :1461-1463.
      //
      // Legacy runs this in ONE code path for both hosts: `setDefaults` (js:3415)
      // has no `self.header` branch, and neither does either call site. The port
      // previously gated the full preset table behind `headerMode` and left the
      // board with a two-manufacturer subset (TCF 64 + Annexair 237), so on the
      // AHU board a Manufacturer switch left the PREVIOUS vendor's Series, motor
      // mfg/series, efficiency, controller, motor position, supplied-by and
      // mounting in place — and the rate then went out carrying them. That is
      // the parked tracker observation "Switching fan mfg in the open selector
      // doesn't reset dependent selects to the new vendor's defaults (series
      // stayed BAE-DW after switching to Comefri) — legacy behavior not yet
      // compared"; comparing it against origin/dev shows legacy DOES reset.
      // The `motor_type_id` trigger was board-missing for every manufacturer too.
      //
      // FAN_SET_DEFAULTS is already the verbatim full legacy table (all eight
      // rating manufacturers plus 237), so restoring parity is purely dropping
      // the host gate — the preset literals are unchanged.
      if (name === 'mfg_id' || name === 'motor_type_id') {
        state.current = {
          ...state.current,
          ...(FAN_SET_DEFAULTS[Number(state.current.mfg_id)] ?? {}),
        };
      }
      // ★ A Manufacturer whose preset pins no `motor_type_id` — Twin City (64),
      // Greenheck (47), Lau (221), NYB (209) — used to INHERIT the previous
      // vendor's, and the previous vendor may well be one of the three whose
      // preset pins EC (ebm 37, Ziehl-Abegg 67, Rosenberg 164). The dependency
      // engine re-resolves the field a beat later (`default_values: [1, 2]`, so
      // AC wherever AC is offered), but until it does every reader sees the old
      // vendor's motor type — and the ones that react immediately get it wrong
      // and never look again. That is how Has VFD ended up UNCHECKED on Twin City
      // after a switch from an EC vendor (Anna, 2026-10-12): FanVfdTab's
      // auto-uncheck effect fired on the stale `2`, and nothing re-ticks the box
      // once the real AC type lands. Clearing it makes the gap read as "not
      // answered yet" instead of a wrong answer, which is what FanVfdTab's
      // `motorTypeKnown` guard waits on. The engine fills it from the NEW
      // vendor's own option list either way, so the settled value is unchanged.
      if (
        name === 'mfg_id' &&
        !('motor_type_id' in (FAN_SET_DEFAULTS[Number(state.current.mfg_id)] ?? {}))
      ) {
        state.current = { ...state.current, motor_type_id: null };
      }
      // ★ Qty Wide x High goes back to its default `--` on a Manufacturer
      // change (fanQtyDefaultsFor; it was the FAN_SET_DEFAULTS[237] pair when
      // Annexair was the only vendor that opened on `--`). On the mfg change
      // ONLY — motor_type_id must not throw away an array the user chose.
      if (name === 'mfg_id') {
        state.current = {
          ...state.current,
          ...(fanQtyDefaultsFor(state.current.mfg_id) ?? {}),
        };
      }
      // Dev-sync I (fan.selector.js:4962-4970 on merged dev): changing the fan
      // Manufacturer re-applies the has_vfd default, so the box always reflects
      // the NEW manufacturer rather than carrying the old one's value over. Note
      // that since DEFAULT_HAS_VFD became `true` (2026-10-05) this re-TICKS a box
      // the user had unticked, where it used to only untick one they had ticked —
      // it is the same legacy rule either way, but the visible direction flipped.
      // The legacy site runs in changeParams' motor-supports-VFD branch
      // (post-setDefaults), so the guard reads the POST-defaults motor_type_id;
      // when the motor can't take a VFD, FanVfdTab's own effect forces has_vfd off.
      //
      // ⚠ That guard USED TO BE `&& Number(state.current.motor_type_id) == 1`,
      // and it read a STALE value for exactly the vendors whose preset carries no
      // `motor_type_id` of its own — Twin City (64), Greenheck (47), Lau (221),
      // NYB (209). Arriving from an EC vendor (ebm 37, Ziehl-Abegg 67, Rosenberg
      // 164, all of which preset `motor_type_id: 2`) the field still held 2 at
      // this point, so the re-tick was skipped; the motor cascade then resolved
      // the real AC type a moment later and the VFD tab came alive with Has VFD
      // UNCHECKED and nothing left to re-tick it. Reported by Anna for Twin City,
      // 2026-10-12. Comefri (23) was immune only because its preset happens to
      // set `motor_type_id: 1`.
      //
      // So re-tick unconditionally and leave "this motor can't take a drive" to
      // the one place that watches the DERIVED flag and therefore sees the
      // settled value — FanVfdTab's auto-uncheck effect. The end state for an EC
      // vendor is identical (ticked for one render, then off); for an AC vendor
      // it is now the documented default instead of the previous vendor's value.
      if (name === 'mfg_id') {
        state.current = { ...state.current, has_vfd: DEFAULT_HAS_VFD };
      }
      // Selector-parity FAN fix — legacy ALSO re-runs setDischargeAndEffencity
      // inside the Manufacturer-change branch (fan.selector.js:5194),
      // unconditionally and AFTER setDefaults may have preset the fan Type
      // (e.g. DWDI→TCF presets type_id 1). Reads the CURRENT type_id and flips
      // discharge/system_effect with the SAME rule as setDischargeAndEffencity
      // (js:3647-3653): plenum/plug/cube (1/2/3) → discharge_id 2 +
      // system_effect_id null; DWDI (4)/unset → discharge_id null +
      // system_effect_id 20. Idempotent when the Type didn't change; without it
      // a mfg switch that presets a new Type left the OLD type's discharge pair
      // and the ISP/ESP lookup rated against a stale field.
      if (name === 'mfg_id') {
        const t = Number(state.current.type_id);
        const discharge = t === 1 || t === 2 || t === 3 ? 2 : null;
        state.current = {
          ...state.current,
          discharge_id: discharge,
          system_effect_id: discharge ? null : 20,
        };
      }
      // Selector-parity FAN restore — setDefaultsConnectedWithTypes
      // (js:3722-3781), which changeParams runs on EVERY Type change
      // (`else if (name === "type_id")`, js:5220-5222):
      //
      //   - AMCA Class is cleared unconditionally. BOTH branches of the legacy
      //     function do it — the Comefri (mfg 23) branch inside its state.set
      //     block (js:3766) and every other manufacturer in the `else`
      //     (js:3778) — so it is not manufacturer-conditional. The cascade
      //     below cannot re-fill it: `amca_class_id` carries no default_values
      //     and is outside FAN_DEFAULT_FIELDS for the rating manufacturers, so
      //     shouldSetDefaultIfNull is false and the sole-option auto-select is
      //     skipped, exactly as legacy's setDefaultsIfNullChecker does.
      //     Ported here (before onParentChange) so the reload already sees the
      //     cleared value, matching legacy's set-then-loadOptionsData order.
      //   - setDischargeAndEffencity (js:3638-3663, the function's tail): the
      //     fan Type picks which of the two get_isp_esp fields is live. DWDI
      //     (type 4) → discharge_id null + system_effect_id 20; plenum/plug/
      //     cube (1/2/3) → discharge_id 2 + system_effect_id null. Both feed
      //     the ISP/ESP lookup, so they must flip with the type or a stale
      //     value rates wrong.
      //
      // The Comefri-only extras in that branch (arr_id / series_id /
      // motor_position_id / mounting_method_id / housed_discharge_id /
      // mounting_id / orientation_id) are still unported — separate gap, not
      // touched here.
      if (name === 'type_id') {
        const discharge = Number(value) === 4 ? null : 2;
        state.current = {
          ...state.current,
          amca_class_id: null,
          discharge_id: discharge,
          system_effect_id: discharge ? null : 20,
        };
      }
      // Selector-parity FAN restore — checkSpacing (js:8896-8910): a Mounting
      // change to anything other than "Isolated" (1) drops both spacing selects
      // to id 7 and clears their U/D inputs (the rows then go non-clickable).
      if (name === 'mounting_id' && value != 1) {
        state.current = {
          ...state.current,
          fan_spacing_sides_id: 7,
          fan_spacing_top_btm_id: 7,
          fan_spacing_ud_sides: '',
          fan_spacing_ud_top_btm: '',
        };
      }
      // a real user param change ARMS the cascade — from here the engine resumes
      // legacy narrow+drop of out-of-range children (F3 correctness gate).
      cascadeArmed.current = true;
      // a param change invalidates the prior rated outputs + supersedes any rate.
      setRatedRows(null);
      setSelectedRow(null);
      setChosenCurve(null);
      // legacy changeParams `self.choosen = null` → checkButtonsAvailability
      // disarms #select until the next Rate (fan.selector.js 4631 → 3648).
      // The arming effect below derives #select from chosenIndex alone, so
      // nulling it here IS the legacy disarm (P2-verify A4 parity fix).
      setChosenIndex(null);
      abortRating();
      // Sync L (fan.selector.js resetFields, commit 77263bea90) — ALREADY
      // EQUIVALENT, no port. Dev's resetFields nulled a fixed block
      // {wheel_width_percent_id, motor_enclosure_id, motor_housing_id,
      // motor_power, motor_speed_nominal_id} on EVERY caller and (old bug) then
      // only reloaded the childless motor_enclosure_id — a no-op — so non-select
      // callers (Airflow, air stream, Motor-tab selects, project fields) left
      // those fields stuck at '--'. Dev's fix reloads via the series_id root for
      // those callers and skips it for select-driven changes that reload
      // themselves (`willReloadItself = !!last_changed`). Our React host has NO
      // resetFields and never bulk-nulls those fields: a select-driven change
      // self-reloads via onParentChange(name) below (every fan/motor select is a
      // DocSelect → onSearchField → graph field — the `willReloadItself` skip
      // branch); non-select edits (airflow/tsp here, air_stream via onModelField,
      // project fields) touch none of the five, so a value a prior real cascade
      // resolved — including any sole-option auto-select (useDependencyGraph
      // applyNewValues) — simply persists. The "stuck at '--' even when only one
      // option is valid" condition can't arise: nothing nulls them, and no
      // non-select field is a parent of any of the five in FAN_DEPENDENCY_GRAPH.
      if (FAN_DEPENDENCY_GRAPH[name]) {
        graphKicked.current = true;
        graph.onParentChange(name);
      }
      // the live airflow cross-compute (see calcAirflow) — legacy's
      // changeFanField :4660-4668 + changeParams :4876-4880 trigger set, neither
      // of which branches on `self.header`. Previously header-gated, so on the
      // board typing a total airflow did not fill Airflow-per-fan, and changing
      // Qty Wide/High or Redundancy did not re-divide it — the board had only a
      // one-shot backfill at seed time.
      // (`typedHere` only for the two airflow inputs — see calcAirflow.)
      if (name === 'airflow_per_fan') calcAirflow('per_fan', true);
      else if (name === 'airflow_actual') calcAirflow('total', true);
      else if (
        name === 'qty_wide' ||
        name === 'qty_high' ||
        name === FAN_QTY_PAIR_FIELD ||
        name === 'redundancy_id' ||
        // ★ a Manufacturer change too (Anna, 2026-10-12). It resets Qty Wide x
        // High to `--` a hundred lines above, which is the same event as the
        // user picking `--` by hand, so Airflow-per-fan has to follow it the
        // same way: blanked, because there is no array left to divide the total
        // by. It did not, and that is the reported bug — a fan rated and
        // SELECTED at 1x2 persists `total / 2` as its per-fan figure
        // (buildFanSelectPayload), the reopen seeds it, and switching
        // manufacturer left that figure on screen, and in the next rating
        // envelope, under a Qty Wide x High reading `--`.
        name === 'mfg_id'
      ) {
        calcAirflow('total');
      }
      // ★ HEADER MODE (changeParams js:4901-4905): switching Manufacturer
      // RESTORES that manufacturer's own saved entry (`findFromStoraga`) instead
      // of saving; every other param change saves. Legacy applies the restore in
      // two steps — immediately, then again from the cascade `finished` hook
      // (`findFromStoraga(needToSearch, true)`, js:1240-1244) where it also
      // re-fires the rate. The pending-restore slot below reproduces step two.
      // Genuinely header-only in legacy too: `saveInStorage` returns early on
      // `!self.header` (js:2552).
      if (headerMode) {
        if (name === 'mfg_id') {
          // legacy self.fan_mfg_name = the selected option's TEXT (js:4808).
          fanMfgNameRef.current = value == null
            ? null
            : fanCmsSelectedLabel(fanDoc as FanCmsDoc, 'mfg_id', value as any) || null;
          const entry = findEntryByMfg(storageMapRef.current, value);
          if (entry) pendingRestore.current = { entry, applyState: true };
          else saveInStorage({ rate: false, choosenIndex: null });
        } else {
          saveInStorage({ rate: false, choosenIndex: null });
        }
      }
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, markChanged, bump, abortRating, headerMode, saveInStorage, fanDoc, calcAirflow]
  );

  const onModelField = useCallback(
    (name: string, raw: string | null) => {
      const value = name.endsWith('_id') ? (raw == null ? null : num(raw)) : raw;
      state.current = { ...state.current, [name]: value };
      if (headerMode) saveInStorage();
      markChanged();
      bump();
    },
    [markChanged, bump, headerMode, saveInStorage]
  );

  // ---- #rate required-field gate (checkRequiredFields) ----
  const requiredFilled = useMemo(() => {
    const s = state.current;
    const mfg = Number(s.mfg_id);
    // legacy tests mfg_id FIRST and unconditionally (js:7731-7733), before the
    // per-mfg loop and regardless of any Set.
    if (!fanFieldIsFilled('mfg_id', s.mfg_id)) return false;
    const req = FAN_REQUIRED_FIELDS[mfg];
    // A mfg with no Set would leave legacy's `valid` TRUE (its loop only tests
    // fields the Set `.has()`), i.e. dev enables #rate. We keep the stricter
    // `false` because the branch is unreachable — every mfg_id in MasterAHUFan
    // (23/37/47/64/67/164/209/221/237) is in the map — and enabling Rate for a
    // manufacturer with no rating contract would just fire a doomed vendor POST.
    if (!req) return false;
    for (const f of req) {
      // ★ legacy iterates the RENDERED inputs and asks the Set, not the other
      // way round — so a required field the template never renders is silently
      // skipped. `wheel_dia_percent_id` is the only one, and enforcing it here
      // greyed #rate forever on all five vendor mfgs. See
      // FAN_REQUIRED_FIELDS_WITHOUT_INPUT for the full derivation.
      if (FAN_REQUIRED_FIELDS_WITHOUT_INPUT.has(f)) continue;
      // ★ Qty Wide x High on `--`: `calcAirflow` blanks Airflow-per-fan when
      // there is no array to divide the total by, so enforcing it here would
      // grey #rate for exactly the pick that is supposed to rate the whole
      // grid. Airflow-per-fan is not part of the Annexair rate envelope either
      // (`part_of_json_params` in the vendor input processor sends
      // airflow_actual and the qty pair, never the per-fan figure) — the vendor
      // divides the total by the array itself, once per combination. Scoped to
      // the vendors that offer `--`; everyone else enforces it as before.
      if (
        f === 'airflow_per_fan' &&
        FAN_QTY_UNSET_MFGS.has(mfg) &&
        fanQtyPairIsUnset(s.qty_wide, s.qty_high)
      ) {
        continue;
      }
      if (!fanFieldIsFilled(f, s[f])) return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, detail]);

  // a saved (rated) fan starts with Select ENABLED; an unrated one stays disabled
  // until a Rate produces a chosen row (legacy checkButtonsAvailability:
  // `choosen && …` — 3648). chosenIndex mirrors legacy `choosen`: the mount
  // seed sets 0 for a saved fan (master_fan_id → buildSelectedRow), doRate →
  // chooseFan sets it, and a param change NULLS it (changeParams parity) —
  // so #select must derive from chosenIndex ALONE. The old
  // `Boolean(m.master_fan_id) ||` kept a persisted fan armed FOREVER (a
  // param change could then Select stale/garbage data — P2-verify A4).
  // ★ `&& !curveLoading` is dev's SECOND gate on the same two buttons, the one
  // render_outputs applies from `self.loading` (js:3172-3183: `#select` gets
  // `disable`, `#print` gets `non-clickable-disable`, both removed in the else).
  // Without it the port let a Select land while the per-row curve POST was
  // still out, and that Select writes the curves the chosen row does NOT have —
  // `PUT ahu/fans/curves/<id>` with `sp`/`bhp`/`system_curve` = "null" — so the
  // fan reopens (and prints, and reports) with no curve at all.
  // ★ THIRD gate, #select only (Anna, 2026-10-05): "Has VFD" ticked with no
  // catalog VFD chosen keeps Select DISABLED, so a fan whose VFD tab is
  // CLICKABLE cannot be saved claiming a drive it hasn't got — the other half of
  // defaulting the box on (DEFAULT_HAS_VFD; see the ⚠ there for the paths a
  // non-clickable tab still leaves open). Same predicate as the doSelect abort below
  // (isVfdValidNow), so the two can't disagree; it passes whenever the box is
  // unticked or the VFD tab itself is non-clickable, which is why a DC-motor or
  // not-yet-rated fan is unaffected. The red `vfd-tab-invalid` mark on the VFD
  // nav item (legacy checkButtonsAvailability js:3651) is what says why.
  // NO DEP ARRAY: has_vfd and master_vfd_id live in refs whose writers bump(),
  // so re-deriving on every render is what keeps this live — a dep array would
  // freeze the gate at the value it had when chosenIndex last changed.
  useEffect(() => {
    setSelectEnabled(chosenIndex != null && !curveLoading && isVfdValidNow());
    // #print rode the SAME gate as #select (checkButtonsAvailability
    // js:3715-3733 toggles both together), and print IS live for fan
    // (component_selector.tmpl:61; the chassis only suppresses it for
    // energy_wheel / plate_heat_exchanger via `hide-print`). BOTH hosts now
    // supply the setter — the board chassis used to pass none, which left
    // `#print` greyed there even with a fan chosen. Printing a rated fan does
    // not write anything, so the VFD gate deliberately does NOT extend to it.
    setPrintEnabled?.(chosenIndex != null && !curveLoading);
  });

  useEffect(() => {
    // SCOPED to this selector's own chassis. Legacy's own gate is accidentally
    // scoped by class (`#rate.${self.header ? 'all' : 'fan'}-rate[data-type=
    // "fan"]`, js:6653-6662) — an un-scoped document.getElementById('rate')
    // would hit the BOARD dialog's #rate first (duplicate ids) whenever the
    // header popup and a board dialog coexist. Discovery §7.3 trap 1.
    const btn = chassisEl()?.querySelector<HTMLElement>('#rate');
    if (!btn) return;
    // ★ Phase 6.5 — OWN-TAB PREDICATE. The header popup never unmounts a visited
    // tab (component_selector.js:126-186 is show/hide), so all four selectors
    // stay mounted and would fight over the ONE shared `#rate`: whichever
    // rendered last won, and returning to Fan after visiting HEAT WHEEL / PLATE
    // HEAT EXCHANGER left RATE greyed out with valid inputs. Legacy is immune
    // because every gate is scoped to its own tab —
    // `$('#rate.all-rate[data-type="<own>"]')` (fan :1998/:6657, coil
    // :1656/:2677, ewheel :2469, phx :2427) — which matches NOTHING while
    // another tab is active. The chassis stamps `data-type` from the active tab
    // (component_selector.js:173/:181; IndependentRatingPopup `data-type={tab}`)
    // and each selector's `activate.name` IS that tab key, so comparing the two
    // reproduces legacy's scoping exactly.
    // HEADER-ONLY: the board hosts one dialog / one selector / one `#rate`, so
    // its behaviour stays byte-identical (and a board `data-type` that ever
    // disagreed with `eventName` could otherwise freeze the button).
    if (headerMode && btn.dataset.type !== eventName) return;
    btn.classList.toggle('non-clickable', !requiredFilled);
  });

  // B4: a disabled VFD OUTPUT tab can't stay selected. Legacy re-renders the
  // whole output tmpl (Fan pane hard-coded `selected`) whenever the gate
  // flips — render_outputs on has_vfd change / smartlist select / compute
  // (js:4628/8282/8471) — so a stale VFD pane never lingers; React keeps tab
  // state across renders, so snap back explicitly.
  useEffect(() => {
    if (
      outputTab === 'vfd' &&
      !(Boolean(state.current.has_vfd) && !isVfdNone(vfdRef.current?.master_vfd_id))
    ) {
      setOutputTab('fan');
    }
  });

  // ---- section/tunnel resize listeners (sectionSizesChanged) ----
  useEffect(() => {
    const sec = activate.section as { id?: number; tunnel_id?: number } | undefined;
    if (!sec) return undefined;
    const offs: Array<() => void> = [];
    const onResize = () => bump();
    if (sec.tunnel_id != null)
      offs.push(bus.on(`changed:tunnel_${sec.tunnel_id}:sizes` as any, onResize));
    if (sec.id != null) {
      // legacy event key is misspelled 'secrion' — match verbatim.
      offs.push(bus.on(`changed:secrion_${sec.id}:casing_insulation_id` as any, onResize));
      // refetch_fan_<sectionId>_isp → re-pull ISP/ESP (CODE-ONLY, userArmed-gated).
      offs.push(
        bus.on(`refetch_fan_${sec.id}_isp` as any, () => {
          if (userArmed.current) void doIsp(true);
        })
      );
    }
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, bump]);

  // ---- collectData (legacy 4128-4256) — the search-field → envelope input ----
  // ★ PARITY-CRITICAL (2026-07-08 replay evidence): the envelope input is keyed
  // by each search-field's `data-name` (type/series/arr/…, NOT the raw *_id
  // state keys) and valued by the selected option's `data-value` (the CMS/
  // master row's series/value/power CODE, jQuery-typed — "4" → 4). The vendor
  // mappers (integrated_parts/fan/<vendor>/mapping.py) translate exactly those
  // name-keys and silently SKIP unknown ones — raw *_id keys made TCF 400
  // (missing series/frequency/arr) and NYB 422 (FanSize/Arrangement/FanClass/
  // WheelMaterialCategory/IsVSD). Null selections go out as null (legacy
  // "None" → null, js:4145); empty NUMERIC inputs are omitted (undefined is
  // dropped by JSON.stringify).
  /** `doIsp` is declared below `runRate`; the rate tail reaches it through this
   *  ref rather than reordering the two callbacks. */
  const doIspRef = useRef<((empty?: boolean) => Promise<boolean>) | null>(null);

  const collectData = useCallback((): Record<string, unknown> => {
    const s = state.current;
    const mfg = Number(s.mfg_id);
    const data: Record<string, unknown> = {};
    const raw = (field: string): unknown => {
      const v = s[field];
      return v == null || v === '' || v === 'None' ? null : v;
    };

    // ① fans-CMS-doc selects → option data-value (power||series||value||id,
    // js:7451-7460); motor_mfg options carry the mfg NAME (js:7409); ZA style
    // sends the option TEXT (js:4237-4239); greenheck re-keys speed_nominal
    // (js:4149-4153). Unresolvable rows fall back to the raw state value
    // (legacy option-without-data-value → elem.val()).
    for (const [field, key] of Object.entries(FAN_RATE_DOC_FIELDS)) {
      const outKey = field === 'motor_speed_nominal_id' && mfg == 47 ? 'speed_nominal' : key;
      const v = raw(field);
      if (v == null) {
        // ZA style: legacy re-maps a "None" style to '--' AFTER the null fold
        // (js:4237-4239 `if (value=="None") value="--"`) — everything else
        // stays null (P1-verify fix, 2026-07-09).
        data[outKey] = field === 'style_id' && mfg == 67 ? '--' : null;
        continue;
      }
      const row = fanCmsOptionList(fanDoc as FanCmsDoc, field).find(
        (r) => String(r.id) == String(v)
      ) as Record<string, any> | undefined;
      if (field === 'motor_mfg_id') {
        // Motor mfg is name-matched by the backend
        // (MasterManufacturer.name__iexact, aw_and_motor.py:26). Legacy resolves
        // it to `.name` (fan.selector.js:2043 getSelectedMotor). Never fall back
        // to the raw id: `name__iexact=<int>` → upper(integer) → 500.
        data[outKey] = row ? (row.name ?? row.value ?? null) : null;
      } else if (FAN_RATE_MOTOR_NAME_FIELDS.has(field)) {
        // motor_type/efficiency/enclosure — backend value__iexact/name
        // (aw_and_motor.py:18/34/42). The cms/1 doc holds only the tenant's
        // subset (e.g. enclosure = TEFC/TEAO, no ODP), so a value outside it
        // has no row → resolve to null, NOT the numeric id (upper(integer) →
        // 500). Mirrors legacy's harmless string-id skip. See
        // FAN_RATE_MOTOR_NAME_FIELDS / fanMotorNameValue.
        data[outKey] = fanMotorNameValue(row);
      } else if (field === 'style_id' && mfg == 67) {
        data[outKey] = row ? String(row.name ?? row.value ?? '') : '--';
      } else {
        data[outKey] = row ? fanOptionDataValue(row) : v;
      }
    }

    // ② master-list selects → the row's vendor CODE (the structural-switch
    // data-value, js:7366-7369 — mtr_controller_id → 'vfd' [NYB IsVSD],
    // in_guard id 2 → 'inlet_guard_hpa', afs_xmitter → .configuration,
    // mounting_method → 'under_base' [the TCF fm_base lookup]). Narrowed
    // cascade list first, then the FULL (F3) list, then the raw state value.
    // ebm/ZA re-key inlet_guard_id → inlet_guard with null → '--' (js:4154-63).
    for (const [field, key] of Object.entries(FAN_RATE_MASTER_FIELDS)) {
      const guardRename = field === 'inlet_guard' && (mfg == 37 || mfg == 67);
      const outKey = guardRename ? 'inlet_guard' : key;
      const v = raw(field);
      if (v == null) {
        data[outKey] = guardRename ? '--' : null;
        continue;
      }
      const code =
        fanMasterOptionValue(field, v as any, masterListsRef.current[field] as any[] | undefined) ??
        fanMasterOptionValue(field, v as any, masterFullListsRef.current[field] as any[] | undefined);
      data[outKey] = code != null ? jqDataValue(code) : v;
    }

    // motor_power → 'power' (tmpl:988 data-name; the option's value/data-value
    // ARE the power itself — a number).
    data.power = raw('motor_power') == null ? null : Number(s.motor_power);

    // ③ spring-isolator CMS selects (isolator_mfg options carry the mfg NAME,
    // the isolator_mfg_id/motor_mfg_id branch of js:7405-7410).
    for (const [field, key] of Object.entries(FAN_RATE_ISOLATOR_FIELDS)) {
      const v = raw(field);
      if (v == null) {
        data[key] = null;
        continue;
      }
      const row = fanCmsOptionListByPath(
        isolatorDoc as FanCmsDoc,
        FAN_ISOLATOR_CMS_PATHS[field]
      ).find((r) => String(r.id) == String(v)) as Record<string, any> | undefined;
      // Backend resolves these by TEXT match (build_aw_context, aw_and_motor.py
      // :117-127): isolator_mfg name__iexact, isolator_series series__iexact,
      // isolator_type value__iexact, specified_deflection value=. A raw numeric
      // id would hit upper(integer)/varchar=int → 500, so when the doc omits the
      // row send null (the backend's `if mfg and series and type:` guard then
      // skips si_params — legacy's net effect for an unmatched string id).
      data[key] = row
        ? field === 'isolator_mfg_id'
          ? (row.name ?? row.value ?? null)
          : fanOptionDataValue(row)
        : null;
    }

    // ④ motor-base CMS selects (keys keep their *_id data-names).
    for (const [field, key] of Object.entries(FAN_RATE_MOTOR_BASE_FIELDS)) {
      const v = raw(field);
      if (v == null) {
        data[key] = null;
        continue;
      }
      const row = fanCmsOptionListByPath(
        motorBaseDoc as FanCmsDoc,
        FAN_MOTOR_BASE_CMS_PATHS[field]
      ).find((r) => String(r.id) == String(v)) as Record<string, any> | undefined;
      data[key] = row ? fanOptionDataValue(row) : v;
    }

    // ⑤ the PER-MFG voltage key (tmpl:1026-1030 + js:4165-4186/4202-4206):
    // 64/221 → frequency = the Hz tail of the option NAME "460/3/60" → 60
    // (int — the replay evidence); 164/37/67 → motor_voltage = the NAME (the
    // ZA/rosenberg backends look the MasterAHUVoltage row up BY NAME);
    // 47 → voltage_id = the raw select id; others → voltage_id = the NAME.
    //
    // Sync K (fan.selector.js:4303-4323 @dev) — Annexair 237: legacy's generic
    // .search_field loop missed its Voltage select, so dev resolves it
    // explicitly as `state.motor_voltage_id ?? fanModel.motor_voltage_id`,
    // NAME-resolved from cms fan.motor.voltage, sent as input.voltage_id every
    // time. This branch ALREADY did the key ('voltage_id' — the
    // fanVoltageRateKey default), the NAME resolution (name ?? raw id) and the
    // always-send for 237; state is also seeded from the model column at open
    // (MODEL_FIELDS). The one gap was the MODEL fallback when state's voltage
    // is cleared AFTER open (the motor_power cascade reset / an mfg switch):
    // mirror it via init.current — the port's model_data.model analog (board:
    // the saved column; header: the defaults/storage merge, like legacy's
    // js:713-721). null still emits `voltage_id: null` (dev omits the key;
    // backend-identical — aw_and_motor.py guards `if voltage_id:`).
    {
      const vKey = fanVoltageRateKey(mfg);
      const v =
        raw('motor_voltage_id') ??
        (mfg == 237 ? (init.current.motor_voltage_id ?? null) : null);
      if (v == null) {
        data[vKey] = null;
      } else if (mfg == 47) {
        data[vKey] = String(v);
      } else {
        const vRow = fanCmsOptionListByPath(fanDoc as FanCmsDoc, 'fan.motor.voltage').find(
          (r) => String(r.id) == String(v)
        ) as Record<string, any> | undefined;
        const name = vRow?.name != null ? String(vRow.name) : null;
        if (vKey === 'frequency') {
          data.frequency = name ? Number(name.substring(name.lastIndexOf('/') + 1)) : null;
        } else {
          // voltage_id/motor_voltage are NAME-matched (MasterAHUVoltage.get(
          // name=…), aw_and_motor.py:50; ZA/rosenberg look up by name too). The
          // cms/1 doc's voltage list is a tenant subset (5 of 8 on dev3), so an
          // out-of-subset voltage has no row → send null, NOT the numeric id:
          // `get(name=<int>)` → varchar=integer → 500. `if voltage_id:` skips
          // null. (mfg 47 above keeps its legacy String(v) raw-id form.)
          data[vKey] = name ?? null;
        }
      }
    }

    // ⑤b Finish (Options tab). Sent for EVERY manufacturer as the raw
    // MasterAHUFanFinish id: each vendor's handler then keeps only the
    // candidates KSDM's own catalog records in that finish
    // (integrated_parts/fan/finish.py::narrow_by_finish). No vendor's selection
    // API takes a finish, so this is never translated into a vendor parameter —
    // the mappers ignore the key, and an unmapped input is not an error.
    // null goes out as null, which the backend reads as "no finish filter" —
    // which is what a Mfg/Series with no MasterAHUFanMfgSeriesFinish row sends,
    // since its Finish select has no options and is not rendered at all. Only a
    // finish the dialog is SHOWING is sent (shownFinishId), so one with no
    // select cannot filter a rating the user has no way to widen again.
    data.finish_id = shownFinishId();

    // ⑥ numeric search-inputs → Number, comma-stripped; EMPTY ones OMITTED.
    //
    // ★ `airflow_per_fan` is also omitted whenever Qty Wide x High is on `--`
    // (Anna, 2026-10-12), whatever the box happens to be showing. A per-fan
    // duty only means something next to an array, and with `--` the array is
    // what the rating is being asked to find: the backend's fan-out divides the
    // TOTAL by each candidate fan count itself (`qty_expansion._data_for_fan_count`),
    // so a figure sent from here is at best ignored and at worst — when the
    // grid cannot be built and the fan-out declines — the duty the vendor
    // actually rates against. The box can hold a stale figure because a
    // previously SELECTED fan persists `airflow_actual / fan_qty`
    // (buildFanSelectPayload) and the reopen seeds it, so dropping the key here
    // is what makes "no array ⇒ no per-fan duty" true of the envelope rather
    // than of the input field only.
    const qtyPairUnset =
      FAN_QTY_UNSET_MFGS.has(mfg) && fanQtyPairIsUnset(s.qty_wide, s.qty_high);
    for (const f of FAN_RATE_NUMERIC_FIELDS) {
      if (f === 'airflow_per_fan' && qtyPairUnset) continue;
      const v = s[f];
      if (v == null || v === '') continue;
      const n = Number(String(v).replace(/,/g, ''));
      if (!Number.isNaN(n)) data[f] = n;
    }

    // ⑦ wheel width percent → its CMS .value (legacy 4207-4212). The
    // dia-percent search-field no longer exists in the legacy tmpl, so ONLY
    // width goes out (the js:4213 branch is dead there).
    {
      const v = raw('wheel_width_percent_id');
      const widthRow = fanCmsOptionList(fanDoc as FanCmsDoc, 'wheel_width_percent_id').find(
        (r) => String(r.id) == String(v)
      );
      data.wheel_width_percent =
        v == null ? null : widthRow ? jqDataValue((widthRow as any).value) : v;
    }

    // ⑧ the computed tail (legacy 4243-4255): qty = wide×high (−1 on N+1
    // redundancy), the ZA/ebm/rosenberg airway sizes, the PROJECT elevation
    // and the mtr_controller echo (stripped per-mfg by buildFanRateInput).
    data.qty = (Number(s.qty_wide) || 1) * (Number(s.qty_high) || 1);
    if (mfg == 67 || mfg == 37 || mfg == 164) {
      const sec = activate.section as any;
      data.aw_width = sec?.aw_width ?? 100;
      data.aw_height = sec?.aw_height ?? 100;
    }
    if (Number(s.redundancy_id) == 2) (data.qty as number) -= 1;
    // Selector-parity FAN restore — "Altitude" is an editable override
    // (fan.selector.tmpl:601 `.project_field`). Legacy collectData reads
    // model_data.project.elevation (js:4624), which changeProjectField updates on
    // every keystroke; the port only read the untouched project value. Prefer the
    // live state override, falling back to the project's stored elevation.
    data.elevation =
      s.elevation != null && s.elevation !== ''
        ? Number(String(s.elevation).replace(/,/g, ''))
        : projectElevation;
    data.mtr_controller = data.mtr_controller_id;
    return data;
  }, [fanDoc, isolatorDoc, motorBaseDoc, activate, projectElevation, shownFinishId]);

  // ---- RATE (CODE-ONLY, userArmed) ----
  // Set just before a re-rate the header popup runs by itself (restoring a
  // saved entry, or a manufacturer switch): that is not a user's rating, so
  // it is kept out of component_rated.
  const autoRateRef = useRef(false);
  const runRate = useCallback(
    async (chooseIdx = 0, trackRating = true) => {
      if (!requiredFilled) return;
      userArmed.current = true;
      // dev #1033 (fan.selector.js:4265 `self.selectet_output_element = null`
      // in rate()): every Rate drops the remembered output sub-tab, so the
      // re-render lands on the default Fan-curve pane.
      setOutputTab('fan');
      setRateErrors([]);
      setRateWarnings([]);
      const s = state.current;
      const mfg = Number(s.mfg_id);
      const fanType = fanTypeValue(fanDoc as FanCmsDoc, s.type_id as any);
      const data = collectData();
      const input = buildFanRateInput(
        data,
        mfg,
        (detail as FanModel | undefined)?.construction_class_id ?? null,
        // KA-5254 (js:4081-4092): the fan slot id + the chosen/saved motor id
        // for the MIN_AW mfgs. React has no motor-row pick flow, so
        // selectedMotor (the saved motor row) mirrors legacy
        // (motor_choosen || selectedMotorData), falling back to the model's
        // master_motor_id.
        // ★ HEADER MODE parity (REQUIRED, discovery §3.2): the whole KA-5254
        // block is gated on `self.current_model?.id` (js:4081-4092), which is
        // undefined there — so `ahu_fan_id` AND `master_motor_id` are OMITTED
        // from the envelope. The live capture confirms neither key is present.
        headerMode
          ? { ahuFanId: null, masterMotorId: null }
          : {
              ahuFanId: modelId,
              masterMotorId:
                (selectedMotor?.id as number | string | undefined) ??
                ((detail as Record<string, any> | undefined)?.master_motor_id as number | null) ??
                null,
            }
      );
      const envelope = buildRatingEnvelope('fan', mfg, input, {
        constructionClassId: (detail as FanModel | undefined)?.construction_class_id ?? null,
      });
      // TCF-family carries the type value; if unresolved legacy bails (3307).
      if (FAN_MFG_MAPPING[mfg] && !FAN_FUNCTION_NAME_HAS(mfg) && !fanType) return;
      // BUG 2: greenheck/ebm_papst/ziehl_abegg are FORCED to type:'plug' by
      // buildRatingEnvelope (legacy hardcodes type:'plug' at 3206/3214). Do NOT
      // overwrite that with the CMS type ("plenum") — the vendor rejected the
      // CMS type with "Invalid route is selected".
      const manufacturer = FAN_MFG_MAPPING[mfg];
      if (fanType && !FAN_PLUG_TYPE_MFGS.has(manufacturer)) (envelope as any).type = fanType;

      // ★ dev EMPTIES the output before the vendor round-trip —
      // `self.$el.find("#for-output-rendering").html("")` (fan.selector.js:4412),
      // fired here, after the last early return and immediately before the POST,
      // so a bail-out leaves the previous rating on screen. The port kept the
      // previous rows rendered for the whole call, so the Rate overlay came up
      // UNDER the old output table instead of over a cleared pane and the dialog
      // scrolled down on every re-rate. `ratedRows = null` is the port's own
      // "nothing rendered" signal (it drives the `hidden` on
      // `#for-output-rendering`), so nulling it here is that `.html("")`.
      // chosenIndex/chosenCurve/selectedRow follow it: dev's DOM has no chosen
      // row once the pane is empty, and leaving them set would redraw the old
      // curve against the new rows if the response ever came back short.
      setRatedRows(null);
      setChosenIndex(null);
      setChosenCurve(null);
      setSelectedRow(null);

      const body = await dispatchRating(envelope, { track: trackRating });
      if (body.cancelled) return;
      // legacy generetionTimeStamp format — rating_datetime is persisted.
      ratedDatetime.current = fanRatingTimestamp();
      sessionRated.current = true; // qualifies the doSelect FULL-APPLY path
      // legacy re-stamps the sort state on every rate (js:4436-4437) — the rows
      // below are already ordered ascending by bhp, which is what `desc` means.
      setRatedSort({ by: 'bhp', order: 'desc' });
      // dev re-pulls ISP/ESP on BOTH rate outcomes — `get_isp_esp(null, true)`
      // at js:4429 (error) and js:4523 (models) — with `empty` TRUE, i.e. no
      // `master_fan_id`, because the stored fan is no longer what is on screen.
      void doIspRef.current?.(true);
      if (body.ok && body.models && body.models.length) {
        // KA-5254 (js:4131-4152): REQUIRED AW W x H renders straight from the
        // rating response for every row — no /ahu/fans/<id> fetch per row.
        // Warnings compare against the section AW (open-time snapshot, like
        // legacy self.section at rate time).
        const sectionAwWidth = parseFloat(String((activate.section as any)?.aw_width));
        const sectionAwHeight = parseFloat(String((activate.section as any)?.aw_height));
        const sorted = [...body.models].sort((a: any, b: any) => a.bhp - b.bhp);
        const mapRatedRow = (r: any) => {
          const row = { ...r };
          // per-row formatting for the TCF-family (legacy 3279-3287; dev-sync H
          // hardened it for Annexair rows — fan.selector.js:4237-4252 on merged
          // dev: fan_model falls back to the nomenclature `model`, amca_class
          // null-safe, bhp_total falls back to bhp when the vendor omits it).
          if (mfg != 23 && mfg != 164 && mfg != 47 && mfg != 37 && mfg != 67) {
            row.fan_model = row.fan_model || row.model;
            row.amca_class = String(row.amca_class ?? '').replace(/ /g, '');
            if (row.bhp != null) row.bhp = (Math.round(row.bhp * 100) / 100).toFixed(2);
            row.bhp_total = (Math.round((row.bhp_total ?? row.bhp) * 100) / 100).toFixed(2);
          }
          if (row.min_aw_width != null && row.min_aw_height != null) {
            row.aw_width = row.min_aw_width;
            row.aw_height = row.min_aw_height;
            row.aw_width_height = `${row.min_aw_width} x ${row.min_aw_height}`;
            row.aw_width_warning =
              !isNaN(sectionAwWidth) && parseFloat(String(row.min_aw_width)) > sectionAwWidth;
            row.aw_height_warning =
              !isNaN(sectionAwHeight) && parseFloat(String(row.min_aw_height)) > sectionAwHeight;
          }
          return row;
        };
        const mapped = sorted.map((r: any) => {
          const row = mapRatedRow(r);
          // ★ Qty Wide x High alternatives (Annexair `--`). The handler returns
          // ONE row per fan model carrying every array it rated for, each with
          // its own full rated payload — the vendor's bhp/speed/curve and the
          // required airway all differ per array, so switching the pick has to
          // swap the WHOLE row, not just the two numbers. Pre-map each variant
          // through the same per-row formatting here, so `chooseWideHigh` is a
          // lookup rather than a second, divergent mapping path.
          const variants = r.wide_high_variants as Record<string, any> | undefined;
          const mapped: Record<string, any> = {};
          for (const [key, variant] of Object.entries(variants ?? {})) {
            mapped[key] = mapRatedRow(variant);
          }
          if (Object.keys(mapped).length) row.__wh_rows = mapped;
          // the payload itself is not needed again once it is mapped, and it is
          // by far the largest thing on the row.
          delete row.wide_high_variants;
          // ★ fit-first ordering + the per-dimension over-airway flags, off the
          // mapped variants (so they use the same warnings the REQUIRED AW
          // column renders) — see `orderWideHighOptions`. Runs even when there
          // are no variants: a rating of ONE explicit array still carries that
          // array as the single option, and it is just as able to need more
          // airway than the section has (its warnings are on the row itself).
          if ((row.wide_high_options as unknown[] | undefined)?.length) {
            row.wide_high_options = orderWideHighOptions(row.wide_high_options, mapped, row);
          }
          return row;
        });
        // ★ the models that fit the section's airway first, smallest array
        // first within that — see `orderRatedRowsFitFirst`. It runs on the
        // MAPPED rows, after the per-row `aw_*_warning` flags and the default
        // `qty_wide`/`qty_high` are both on the row, and before `chooseFan`
        // below, so the auto-chosen row 0 is the best FITTING model at the
        // smallest array rather than whichever model happened to have the
        // lowest bhp.
        const rows = orderRatedRowsFitFirst(mapped);
        setRatedRows(rows);
        setRateWarnings(normalizeRateWarnings(body.warnings));
        setSelectedRow(null);
        // auto-choose the requested row (legacy choosen=0 default).
        chooseFan(chooseIdx, rows, mfg, fanType);
        setSelectEnabled(rows.length > 0);
        // ★ HEADER MODE: the rate tail saves (js:5967 saveInStorage at the end
        // of the rate handler) — this is what stamps `rate:true` so the next
        // popup construction AUTO-RE-FIRES the rate.
        if (headerMode) saveInStorage({ rate: true, choosenIndex: chooseIdx });
      } else {
        setRatedRows([]);
        setChosenIndex(null);
        setChosenCurve(null);
        setRateErrors((body.errors ?? []).filter(Boolean));
        setRateWarnings(normalizeRateWarnings(body.warnings));
        if (headerMode) saveInStorage({ rate: false, choosenIndex: null });
      }
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requiredFilled, collectData, dispatchRating, fanDoc, detail, modelId, selectedMotor, activate, setSelectEnabled, bump, headerMode, saveInStorage]
  );

  /**
   * The `#rate` entry point. Raises the legacy `.fan-preloader` overlay for the
   * FULL duration of the handler — the vendor round-trip AND the row mapping /
   * commit that draws the output table — instead of only for the POST, which is
   * all `isRating` covers (fan.selector.js:4405-4409 → js:3025-3034).
   */
  const doRate = useCallback(
    async (chooseIdx = 0) => {
      const trackRating = !autoRateRef.current;
      autoRateRef.current = false;
      if (!requiredFilled) return;
      const seq = ++rateSeq.current;
      setRating(true);
      try {
        await runRate(chooseIdx, trackRating);
      } finally {
        // a rate that has already been superseded must not pull the overlay
        // out from under the one that replaced it.
        if (rateSeq.current === seq) setRating(false);
      }
    },
    [requiredFilled, runRate]
  );

  // ---- chooseFan (row pick — for curve-vendors a SECOND /rating/ POST) ----
  const chooseFan = useCallback(
    (idx: number, rows: Array<Record<string, any>>, mfg: number, fanType: string | undefined) => {
      setChosenIndex(idx);
      // ★ HEADER MODE: the row pick persists `choosen_index` (js:3107 / 5330
      // saveInStorage) so a reopen restores the SAME chosen row.
      if (headerMode) saveInStorage({ choosenIndex: idx });
      const row = rows[idx];
      if (!row) return;
      // per-row curve cache (legacy rated_fans[idx].choosen).
      if (row.__curve) {
        setChosenCurve(row.__curve as FanChosenCurve);
        return;
      }
      if (CURVE_VENDOR_MFGS.has(mfg)) {
        // fetch the per-fan curve (CODE-ONLY, the SECOND /rating/ POST).
        void getFanCurve(idx, rows, mfg, fanType);
      } else {
        // non-curve vendors render the inline curve from the row directly.
        setChosenCurve({
          rpm_curve: row.rpm_curve ?? null,
          bhp_curve: row.bhp_curve ?? null,
          system_curve: row.system_curve ?? null,
          airflow_actual: Number(row.airflow_actual) || 0,
          bhp_total: Number(row.bhp_total) || 0,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * ★ The rated-outputs Wide x High pick — one fan model, which array to use.
   *
   * The handler rated this model against several Qty Wide x High arrays and
   * sent a full rated payload for each (see the `__wh_rows` note in runRate),
   * because every number in the row depends on how many fans share the duty:
   * bhp, speed, the curve, the sound powers, the fan-array weight and the
   * REQUIRED AW W x H. So the pick SWAPS THE ROW rather than editing two
   * fields — otherwise Select would persist one array's identity next to
   * another array's performance.
   *
   * The qty itself is deliberately NOT written back into the Fan tab's Qty
   * Wide / Qty High selects: those stay on `--` so a re-rate (after a tweak to
   * TSP, say) still explores the whole grid instead of silently narrowing to
   * whatever was picked last. `buildFanSelectPayload` reads the array off the
   * chosen ROW for exactly this reason.
   *
   * Picking an array changes both of the list's primary keys — the row's
   * REQUIRED AW verdict can flip and its array size certainly changes — and the
   * list is deliberately NOT re-grouped here (`orderRatedRowsFitFirst` runs on a rate
   * and on a column sort only): the row would jump out from under the select
   * the user just used, away from the model they were reading. The pick and its
   * REQUIRED AW go red in place instead, and the next sort click re-groups.
   */
  const chooseWideHigh = useCallback(
    (idx: number, value: string, mfg: number, fanType: string | undefined) => {
      const rows = ratedRowsRef.current;
      const current = rows?.[idx];
      const variant = (current?.__wh_rows as Record<string, any> | undefined)?.[value];
      if (!rows || !current || !variant) return;
      const next = [...rows];
      next[idx] = {
        ...variant,
        // the column's own state has to survive the swap — the variants and the
        // option list live on the row, not in a hook.
        wide_high_options: current.wide_high_options,
        wide_high_selected: value,
        __wh_rows: current.__wh_rows,
      };
      setRatedRows(next);
      ratedRowsRef.current = next;
      // Re-choose so the curve, the motor list and the VFD/isolation tabs all
      // follow the array that is now on screen. Unconditional: the click that
      // OPENED the list bubbled to the row's own `click → chooseFan`, so this
      // row is already the chosen one, and re-choosing it with the swapped rows
      // is what refreshes the curve. The OPTION's click is stopped before it
      // can reach that handler a second time — it would run with the rows from
      // before this swap and put the old array's curve straight back
      // (FanWideHighSelect, the option's `onClick`).
      chooseFan(idx, next, mfg, fanType);
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chooseFan, bump]
  );

  const getFanCurve = useCallback(
    async (idx: number, rows: Array<Record<string, any>>, mfg: number, fanType: string | undefined) => {
      const row = rows[idx];
      const data = collectData();
      const envelope = buildFanCurveEnvelope(mfg, fanType, data, row);
      // dev raises `self.loading` BEFORE this bail (js:4039 vs :4106), which
      // leaves its preloader up forever on an unresolved type. Raise it after,
      // so the only difference from dev is that we do not hang.
      if (!envelope) return;
      const seq = ++curveSeq.current;
      setCurveLoading(true);
      let body: RatingResponse;
      try {
        // A curve fetch, not a rating: keep it out of component_rated.
        body = await dispatchRating(envelope as any, { track: false });
      } finally {
        if (curveSeq.current === seq) setCurveLoading(false);
      }
      if (body.cancelled) return;
      let curve: FanChosenCurve;
      if (mfg == 23) {
        const cm = (body as any).data?.models?.[0] ?? {};
        curve = {
          rpm_curve: (body as any).data?.rpm_curve ?? cm.rpm_curve ?? null,
          system_curve: (body as any).data?.system_curve ?? cm.system_curve ?? null,
          bhp_curve: cm.bhp_curve ?? null,
          airflow_actual: Number(row.airflow_actual) || 0,
          bhp_total: Number(row.bhp_total) || 0,
          ...cm,
        };
      } else if (mfg == 67 && body.models) {
        const mm = (body.models as any)[0] ?? {};
        curve = {
          ...row,
          // ★ `MAX` IS the response's `BP` object, verbatim (js:4152-4155 and
          // :4190-4194 — `MAX: incoming_data.models[0].BP`). GET_CURVE returns
          // `{BP: {ID, CHART}, MAX: {…}, LABEL-X, …, system_curve}`: each
          // curve's points live INSIDE its own object, built as
          // `data[curve['ID']]['CHART']` (ziehl_abegg/
          // custom_business_logic_for_search_product.py:197-215). There is no
          // top-level `CHART` key, so the old `{CHART: mm.CHART, BP: mm.BP}`
          // wrapper made `MAX.CHART` undefined — `rpm_curve` was never derived,
          // drawFanCurves bailed at `if (!choosen.rpm_curve) return` having
          // painted only the grey background (the reported blank plot), and
          // Select persisted `sp: "null"`.
          MAX: mm.BP,
          system_curve: mm.system_curve ?? null,
          airflow_actual: Number(row.airflow_actual) || 0,
          bhp_total: Number(row.bhp_total) || 0,
        };
      } else {
        const mm = (body.models as any)?.[0] ?? {};
        curve = {
          ...row,
          ...mm,
          rpm_curve: mm.rpm_curve ?? row.rpm_curve ?? null,
          bhp_curve: mm.bhp_curve ?? row.bhp_curve ?? null,
          system_curve: mm.system_curve ?? row.system_curve ?? null,
          airflow_actual: Number(row.airflow_actual) || 0,
          bhp_total: Number(row.bhp_total) || 0,
        };
      }
      // Derive rpm_curve/bhp_curve HERE, not only in the canvas: legacy does it
      // inside drawCurves but mutates `self.choosen` in place, and that object
      // IS `rated_fans[idx].choosen` (js:4205) — so by the time selectFan reads
      // it for the curves PUT the derived arrays are on it. The React canvas
      // draws from a copy, so without this the ZA/comefri Select stored
      // `sp: "null"` / `bhp: "null"` and the curve was gone on reopen and in
      // the Fan Data report.
      curve = deriveVendorCurves(curve, mfg);
      // cache on the row (legacy rated_fans[idx].choosen).
      setRatedRows((prev) => {
        if (!prev) return prev;
        const copy = [...prev];
        if (copy[idx]) copy[idx] = { ...copy[idx], __curve: curve };
        return copy;
      });
      setChosenCurve(curve);
    },
    [collectData, dispatchRating]
  );

  // ---- ISP/ESP round-trip (CODE-ONLY, userArmed) ----
  const doIsp = useCallback(
    /** Resolves `true` once the POST has actually been issued — the open-time
     *  effect below latches on that, so a call that bailed because the
     *  air-stream list had not landed yet is retried when it does. */
    async (empty?: boolean): Promise<boolean> => {
      // ★ `get_isp_esp` opens with `if (self.header) return;` (js:7678) — the
      // ISP/ESP lookup is AHU-derived and there is no fan row to POST to. The
      // header-mode inputs keep the rendered 0/0 (see HEADER_FAN_DEFAULTS).
      if (headerMode || !modelId) return false;
      const s = state.current;
      const airStreamRow = fanCmsOptionList(fanDoc as FanCmsDoc, 'air_stream' as any);
      const air = (airStreamsQ.data ?? airStreamRow ?? []).find(
        (e: any) => String(e.id) == String(s.air_stream_id)
      );
      const airValue = (air as any)?.value;
      if (!airValue) return false;
      const data: Record<string, unknown> = {
        air_stream: airValue,
        air_stream_id: s.air_stream_id,
        // Selector-parity FAN restore — the two get_isp_esp drivers legacy sends
        // in the /isp body (fan.selector.js:8762-8763). discharge_id (Plenum
        // Discharge) and system_effect_id (System Effect) change the returned
        // ISP/ESP, which feeds the fan rating; the port dropped both.
        discharge_id: s.discharge_id,
        system_effect_id: s.system_effect_id,
        airflow_per_fan: Number(s.airflow_per_fan) || (Number(s.airflow_actual) / (Number(s.fan_qty) || 1)),
        fan_id: modelId,
        fan_air_stream: airValue,
        tsp: Number(s.tsp),
        airflow_actual: Number(s.airflow_actual),
      };
      if (!empty) data.master_fan_id = (detail as FanModel | undefined)?.master_fan_id ?? s.master_fan_id;
      const resp = await postFanIsp({ fanId: modelId, data }).unwrap().catch(() => null);
      if (resp) {
        state.current = {
          ...state.current,
          apd: resp.apd ?? '',
          isp: resp.isp ?? '',
          esp: resp.esp ?? '',
        };
        bump();
      }
      return true;
    },
    [fanDoc, airStreamsQ.data, detail, modelId, postFanIsp, bump, headerMode]
  );
  doIspRef.current = doIsp;

  /**
   * OPEN-TIME ISP/ESP pull. Internal Static Pressure, ESP Available and Fan
   * Pressure Loss are three READ-ONLY boxes fed only by
   * `POST /ahu/fans/<id>/isp` — nothing persists them on the fan row — so
   * without a call they render blank, which is what dev3 showed against dev's
   * 1.00 / 0.50.
   *
   * dev fires it from `render_outputs` (fan.selector.js:3011), and `render()`
   * ends with `if (self.choosen) self.render_outputs()` (js:2744-2746): a fan
   * that opens with a rated row on screen pulls them immediately. Legacy's
   * `empty` argument is false on that path, so `master_fan_id` IS sent — the
   * stored fan's own pressure drop. This port wired `doIsp` only to the two
   * `.get_isp_esp` SELECTS (Plenum Discharge / System Effect) and the
   * `refetch_fan_<section>_isp` bus event, so nothing called it on open.
   *
   * Latched per fan on the POST actually going out, not on the effect running:
   * `doIsp` bails until the air-stream list resolves, and that list is fetched.
   */
  const ispOpened = useRef<number | null>(null);
  useEffect(() => {
    if (headerMode || !modelId) return;
    if (seeded.current !== modelId) return;
    if (ispOpened.current === modelId) return;
    // `if (self.choosen)` — no rated row on screen, no render_outputs, no pull.
    if (!(detail as FanModel | undefined)?.master_fan_id) return;
    // latch optimistically so a re-render mid-flight cannot fire a second POST,
    // and release it again if the call bailed (no air-stream list yet).
    ispOpened.current = modelId;
    void doIsp().then((fired) => {
      if (!fired) ispOpened.current = null;
    });
  }, [detail, modelId, headerMode, doIsp]);

  // ---- SELECT (selectFan — CODE-ONLY, userArmed; dual-mode) ----
  // ★ UNREACHABLE IN HEADER MODE: `#select` is never rendered
  // (component_selector.tmpl:131-136) and the `:selector:select` subscription is
  // omitted above, so none of the persist seams below (__FORM_SHIM__, curve PUT,
  // VFD persist, detail-cache mirror, rating_mode:hide:<id>) can ever run there.
  const getFormShim = (): ComponentFormShim | undefined => window.__FORM_SHIM__?.[modelId];

  const readGenericMfg = (): boolean => {
    // Scoped to this selector's own chassis (see chassisEl) — an un-scoped
    // `#fan-selector` read would find the board dialog's select when both hosts
    // are mounted. Discovery §7.3 trap 1.
    const el =
      chassisEl()?.querySelector<HTMLSelectElement>('#fan-selector select[name="mfg_id"]') ?? null;
    const opt = el?.selectedOptions?.[0];
    const generic = opt?.dataset?.generic;
    return generic != null && generic !== 'none' && generic !== 'false';
  };

  // ---- master_fan_id resolution (P1-verify fix, 2026-07-09) ----
  // Legacy chooseFan (js:5295-5372) validates the chosen vendor row against
  // the KSDM fans catalog (cmsFanPages.getFirstModelDetailed on
  // cms/1/.../ahu/fans/ with the getModelFanParams-remapped filters); the
  // FIRST match's id IS the master_fan_id selectFan persists (js:5975-5979
  // via fanData). Curve-vendors (23/37/67/164) carry fan_id on the vendor
  // row instead. Without this the PATCH nulls master_fan_id — the fan ↔
  // MasterAHUFan linkage is destroyed and reopen renders blank (the seed
  // effect gates the stored-row render on master_fan_id).
  //
  // F4b: lifted out of doSelect so the Motor OUTPUT list can resolve the SAME
  // catalog fan on a rated-row choice, exactly as legacy does — chooseFan
  // resolves `fanData` first and only then queries the motor catalogue with
  // `fan_id: self.master_fan_id` (js:6423-6470). `warn` is doSelect-only so
  // the row-choose path cannot double-append the Annexair no-match warning.
  const resolveCatalogFan = useCallback(
    async (
      idx: number,
      choosen: Record<string, any>,
      opts?: { warn?: boolean }
    ): Promise<{ id: number | null; model: string | null; row: Record<string, any> | null }> => {
      const rows = ratedRows ?? [];
      const mfgNum = Number(state.current.mfg_id);
      const isCurveVendorMfg = CURVE_VENDOR_MFGS.has(mfgNum);
      // `choosen.fan_id` is only trustworthy as an already-resolved KSDM
      // master_fan_id for the curve vendors (comment above). The Annexair
      // output_processor also stamps a `fan_id` on every row (aliased from
      // `annexair_fan_id`, output_processor.py's `_normalize_model`), but
      // that's the VENDOR's own (UUID) fan identifier, not a KSDM catalog id
      // — reading it here as fanDataId short-circuits the catalog-match block
      // below (the mfgNum==237 branch that resolves the real numeric id via
      // fan_type) and leaks the UUID into `fan_id=<uuid>` on the /motors/all/
      // and isolator PATCH calls, which expect a numeric id and 500.
      let fanDataId: number | null =
        (((isCurveVendorMfg ? choosen.fan_id : null) ?? (rows[idx] as any)?.master_fan_id) as
          | number
          | undefined) ?? null;
      // the resolved catalog fan's model string — the serializer's fan_model
      // (master_fan.model) for the detail-cache mirror in doSelect.
      let fanDataModel: string | null = null;
      // the matched KSDM catalog row itself — legacy keeps it as `self.fanData`
      // and reads model.get('type_id'/'arr_id'/…) off it for the motor filters
      // (js:6459-6470). Null on the curve-vendor path, where legacy uses
      // `self.fanData = self.choosen` (the vendor row) instead (js:6714).
      let fanDataRow: Record<string, any> | null = null;
      if (fanDataId != null || isCurveVendorMfg) {
        // ★ The catalog name is already on the rated row here, and doSelect's
        // detail-cache mirror needs it just as much as on the lookup path
        // below. These vendors' handlers resolve the KSDM catalog row
        // themselves and stamp BOTH its id and its name onto the model they
        // return — `model['fan_id'] = fan.id` / `model['model'] = fan.model`
        // (ziehl_abegg/handle_call.py:352-363, and the same pair in the
        // comefri / ebm-papst / rosenberg handlers) — so the row's `model` IS
        // what the serializer computes as `fan_model` for the id being
        // persisted.
        //
        // Leaving it null meant the mirror's `if (fanDataModel != null)` never
        // fired on this path, so the cached detail kept its PRE-select
        // `fan_model` — null on a fan rated for the first time — and a reopen
        // without a reload drew the stored row with an EMPTY Model cell
        // (buildSelectedRow reads `m.fan_model`), right again only after F5.
        // Reported by Anna on a Ziehl-Abegg selection, 2026-10-12.
        //
        // Read the ROW, not `choosen`: for mfg 23 the per-row curve cache that
        // doSelect passes in as `choosen` is built from the curve response
        // alone (getFanCurve) and carries no `model` key.
        const rowModel = (rows[idx] as any)?.model ?? (choosen as any)?.model ?? null;
        // Only alongside a resolved id — a model name mirrored next to a null
        // master_fan_id would name a catalog row that was never persisted.
        if (fanDataId != null && rowModel != null && rowModel !== '') {
          fanDataModel = String(rowModel);
        }
        return { id: fanDataId, model: fanDataModel, row: null };
      }
      const vendorRow = (rows[idx] ?? {}) as Record<string, any>;
      // getModelFanParams (js:5545-5620): map the chosen row's vendor values
      // back to CMS ids (amca_class by name; wheel/rotation/series by value).
      const byPath = (path: string, key: string, v: unknown): unknown => {
        if (v == null || v === '') return null;
        const row = fanCmsOptionListByPath(fanDoc as FanCmsDoc, path).find(
          (r: any) => String(r[key]) == String(v)
        );
        return row ? (row as any).id : null;
      };
      const qs = new URLSearchParams();
      const setIf = (k: string, v: unknown) => {
        if (v != null && v !== '') qs.set(k, String(v));
      };
      setIf('amca_class_id', byPath('fan.amca_classes', 'name', vendorRow.amca_class));
      setIf('wheel_size_id', byPath('fan.wheel.size', 'value', vendorRow.wheel_size));
      setIf('wheel_dia_nominal_id', byPath('fan.wheel.nominal_diameter', 'value', vendorRow.wheel_dia_nominal));
      setIf('wheel_dia_percent_id', byPath('fan.wheel.dia_percent', 'value', vendorRow.wheel_dia_percent));
      setIf('wheel_width_percent_id', byPath('fan.wheel.width_percent', 'value', vendorRow.wheel_width_percent));
      setIf('wheel_mtl_id', byPath('fan.wheel.material', 'value', vendorRow.wheel_mtl));
      setIf('series_id', byPath('fan.series', 'series', vendorRow.series));
      setIf('rotation_id', byPath('fan.rotation', 'value', vendorRow.rotation) ?? state.current.rotation_id);
      // the state overrides (js:5334-5347).
      setIf('mfg_id', state.current.mfg_id);
      setIf('type_id', state.current.type_id);
      setIf('arr_id', state.current.arr_id);
      if (mfgNum != 64 && mfgNum != 221 && Number(state.current.type_id) != 1) {
        setIf('housed_discharge_id', state.current.housed_discharge_id);
      }
      // NYB: the catalog spn filter — dev-sync H widened the chain to
      // spn || nomenclature || model (fan.selector.js:5945-5950 on merged dev).
      if (mfgNum == 209) {
        const spn = vendorRow.spn || vendorRow.nomenclature || vendorRow.model;
        if (spn != null) qs.set('spn', String(spn));
      }
      // Dev-sync H — ANNEXAIR (237): the KSDM catalog match is by fan_type
      // (the nomenclature FAMILY key — the variant key minus its trailing
      // "-N" suffix, createAnnexairCatalogFanPages js:5882-5900 on merged
      // dev), with the rating API's own KSDM-resolved wheel_dia_nominal_id
      // preferred over the inches-guessed remap above (js:5890-5899).
      if (mfgNum == 237) {
        const familyKey = annexairCatalogFamilyKey(vendorRow);
        qs.set(
          'fan_type',
          String(
            familyKey ||
              vendorRow.nomenclature ||
              vendorRow.fan_type ||
              vendorRow.spn ||
              vendorRow.model ||
              ''
          )
        );
        if (vendorRow.wheel_dia_nominal_id != null) {
          qs.set('wheel_dia_nominal_id', String(vendorRow.wheel_dia_nominal_id));
        }
      }
      try {
        const resp = await fetch(`/api/v1.0/cms/1/v1.0.0/ahu/fans/?${qs.toString()}&page=1`, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const list: unknown = resp.ok ? await resp.json() : null;
        const allRows: Array<Record<string, any>> = Array.isArray(list)
          ? (list as Array<Record<string, any>>)
          : (((list as any)?.results as Array<Record<string, any>>) ?? []);
        // ★ Narrow to the Options-tab Finish BEFORE picking a row. The query
        // above cannot tell two finish variants of one fan apart — the Annexair
        // "- 1" (Mill) and "- 1 - RA" (Rust Anode) rows of a fan_type differ in
        // nothing else (not even wheel geometry), and the endpoint applies no
        // ORDER BY, so `listRows[0]` (and the exact-variant `.find` below, which
        // matches both on `fan_type`) took whichever row Postgres happened to
        // return first — the RA row, on this data, which is how a Mill rating
        // ended up stored with the Rust Anode master_fan_id and finish_id 2.
        // The rating backend already resolved the right row by finish
        // (annexair handle_call `_pick_by_finish`); this is the same rule on
        // the way back (fan/finish.py::narrow_by_finish).
        // Narrowed on the SAME finish the rating itself filtered on — the one
        // the dialog is showing (shownFinishId), never one the request did not
        // carry, which would re-match against a finish nothing was rated for.
        const listRows = narrowCatalogRowsByFinish(allRows, shownFinishId());
        let first: Record<string, any> | null = listRows[0] ?? null;
        if (mfgNum == 237) {
          // findAnnexairCatalogFanMatch (js:5827-5844): prefer the EXACT
          // variant match (spn/fan_type/model == the normalized variant key);
          // legacy expands multi-match families into extra table rows for the
          // user to pick — the React table has no expansion, so the first
          // family row stands in when no exact variant matches (deviation:
          // page-1-only, first-row fallback — see the run report).
          const exact = findAnnexairCatalogRowMatch(vendorRow, listRows);
          if (exact) first = exact;
          if (!first && opts?.warn) {
            // setAnnexairMappingWarning (js:5747-5758) — the no-match text,
            // appended to the live warning list like legacy.
            setRateWarnings((prev) => [
              ...prev,
              'Rated Annexair fan found, but no matching KSDM catalog fan/SPN record was found.',
            ]);
          }
        }
        fanDataId = (first as any)?.id ?? null;
        // the cms fans LIST serializer exposes spn/mpn (no `model` key);
        // for the catalog these mirror MasterAHUFan.model.
        fanDataModel = (first as any)?.model ?? (first as any)?.spn ?? (first as any)?.mpn ?? null;
        fanDataRow = first;
      } catch {
        fanDataId = null;
      }
      return { id: fanDataId, model: fanDataModel, row: fanDataRow };
    },
    [ratedRows, fanDoc, shownFinishId]
  );

  const doSelect = useCallback(
    async (select = true) => {
      // isVfdValid gate (selectFan js:5871-5877): abort the Select and force
      // the VFD input tab open. Since 2026-10-05 the button itself is also
      // greyed on this predicate (the setSelectEnabled effect above). That
      // matches Backbone's checkButtonsAvailability (js:3648-3661) for #select
      // but NOT for #print, which Backbone greys on the same term and we
      // deliberately leave live — PHASE6_AHUBOARD_INVENTORY.md item 245 records
      // the deviation. So this is the backstop rather than the only check for
      // Select — it still fires for the Select
      // that arrives by bus event rather than by click (the
      // `:selector:select` subscription below, this file).
      if (select && !isVfdValidNow()) {
        setInputTab('vfd');
        bump();
        return;
      }
      userArmed.current = true;
      const shim = getFormShim();
      const rows = ratedRows ?? [];
      const idx = chosenIndex ?? 0;
      const choosen = (rows[idx]?.__curve as FanChosenCurve) ?? rows[idx] ?? chosenCurve ?? {};
      // ---- Path qualification ----
      // NO-OP path (stamp has_vfd if changed + persist the VFD + close):
      //   no rated rows at all, OR a saved fan (master_fan_id) WITHOUT an
      //   in-session Rate (sessionRated). Legacy gates the full apply on an
      //   in-session rate; ratedDatetime alone must NOT qualify it — it is
      //   seeded from the STORED rating_datetime on open (seed effect), so an
      //   untouched saved-fan Select was taking the full path (curves PUT with
      //   literal "null" strings + full model rewrite).
      // FULL-APPLY path (curves PUT + model rewrite + VFD persist):
      //   rated rows exist AND (the fan is unsaved OR a Rate ran this session).
      if (!rows.length || (detail as FanModel | undefined)?.master_fan_id && !sessionRated.current) {
        // Backbone's selectFan has NO early return on this flow — the
        // saved-fan/no-rerate Select still reaches the fan_params has_vfd
        // stamp (js:6225-6230, via dataForSet) and _persistVfdAccessory
        // (js:6271). Stamp only on an actual has_vfd change (a same-string
        // Backbone set() is a no-op save) and persist the VFD lifecycle.
        if (select) {
          // Arm the auto-save gate BEFORE the set: the Select click IS the
          // user gesture, but the gate normally flips only in the
          // rating_mode:hide:<id> handler (useComponentForm.ts:275), which
          // fires AFTER these writes — an unarmed set is hard-dropped
          // (autoSaveModel.ts:283) and the has_vfd stamp silently vanished on
          // the FIRST Select of a page session. Legacy has no such gate;
          // finishSelect arms the model moments later anyway, so this only
          // moves the arming ahead of the writes.
          shim?.model.markUserAction();
          const existing = parseParams((detail as FanModel | undefined)?.fan_params);
          // `existing.has_vfd == null` is the ABSENT-KEY case and it must stamp
          // too. Fans saved without the key exist — Kai Select writes
          // `fan_params = JSON.stringify(fan_model)` and never puts `has_vfd` in
          // it (kai-select/helpers/bot/designComponents/fan.ts:740-741) — and
          // the seed reads an absent key as DEFAULT_HAS_VFD, now `true`. So such
          // a fan reopens TICKED; if the user unticks it, the old
          // changed-only test compared `Boolean(undefined)` against the user's
          // `false`, found them equal, and silently dropped the untick. It then
          // reopened ticked again, and once the smartlist auto-picks its single
          // match (FanVfdTab.tsx:599-603) an untouched Select would put the
          // drive back. Writing the key whenever it is missing settles the fan's
          // value either way, and from then on the changed-only test is enough.
          if (existing.has_vfd == null || Boolean(existing.has_vfd) !== Boolean(state.current.has_vfd)) {
            const stamped = JSON.stringify({ ...existing, has_vfd: !!state.current.has_vfd });
            shim?.model.set({ fan_params: stamped });
            syncFanDetailCache(stamped); // same-session reopen seeds current truth
          }
          // …and the Season / EADB / EAWB block, which legacy saves on this
          // path too (:7465, outside its `self.selected` branch). It is the
          // only part of the dialog that is editable without re-rating, so
          // without this the edit had nowhere to go — see persistSectionDraft.
          persistSectionDraft();
          persistVfd();
        }
        finishSelect();
        return;
      }

      // ---- getModelFanParams overlay (P1b fix, 2026-07-09) ----
      // Legacy overlays EVERY chosen-row key onto model_data.model and REBUILDS
      // params_fan from the row via CMS value→id remaps (js:5027-5149, called
      // from selectFan at js:5922) BEFORE the write shape is built. Without it
      // the PATCH carried the fan's STALE pre-rate outputs (speed/bhp/sound
      // powers/wheel ids unchanged after Select — P1 verifier, fan 13351).
      const overlay = buildModelFanParams(
        state.current,
        choosen as Record<string, any>,
        fanDoc as FanCmsDoc
      );
      const modelData = {
        model: {
          ...(detail as Record<string, any>),
          si_params: parseParams((detail as FanModel | undefined)?.si_params),
          ...overlay.model,
        },
        // legacy RESETS params_fan={} and rebuilds (js:5090) — the stored
        // fan_params blob does NOT bleed in (buildFanSelectPayload restamps
        // mfg/series/type/wheel_mtl/notes/wheel_type on top, js:6018-6084).
        params_fan: overlay.paramsFan,
      };
      // scoped (see chassisEl) — legacy uses `self.$el.find(...)` here.
      const motorBaseVisible = !chassisEl()?.querySelector('.motor-menu.invisible-tabs');

      // ---- master_fan_id resolution (P1-verify fix, 2026-07-09) ----
      // The body lives in resolveCatalogFan above (F4b lifted it out so the
      // Motor OUTPUT list resolves the same catalog fan on a rated-row click).
      const { id: fanDataId, model: fanDataModel } = await resolveCatalogFan(
        idx,
        choosen as Record<string, any>,
        { warn: true }
      );

      const payload = buildFanSelectPayload(
        state.current,
        choosen as Record<string, any>,
        modelData,
        // F4b — legacy passes `self.motor_choosen` here (selectFan js:5915),
        // which is what stamps master_motor_id + rebuilds motor_params from
        // the picked catalogue row. The port used to hard-code `null` because
        // there was no motor-row pick flow; now that the Motor OUTPUT list
        // renders, the user's pick (or the auto-picked first row, which is
        // what legacy's render_outputs synthetic click produces — js:3336-3351)
        // is what gets persisted.
        motorPick?.row ?? null,
        {
          genericMfg: readGenericMfg(),
          ratingDatetime: ratedDatetime.current,
          fanDataId,
          finishId: shownFinishId(),
          motorBaseVisible,
          mtrSuppliedById: Number(state.current.mtr_supplied_by_id) || null,
          isolatorSaveData: null,
          ratedNotes: (rows[idx] as any)?.notes,
        }
      );

      // curves persisted (PUT ahu/fans/curves/<id>) BEFORE the row's curve keys
      // are stripped (legacy 4718-4741). Stamp shim #9 with the curves model.
      const curveData = {
        bhp: JSON.stringify((choosen as any).bhp_curve ?? null),
        cfm: '',
        sp: JSON.stringify((choosen as any).rpm_curve ?? null),
        system_curve: JSON.stringify((choosen as any).system_curve ?? null),
      };
      if (select) {
        await saveFanCurves({ fanId: modelId, data: curveData }).unwrap().catch(() => undefined);
        // ★ shim #9 — stamp the fan_rating_curves_model on the form view.
        if (shim) shim.fan_rating_curves_model = { id: modelId, ...curveData };
      }

      // Dev-sync J4+J6 — commitFanChain (fan.selector.js:7370-7379), run as
      // the /select POST's onCommit, now WITH the section season persist
      // (:7350-7376 — `persistSectionDraft`, shared with the no-op path).
      // ★ NOT replicated (survey §B6): the legacy DOUBLE PATCH —
      // `section.set(...)` (autosave PATCH #1) immediately followed by
      // `section.save(...)` (PATCH #2) at :7372/:7376.
      const commitFanChain = (resp?: CascadeResponse) => {
        // current_model.set(...) (auto-save) + isolatorSaveData (legacy 4869-4880).
        // Arm before the writes (see the no-op branch note) — an unarmed first
        // Select otherwise drops them silently (autoSaveModel.ts:283).
        shim?.model.markUserAction();
        shim?.model.set(payload.currentModelSet);
        if (payload.isolatorSet) shim?.model.set(payload.isolatorSet);
        shim?.model.set(payload.dataForSet);
        // Same-session reopen must seed has_vfd from current truth (the stamped
        // fan_params carries it on this path too — fanSelectorData.ts merge) AND
        // the chosen row's persisted performance columns — the seed effect reads
        // the RTKQ cache synchronously on remount and locks, so without this the
        // reopen stored row rendered the PRE-select numbers (stale speed/bhp/SE/
        // TE with the new model name). Skip undefined (key untouched by the
        // overlay) and NaN (JSON→null the serializer proved to ignore, e.g.
        // motor_power) so the draft mirrors what the PATCH actually persists.
        if (typeof payload.dataForSet.fan_params === 'string') {
          const cols: Record<string, unknown> = {};
          for (const k of DETAIL_CACHE_PERF_KEYS) {
            const v = (payload.currentModelSet as Record<string, unknown>)[k];
            if (v === undefined || (typeof v === 'number' && Number.isNaN(v))) continue;
            cols[k] = v;
          }
          // serializer-computed bhp_total_all_fans (= bhp × effective fan qty)
          // matches the vendor row's bhp_total — the stored-row Op. Pwr cell.
          const bhpTotal = (payload.currentModelSet as Record<string, unknown>).bhp_total;
          if (bhpTotal != null && !Number.isNaN(Number(bhpTotal))) {
            cols.bhp_total_all_fans = bhpTotal;
          }
          // serializer-computed fan_model = master_fan.model — when the catalog
          // resolution changed the master fan, the reopen row's Model cell must
          // not keep the OLD catalog name.
          if (fanDataModel != null) cols.fan_model = fanDataModel;
          // ---- the INPUT side of the same staleness -------------------------
          // The loop above only mirrors the OUTPUT columns, so every INPUT the
          // user typed in this dialog stayed on the PRE-select cache entry:
          // Airflow per fan/total and Total Static Pressure reopened BLANK on a
          // fan whose stored airflow_actual/tsp were still null (a first rate),
          // and reverted to the old numbers on a re-rate — correct again only
          // after F5, which is this defect's signature. Legacy has no cache: it
          // re-reads current_model on every open (fan.selector.js:1537-1573).
          // Mirror exactly SEED_MODEL_FIELDS — the set the seed effect reads
          // back — plus the three params blobs it parses, so the allow-list
          // cannot drift from the seed again. dataForSet wins where it carries
          // the key (it is the /select POST body and the later model.set);
          // otherwise currentModelSet, whose unchanged keys are the spread of
          // this very cache entry and so mirror back as no-ops.
          for (const k of [
            ...SEED_MODEL_FIELDS,
            'si_params', 'motor_params', 'motor_base_params',
          ]) {
            const from = k in payload.dataForSet ? payload.dataForSet : payload.currentModelSet;
            const v = (from as Record<string, unknown>)[k];
            if (v === undefined || (typeof v === 'number' && Number.isNaN(v))) continue;
            cols[k] = v;
          }
          // ---- REQUIRED AW W x H (min_aw_width/min_aw_height) ---------------
          // Neither loop above can carry these: they are NOT columns, the
          // AHUFan serializer COMPUTES them in to_representation from the
          // freshly persisted master_fan/master_motor/spacing geometry
          // (fan_serializers.py:226-244), so the client has no value to mirror.
          // Legacy has one anyway — Backbone applies the save response to
          // current_model, and setOutputs reads `current_model.get(
          // "min_aw_width")` on the next open (fan.selector.js:3921-3926). The
          // port's cache entry kept the PRE-select representation instead, so
          // reopening a fan rated+selected in this page session rendered the
          // REQUIRED AW cell as bare loading-dots (min_aw_* null on a fan that
          // had no master_fan_id when the entry was first fetched).
          // `/select` returns the same serializer under `updated`
          // (fan_viewset.py:566-568) — that IS the response legacy applies.
          // `housed_discharge_id` rides the same path for the same reason: it
          // is the SELECTED catalogue row's housing discharge, derived by the
          // serializer, so the only correct value after a Select is the one the
          // server computed from the row that was just persisted — and the
          // SEED_MODEL_FIELDS loop above can only mirror what the client sent.
          // Without it a same-session reopen seeds no discharge and the Position
          // list goes back to offering all four positions.
          // `motor_position_id` for the mirror-image reason: `AHUFan.save()`
          // SNAPS an impossible position to one the selected catalogue row can
          // be built in (sync_dwdi_motor_position), so after a Select the
          // client's own value can be the one the server just replaced — and
          // the unit is drawn with the server's. Mirroring the pre-snap value
          // made the reopened dialog disagree with the Model View until F5.
          const updated = resp?.updated as Record<string, unknown> | undefined;
          for (const k of [
            'min_aw_width', 'min_aw_height', 'housed_discharge_id', 'motor_position_id',
          ] as const) {
            if (updated && k in updated) cols[k] = updated[k];
          }
          syncFanDetailCache(payload.dataForSet.fan_params, cols);
        }
        shim?.render();
        // J6 — the section persist (legacy `section.set` + `section.save`
        // inside commitFanChain :7371-7376 — Select path; one PATCH here).
        if (select) persistSectionDraft();
        // _persistVfdAccessory (js:6268-6273) — Select path only, not awaited.
        if (select) persistVfd();
      };
      if (select) {
        // Dev-sync J4 — the Checkpoint-4 switch (fan.selector.js:7381-7388):
        // POST fans/<id>/select with dataForSet (★ the section air
        // conditions are NOT in the payload, unlike coil); commitFanChain as
        // onCommit; no autoRefresh flag (a non-empty cascade re-reads the
        // rows it changed — cascadeRefresh.ts); no onError
        // (legacy passes none). The dialog-close (finishSelect below) fires
        // after issuing the POST, like legacy :7390-7393.
        void guardSelect({
          url: `/api/v1.0/ahu/fans/${modelId}/select`,
          analyticsComponent: 'fan',
          payload: payload.dataForSet as Record<string, unknown>,
          onCommit: commitFanChain,
        });
      } else {
        commitFanChain();
      }
      finishSelect();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ratedRows, chosenIndex, chosenCurve, detail, modelId, fanDoc, saveFanCurves, persistVfd, persistSectionDraft, isVfdValidNow, syncFanDetailCache, dispatch, activate, resolveCatalogFan, motorPick, shownFinishId]
  );

  /* ===================================================================== *
   * F4b — the Motor OUTPUT result list (legacy `self.motorModels`).
   *
   * Reported as "the Motor tab in the Fan Rating Dialog is empty": dev fills
   * that tab with the motor catalogue rows that match the chosen rated fan
   * (fan.selector.output.tmpl:539 `{{#each motorModels}}`); the port only ever
   * rendered the `{{#unless motorModels.length}}` fallback — the single SAVED
   * motor row — so a fan with no `master_motor_id` (every header-mode fan, and
   * any board fan that has never been Selected) showed nothing at all.
   *
   * Legacy loads the list inside chooseFan, AFTER the KSDM catalog fan is
   * resolved, because `fan_id` is one of the motor filters (js:6444-6630 for
   * catalog vendors, js:6713-6910 for the curve vendors). The request is
   *   GET /api/v1.0/cms/1/v1.0.0/ahu/motors/all/?page=N&<filters>
   * built by CMSPages: `allowed_fields` whitelists the keys and
   * null/''/'None' are dropped (results_filter.js:26-66). `expandByVoltage`
   * needs no equivalent — it only stops Backbone collapsing the server's
   * per-voltage rows by shared `id`, which a plain fetch never does.
   *
   * Gated on `sessionRated` to match legacy: on a plain reopen of a saved fan
   * chooseFan never runs (the restored row carries no `data-id`, js:6227), so
   * dev shows the saved-motor row there and only lists after a real Rate.
   * ===================================================================== */
  const buildMotorQuery = useCallback(
    (masterFanId: number, catalogRow: Record<string, any> | null, choosen: Record<string, any>) => {
      const st = state.current;
      const mfgNum = Number(st.mfg_id);
      const isCurveVendor = CURVE_VENDOR_MFGS.has(mfgNum);
      // Annexair (237) doesn't go through the generic catalog-vendor filter
      // rules below -- legacy resolves its motor list through a dedicated
      // getAnnexairMotorLookupConfig (js:6132-6173), which always filters on
      // efficiency_id and never on enclosure_id (no enclosure_id key in its
      // filteringFields at all). Without this branch the generic rules
      // (efficiency_id dropped, enclosure_id kept) over/under-filter the
      // /motors/all/ query and it comes back empty -- the Motor output tab
      // then has nothing to show for an Annexair fan.
      const isAnnexair = mfgNum == 237;
      // legacy `self.motor_rate_data` is the RAW select values (the *_id), not
      // the rate-envelope codes — getMotorForRateData js:6195-6216 deliberately
      // keeps `elem.val()` and comments the `data-value` read out.
      const pairs: Array<[string, unknown]> = [
        ['mfg_id', st.motor_mfg_id],
        ['series_id', st.motor_series_id],
        // efficiency_id is commented out of the catalog-vendor filter set
        // (js:6455) and present for 64/221, every curve vendor, and Annexair
        // (js:6137, unconditional).
        ['efficiency_id', isAnnexair || isCurveVendor || mfgNum == 64 || mfgNum == 221 ? st.motor_efficiency_id : null],
        // …and enclosure_id is the mirror image: dropped ONLY for the 37/67/164
        // curve vendors (js:6784) and Annexair (js:6135-6152 has no
        // enclosure_id key).
        ['enclosure_id', isAnnexair || (isCurveVendor && mfgNum != 23) ? null : st.motor_enclosure_id],
        ['housing_id', st.motor_housing_id],
        ['speed_nominal_id', st.motor_speed_nominal_id],
        ['type_id', st.motor_type_id],
        ['shaft_grounding_id', st.motor_shaft_grounding_id],
        ['fan_id', masterFanId],
      ];
      // the fan-side filters read the resolved KSDM catalog row on the catalog
      // path (js:6459-6470) and the vendor row's own KSDM ids on the curve path
      // (js:6740-6753), both falling back to live state.
      const fanSrc = (k: string, curveKey: string, stateKey: string) =>
        (isCurveVendor ? choosen[curveKey] : catalogRow?.[k]) || st[stateKey];
      pairs.push(
        ['fan_type_id', fanSrc('type_id', 'fan_type_id', 'type_id')],
        ['fan_arr_id', fanSrc('arr_id', 'fan_arr_id', 'arr_id')],
        ['fan_mfg_id', fanSrc('mfg_id', 'fan_mfg_id', 'mfg_id')],
        ['fan_series_id', fanSrc('series_id', 'fan_series_id', 'series_id')],
        ['fan_wheel_dia_nominal_id', fanSrc('wheel_dia_nominal_id', 'fan_wheel_dia_nominal_id', 'wheel_dia_nominal_id')],
        ['fan_amca_class_id', fanSrc('amca_class_id', 'fan_amca_class_id', 'amca_class_id')],
        // fan_bhp drives the server-side per-voltage expansion; it is commented
        // out for the 37/67/164 curve vendors only (js:6753 vs :6796).
        ['fan_bhp', isCurveVendor && mfgNum != 23 ? null : choosen.bhp],
        ['voltage_id', st.motor_voltage_id ?? (detail as any)?.motor_voltage_id],
        // `speed` is only ever read by filterFromRequest's arrangement-4 AC
        // branch (motors_viewset.py:786-805), which assumes a line-fed
        // fixed-speed motor: it treats MasterAHUMotor.speed_actual as the speed
        // the fan will really turn at and re-derates the duty by the fan law,
        // `power / (speed_actual/speed)**3 >= fan_bhp`. Annexair's Domel motors
        // are speed-controlled (the vendor returns the matching VFD with the
        // selection) and their `speed_actual` is the motor's MAX speed, so that
        // derate rejects the one motor the fan is linked to -- 5.1 HP on an
        // AZ 150-45 (speed_actual 4800) scores 0.08 HP against a 0.93 BHP duty
        // at 1183 rpm -- and the Motor output tab renders empty. Omitting the
        // key skips the derate (and its `speed <= 1.5 * speed_actual` overspeed
        // partner, which the vendor already enforces via speed_max); fan_bhp
        // still filters on the motor's own rated power.
        ['speed', isAnnexair ? null : choosen.speed]
      );
      // 64/221 additionally send rating + motor_power (js:6506-6535).
      if (mfgNum == 64 || mfgNum == 221) {
        pairs.push(['rating', true], ['motor_power', st.motor_power]);
      }
      const qs = new URLSearchParams();
      for (const [k, v] of pairs) {
        if (v == null || v === '' || v === 'None' || v === 'none') continue;
        qs.set(k, String(v));
      }
      return qs.toString();
    },
    [detail]
  );

  const loadMotorPage = useCallback(async (query: string, page: number) => {
    const url = `/api/v1.0/cms/1/v1.0.0/ahu/motors/all/?page=${page}&${query}`;
    const resp = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    // the viewset paginates bare, so an out-of-range page is a 404, not [].
    if (!resp.ok) return [] as Array<Record<string, any>>;
    const body: unknown = await resp.json();
    return (Array.isArray(body) ? body : ((body as any)?.results ?? [])) as Array<Record<string, any>>;
  }, []);

  useEffect(() => {
    const rows = ratedRows ?? [];
    const idx = chosenIndex ?? 0;
    const row = rows[idx];
    if (!row || !sessionRated.current) {
      setMotorModels(null);
      setMotorPick(null);
      setMotorTotal(null);
      motorQueryRef.current = null;
      masterFanIdRef.current = null;
      return;
    }
    let cancelled = false;
    const choosen = ((row as any).__curve ?? row ?? {}) as Record<string, any>;
    void (async () => {
      const fan = await resolveCatalogFan(idx, choosen);
      if (cancelled) return;
      // legacy `self.master_fan_id` (chooseFan js:6641) — the catalog fan the
      // isolator PATCH is keyed on, resolved here rather than a second time.
      masterFanIdRef.current = fan.id ?? null;
      if (fan.id == null) {
        setMotorModels(null);
        setMotorPick(null);
        return;
      }
      const query = buildMotorQuery(fan.id, fan.row, choosen);
      const [list, count] = await Promise.all([
        loadMotorPage(query, 1),
        // legacy's load_page fetches the count alongside page 1 and drives
        // check_scroll's has_available_pages off it (cms.components.pages.js
        // :152-156, :344).
        fetch(`/api/v1.0/cms/1/v1.0.0/ahu/motors/all/count/?${query}`, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((b) => (b?.count == null ? null : Number(b.count)))
          .catch(() => null),
      ]);
      if (cancelled) return;
      motorQueryRef.current = query;
      motorPageRef.current = 1;
      setMotorTotal(count);
      setMotorModels(list);
      // legacy render_outputs makes the first row the REAL selection when the
      // list loaded and nothing is picked yet — it synthesises the row click
      // (js:3336-3351), which is also what enables the VFD tab. The restored
      // `motor_choosen_index` wins when it still addresses a row
      // (`self.motor_choosen_index = self.motor_choosen_index || 0`, js:6640).
      const restored = motorChoosenIndex.current;
      const pickIdx = restored != null && list[restored] ? restored : 0;
      setMotorPick(list.length ? { index: pickIdx, row: list[pickIdx] } : null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratedRows, chosenIndex, resolveCatalogFan, buildMotorQuery, loadMotorPage, setMotorPick]);

  // ---- chooseIsolation's PATCH branch (fan.selector.js:7520-7570) ----------
  //
  // `AHUFan.isolators` is a SERVER-COMPUTED property: it walks
  // isolator_for_location_1..4, each of which resolves through `isolator()` off
  // the record's own master_fan / master_motor / si_params /
  // si_specified_deflection (models_component_fan.py:2043-2211). For a fan that
  // has been rated but not yet Selected none of those are persisted yet, so dev
  // WRITES the rating dialog's current choice to the fan and renders the
  // response — which is why its four Model rows are populated the moment the
  // Isolation output pane is opened, where the port left them blank until
  // Select. This is a write on a read action; it is dev's behaviour verbatim,
  // including the memo that keeps a re-open from re-PATCHing (js:7521-7534).
  //
  // The other two branches are pure reads and already render from `detail`:
  // fan_isolators_supplied_by_id == 2 (js:7513) and an already-Selected fan
  // (js:7580, `self.selected != null` → `selectedRow`).
  const [patchedIsolators, setPatchedIsolators] = useState<Record<string, any> | null>(null);
  const prevIsolatorSaveData = useRef<string | null>(null);
  const [patchFanIsolators, { isLoading: isolatorsPatching }] = usePatchFanIsolatorsMutation();
  useEffect(() => {
    const st = state.current;
    if (outputTab !== 'isolation') return;
    if (!modelId || headerMode) return;
    // branch 1 / branch 3 — both read the component record, no write.
    if (Number(st.fan_isolators_supplied_by_id) === 2) return;
    if (selectedRow) return;
    // legacy builds isolatorSaveData inside chooseFan, so it only exists for a
    // row chosen IN SESSION off a live rate.
    if (!sessionRated.current || chosenIndex == null) return;
    const masterFanId = masterFanIdRef.current;
    if (masterFanId == null) return;
    const row = (ratedRows ?? [])[chosenIndex] as Record<string, any> | undefined;
    // `self.motor_choosen ? … : masterMotorId` (js:6611-6616) — motor_choosen is
    // still null when the save data is built, so it is the first catalogue row,
    // falling back to the record's saved motor.
    const masterMotorId =
      motorModels?.[0]?.id ?? (detail as FanModel | undefined)?.master_motor_id ?? null;
    const data: Record<string, unknown> = {
      master_fan_id: Number(masterFanId),
      master_motor_id: masterMotorId == null ? null : Number(masterMotorId),
      si_specified_deflection_id:
        st.specified_deflection == null || st.specified_deflection === ''
          ? null
          : Number(st.specified_deflection),
      fmbase: Boolean(row?.fm_base),
      // legacy stringifies the three si_params selects (js:6621-6626); the
      // server json.loads() them back in `isolator()`.
      si_params: JSON.stringify({
        series_id: st.isolator_series_id ?? null,
        mfg_id: st.isolator_mfg_id ?? null,
        isolator_type_id: st.isolator_type_id ?? null,
      }),
    };
    const key = JSON.stringify(data);
    if (prevIsolatorSaveData.current === key) return;
    prevIsolatorSaveData.current = key;
    let cancelled = false;
    void patchFanIsolators({ fanId: modelId, data })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setPatchedIsolators((res?.isolators as Record<string, any>) ?? null);
      })
      .catch(() => {
        // legacy has no error branch here — the pane just stays as it was.
        if (!cancelled) prevIsolatorSaveData.current = null;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputTab, modelId, headerMode, selectedRow, chosenIndex, ratedRows, motorModels, version]);

  /** check_scroll (js:5728-5778) — infinite-scroll pagination of the list. */
  const onMotorListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const query = motorQueryRef.current;
      if (!query || motorLoadingRef.current || motorTotal == null) return;
      const loaded = motorModels?.length ?? 0;
      if (loaded >= motorTotal) return;
      if (el.scrollHeight - (el.scrollTop + el.clientHeight) >= 500) return;
      motorLoadingRef.current = true;
      const next = motorPageRef.current + 1;
      void loadMotorPage(query, next)
        .then((more) => {
          if (motorQueryRef.current !== query || !more.length) return;
          motorPageRef.current = next;
          setMotorModels((prev) => [...(prev ?? []), ...more]);
        })
        .finally(() => {
          motorLoadingRef.current = false;
        });
    },
    [motorModels, motorTotal, loadMotorPage]
  );

  const finishSelect = useCallback(() => {
    bus.emit('minRequiredLengthChange');
    bus.emit('rating_mode:hide');
    bus.emit(`rating_mode:hide:${modelId}`, { selected: true });
    window.setTimeout(() => bus.emit('check:fans:need_to_rate', 1000), 1000);
  }, [modelId]);

  // ---- combinations popup (available_combinations) ----
  const applyCombination = useCallback(
    (params: Record<string, unknown>) => {
      userArmed.current = true;
      state.current = { ...state.current, ...params };
      // params_fan:change_combinations → bust the TCF cache + re-rate.
      bus.emit('params_fan:change_combinations');
      setRatedRows(null);
      setCombinationsOpen(false);
      markChanged();
      bump();
    },
    [markChanged, bump]
  );

  // ---- chassis fan-out subscriptions (§30.4) ----
  useEffect(() => {
    const offs = [
      bus.on(`${eventName}_${scope}:selector:rate` as any, () => void doRate(0)),
      // ★ HEADER MODE omits `:selector:select` entirely. `.control-buttons`
      // (#select + #cancel) is wrapped in `{{#ifCompare show_tab '!=' 'all'}}`
      // (component_selector.tmpl:131-136), so the header chassis can never
      // dispatch it — legacy subscribes anyway (fan.selector.js:1517-1523) but
      // the handler is unreachable. Not subscribing makes header mode's
      // ZERO-WRITES property structural rather than incidental (discovery §3.1).
      ...(headerMode
        ? []
        : [bus.on(`${eventName}_${scope}:selector:select` as any, () => void doSelect(true))]),
      bus.on(`${eventName}_${scope}:selector:cancel` as any, () => abortRating()),
      bus.on(`${eventName}_${scope}:selector:reset` as any, () => doReset()),
      bus.on(`${eventName}_${scope}:selector:show_notes` as any, () => showFanNotes()),
      bus.on(`${eventName}_${scope}:selector:print` as any, () => doPrint()),
      bus.on('cancel:dialog:changes', () => abortRating()),
      bus.on('params_fan:change_combinations', () => setRatedRows(null)),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, scope, bus, headerMode, doRate, doSelect, abortRating]);

  useEffect(() => () => abortRating(), [abortRating]);

  // ---- reset (fan_<id>:selector:reset) ----
  /**
   * `reset` (fan.selector.js:8206-8311). Until now this was DEAD — the chassis
   * rendered no `#reset` for the fan tab, so nothing ever emitted the event and
   * the body was never exercised. It read as an 8-key wipe, which is not what
   * dev does: dev's literal SPREADS the current state and overrides ~70 named
   * keys, so a reset keeps the duty you typed (airflow, TSP, elevation, the
   * entering conditions, the assembly clearances) and clears the SELECTION —
   * the rated fan, the motor, the pricing overrides, the guards, the isolation
   * and the wheel search filters. Rebuilt to that shape before wiring the
   * button up, or the first press would have looked like it emptied the dialog.
   *
   * Three things dev expresses through Backbone blobs that our flat state has
   * to do by hand:
   *   - `fan_params: JSON.stringify({mfg_id})` — clears the blob-only members.
   *     Members that ALSO have a model column survive, because dev's
   *     `initialize(options, true)` re-seeds from the column it never nulled;
   *     that is exactly why airflow_actual and tsp come back.
   *   - `motor_params: ""` / `motor_base_params: null` / `si_params: null` —
   *     clear the motor, motor-base and isolator ids the seed prefixes.
   *   - `has_vfd` is recomputed by the re-initialize, whose literal is
   *     `has_vfd: <the default>` followed by a now-empty `...fanParams`.
   *
   * `self.stopListening()` + `self.initialize(options, true)` have no analogue:
   * our subscriptions are effect-scoped and the seed is keyed on the detail, so
   * the equivalent is resetting the refs/state the re-initialize would rebuild.
   * `$select.val(mfg).trigger('change')` (:8307) is `onParentChange('mfg_id')`,
   * the same substitution PR #1099 made for the other four dialogs.
   */
  const doReset = useCallback(() => {
    const mfg = state.current.mfg_id;
    abortRating(); // dev: self.rate_request?.abort() (:8216)
    const next: Record<string, unknown> = { ...state.current };

    // fan_params → {mfg_id}: drop the members with no column to restore them.
    for (const k of ALLOWED_FAN_PARAMS) {
      if (k !== 'mfg_id' && !SEED_MODEL_FIELD_SET.has(k)) delete next[k];
    }
    for (const k of FAN_RESET_BLOB_ONLY_MOTOR_KEYS) delete next[k];
    for (const k of FAN_RESET_MOTOR_BASE_KEYS) delete next[k];

    Object.assign(next, FAN_RESET_OVERRIDES);
    // ★ FAN_RESET_OVERRIDES is dev's literal, which resets the array to 1x1.
    // `--` is now the default for every manufacturer, and Reset means "back to
    // the defaults", so it lands there instead (fanQtyDefaultsFor).
    Object.assign(next, fanQtyDefaultsFor(mfg) ?? {});
    // ★ …and with the array gone, so is the per-fan duty derived from it. Reset
    // deliberately KEEPS the typed duty (airflow_actual, tsp), but
    // Airflow-per-fan is not typed duty when the array is `--`: it is
    // `total / array`, and there is no array any more.
    if (fanQtyPairIsUnset(next.qty_wide, next.qty_high)) next.airflow_per_fan = '';
    next.mfg_id = mfg; // dev re-selects the SAME manufacturer (:8207, :8307)
    next.has_vfd = DEFAULT_HAS_VFD;
    state.current = next;
    init.current = { ...next };

    setRatedRows(null);
    setSelectedRow(null);
    setChosenCurve(null);
    setChosenIndex(null);
    cascadeFired.current = false;
    setSelectEnabled(false);
    markChanged();
    bump();
    // re-cascade from mfg.
    if (mfg != null) graph.onParentChange('mfg_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, setSelectEnabled, markChanged, bump, abortRating]);

  const showFanNotes = useCallback(() => {
    const notes = (notesQ.data ?? []).map((n) => ({ note: n.note }));
    setOutputTab('notes');
    return notes;
  }, [notesQ.data]);

  // -------------------------------------------------------------------------
  // dev-sync F Stage 2 — "How to Rate a Fan" (fan.selector.js:6567-6589).
  // openHowTo is a TOGGLE: legacy early-returns into closeHowTo when the popup
  // already carries .active. Same shape as FilterSelector.tsx:1102-1106.
  // -------------------------------------------------------------------------
  const openHowTo = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHowToOpen((o) => !o);
  }, []);

  // fan.selector.js:4819-4825 — every mfg change re-toggles `.hide` on the menu
  // item and calls closeHowTo() when the user leaves 209 (New York Blower).
  // The `.hide` half is derived in the render below; this covers the close.
  // Legacy also closes it on destroy (:6557) — unmounting removes it here.
  // mfg_id lives in a ref (state.current) that bump()s the render, so this
  // effect intentionally has NO dep array: it re-checks after every render,
  // exactly like the legacy per-change handler. Setting false while already
  // false is a React no-op, so it cannot loop.
  useEffect(() => {
    if (Number(state.current.mfg_id) !== 209) setHowToOpen(false);
  });

  // ---- PRINT (fan.selector.js:8121-8204 print + :8313-8383 correctPrintStyles)
  // Legacy appends `<div class="print-content">`, renders `fan.selector.print
  // .tmpl` into it, draws the curve into that sheet's own `#curves`, sets the
  // title and calls window.print() 200 ms later. The sheet is the ONLY thing
  // that prints: common.css:22140 hides every other body child under
  // `@media print`, so printing the live dialog — which is what this used to do
  // — yields BLANK paper. <FanPrintSheet> is the port, mounted through a portal
  // while `printing` is true (see the render tail); same shape as the coil one.
  const [printing, setPrinting] = useState(false);
  const printTitle = useRef<string | null>(null);

  const doPrint = useCallback(() => {
    // The document title names the resulting PDF. Legacy:
    //   document.title = `${self.project?.get("name")}_${data.ahu_tag}_` +
    //                    `${data.section_tag ? `${data.section_tag}_` : ""}fan_selection`
    //   (fan.selector.js:8375, off `ahu_tag`/`section_tag` at :8155-8156)
    //
    // ★ The two reads this used to make are BOTH always-undefined on the board.
    // `activate.project` is never populated by the board's only
    // `rating_mode:activate` emitter (useComponentForm.openRatingStub sends
    // `{name, loading, current_model, section, section2}`) — the same hole the
    // Altitude field hit — and `activate.ahu` is not sent either. So the board
    // printed `undefined__<section>_fan_selection` where dev prints
    // `<project>_<ahu>_<section>_fan_selection`. Sourced now the way the rest of
    // this component already sources them: the project record fetched off
    // `ahu.project_id`, and the board AHU record the dialog passes as a prop.
    //
    // ★ HEADER MODE keeps the mirrored legacy quirk: there is no AHU and no
    // project in that host on EITHER app (`self.project` undefined, `ahu_tag`
    // '' via `self.ahu ? … : ""`), so the title stays literally
    // `undefined__fan_selection` — tracked, deliberate, unchanged here.
    const project =
      (printProject.name as string | undefined) ??
      (activate.project as { name?: string } | undefined)?.name;
    const ahuTag =
      (ahu as { tag?: string } | null | undefined)?.tag ??
      (activate.ahu as { tag?: string } | undefined)?.tag ??
      '';
    const sectionTag = (activate.section as { tag?: string } | undefined)?.tag ?? '';
    printTitle.current = document.title;
    document.title = `${project}_${ahuTag}_${sectionTag ? `${sectionTag}_` : ''}fan_selection`;
    // mount the sheet; the effect below fires window.print() 200 ms later, the
    // same defer legacy uses to let the compiled template + canvas paint.
    setPrinting(true);
  }, [activate, ahu, printProject]);

  useEffect(() => {
    if (!printing) return undefined;
    // ★ `printSheetReady` is legacy's 200 ms defer PLUS a wait on the sheet's
    // images. The bare timer printed an empty logo box whenever `/media` — a
    // gunicorn route on dev/staging/prod, unlike dev3 — had not answered yet;
    // see printSheetReady.ts for the measured cause.
    let cancelled = false;
    void printSheetReady().then(() => {
      if (cancelled) return;
      window.print();
      // js:8378-8381 — restore the title and drop the sheet once print returns.
      if (printTitle.current != null) document.title = printTitle.current;
      printTitle.current = null;
      setPrinting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [printing]);

  // ---- ★ Phase 6.5 — HEADER-MODE pending restore + AUTO-RE-FIRE -------------
  // Legacy hangs this off the DependenciesHandler `finished` callback
  // (fan.selector.js:1240-1244): once the cascade settles it either re-fires the
  // rate (`needToRate`) or re-applies the manufacturer's saved state and rates
  // (`findFromStoraga(needToSearch, true)`). React has no `finished` hook, so the
  // cascade is settle-detected (isCascading false through one 250 ms tick). The
  // effect deliberately has NO dep array: it re-arms on every render, which is
  // how a still-settling cascade postpones itself.
  useEffect(() => {
    if (!headerMode) return undefined;
    const pending = pendingRestore.current;
    if (!pending) return undefined;
    if (!fanDoc) return undefined;
    if (!graphKicked.current) return undefined;
    if (graph.isCascading) return undefined;
    const t = window.setTimeout(() => {
      if (graph.isCascading) return; // still settling — a later render re-arms
      if (pending.applyState) {
        // findFromStoraga js:2346-2353 — apply the entry's state, minus
        // motor_power (legacy `delete model.state.motor_power`).
        const st: Record<string, unknown> = { ...(pending.entry.state ?? {}) };
        delete st.motor_power;
        state.current = { ...state.current, ...st };
        motorChoosenIndex.current = pending.entry.motor_choosen_index ?? null;
        // phase 2 (the rate) runs on the next pass so the requiredFilled memo
        // has re-derived from the freshly applied state.
        pendingRestore.current = { entry: pending.entry, applyState: false };
        bump();
        return;
      }
      pendingRestore.current = null;
      motorChoosenIndex.current = pending.entry.motor_choosen_index ?? null;
      if (pending.entry.rate) {
        autoRateRef.current = true;
        void doRate(pending.entry.choosen_index ?? 0);
      }
    }, 250);
    return () => window.clearTimeout(t);
  });

  // ---- render ----
  // seed completes once `detail` lands (the seed effect). The doc/loading flags
  // are NOT gated here: a background refetch (refetchOnMountOrArgChange) can keep
  // detailLoading true while the data is already present — gating on it left the
  // body stuck in the preloader on some AHUs. seeded.current is the real signal.
  if (seeded.current !== modelId) {
    return <div className="preloader dotted-preloader active" />;
  }
  void detailLoading; void fanDocLoading;

  const s = state.current;
  const mfg = Number(s.mfg_id);
  // Annexair (237) ships a fan as one catalogued assembly — wheel, arrangement
  // and motor are properties of the model the vendor API returns, not things the
  // user chooses — so the Fan/Motor inputs that only describe a built-to-order
  // fan are not rendered for it (Anna, 2026-10-07). This HIDES the rows only:
  // the values stay in `state`, so the rating envelope and the motors lookup
  // (which does read motor_efficiency_id for 237, see the isAnnexair branch in
  // the motors-URL builder) are byte-for-byte what they were.
  const isAnnexair = mfg === 237;
  const fanType = fanTypeValue(fanDoc as FanCmsDoc, s.type_id as any);
  const streams = (airStreamsQ.data as OptionRow[] | undefined) ?? [];

  // ---- Selector-parity FAN restore: render-scope helpers -------------------
  const typeId = Number(s.type_id);
  const mountingId = Number(s.mounting_id);
  const orientationId = Number(s.orientation_id);
  // Qty Wide / Qty High option ranges — 1..max from the fan-type mfg settings
  // (updateQty, js:3837-3849). Until the settings land, the range still has to
  // contain the current value or the select would render blank.
  const qtyRange = (max: unknown, current: unknown) => {
    const n = Math.max(Number(max) || 0, Number(current) || 0, 1);
    return Array.from({ length: n }, (_, i) => i + 1);
  };
  const qtySettings = qtySettingsQ.data as Record<string, unknown> | null | undefined;
  // ★ `--` on Qty Wide x High (FAN_QTY_UNSET_MFGS — every manufacturer, and
  // the default each one opens on):
  // a leading blank option meaning "rate every array up to the CMS maximum".
  // The option must be a real `<option value="">` — a `<select>` whose value
  // matches no option renders the FIRST one, so leaving it out would show
  // "1 x 1" for an unset qty rather than a blank (the #1791 trap). The 1..max
  // range itself is unchanged: it is still CMS's Max Qty Wide x High, which is
  // also the grid the backend expands over.
  const qtyAllowsUnset = FAN_QTY_UNSET_MFGS.has(mfg);
  // ★ ONE select over the COMBINATIONS (Anna, 2026-10-08) — `2 x 3` picks
  // wide 2 + high 3 in one go (FAN_QTY_PAIR_FIELD). The cross product of the two
  // former ranges, wide-major, so the same pairs are offered as before and no
  // combination the two selects allowed is lost. `qtyRange` still folds the
  // CURRENT value into its axis, which is what keeps a saved 8x2 renderable
  // while the settings query is in flight or after CMS's maximum shrinks.
  const qtyPairOptions = (() => {
    const wides = qtyRange(qtySettings?.max_qty_wide, s.qty_wide);
    const highs = qtyRange(qtySettings?.max_qty_high, s.qty_high);
    const opts = wides.flatMap((w) =>
      highs.map((h) => ({ value: `${w}x${h}`, label: `${w} x ${h}` })));
    return qtyAllowsUnset ? [{ value: FAN_QTY_UNSET, label: '--' }, ...opts] : opts;
  })();
  // Available Airway W x H — the SECTION's airway, formatted like the legacy
  // `get_one_decimal` helper (toFixed(2), blank for 0/null).
  const sectionAw = (i: 0 | 1) => {
    const sec = activate.section as { aw_width?: unknown; aw_height?: unknown } | undefined;
    const n = Number(i === 0 ? sec?.aw_width : sec?.aw_height);
    return n ? n.toFixed(2) : '';
  };
  // A get_isp_esp select (Plenum Discharge / System Effect): stage the value like
  // any search field, then re-pull ISP/ESP (legacy "change .get_isp_esp", js:582).
  const onGetIspField = (name: string, raw: string) => {
    onSearchField(name, raw);
    void doIsp(true);
  };
  // The "motor base" sub-tab shows only when mtr_base_series_id has options
  // (legacy toggles `.motor-menu.invisible-tabs` off iff that list is non-empty,
  // js:2013-2023) — which is exactly the `motorBaseVisible` save gate (js:7183).
  const mtrBaseSeriesIds = graph.optionIds('mtr_base_series_id');
  const motorBaseApplicable = !!(mtrBaseSeriesIds && mtrBaseSeriesIds.length);
  const motorSubTabActive = motorBaseApplicable ? motorSubTab : 'motor';
  // Altitude override: the live edit, else the project's stored elevation
  // (fan.selector.tmpl:601 value="{{project.elevation}}"). Read-only in the
  // header/sizer "default rate" host (legacy `{{#if defaultRate}}readonly`).
  const elevationDisplay =
    s.elevation != null && s.elevation !== ''
      ? String(s.elevation)
      : projectElevation != null
        ? String(projectElevation)
        : '';

  // which mfgs the user may pick (the available_rating gate — networkAccess).
  // The controller rows carry a `route` like 'rating.fan.rosenberg' — extract
  // the trailing manufacturer token (legacy results_available_rating_array).
  const allowedMfgIds = (() => {
    if (!networkAccess || !networkAccess.length) return null;
    const allowed = new Set<number>();
    const routes = new Set<string>();
    for (const r of networkAccess as any[]) {
      const route = String(r.route ?? r.name ?? r.manufacturer ?? r);
      const m = route.match(/^rating\.fan\.(.+)$/);
      if (m) routes.add(m[1]);
      else routes.add(route);
    }
    for (const [id, name] of Object.entries(FAN_MFG_MAPPING)) {
      if (routes.has(name)) allowed.add(Number(id));
    }
    // the saved mfg always survives the gate (read-only render of a stored fan).
    if (s.mfg_id != null) allowed.add(Number(s.mfg_id));
    return allowed;
  })();

  /** is a doc-resolved field's narrowed option list non-empty? (drives the
   * AMCA row's presence and the Options tab's nav gate). */
  const docFieldHasOptions = (name: string) => {
    const ids = graph.optionIds(name);
    const rows = ids
      ? fanCmsOptionList(fanDoc as FanCmsDoc, name, ids)
      : fanCmsOptionList(fanDoc as FanCmsDoc, name, null);
    return rows.length > 0;
  };

  /** dev stamps `.required` on the LABEL SPAN of every field in the current
   * manufacturer's REQUIRED_FIELDS set, and `.required:after { content:"*" }`
   * draws the asterisk (checkAndUpdateRequiredFields js:7728-7752 +
   * toggleSpanAsterisks :7712). `mtr_safety` is enforced but never marked
   * (REQUIRED_FIELDS_WITHOUT_ASTERISK, js:7723), and `airflow_per_fan` joined
   * it 2026-10-08 — see FAN_REQUIRED_FIELDS_WITHOUT_ASTERISK. The port had the
   * same field sets driving the RATE gate but never rendered the marks. */
  const reqCls = (name: string) =>
    !FAN_REQUIRED_FIELDS_WITHOUT_ASTERISK.has(name) && FAN_REQUIRED_FIELDS[mfg]?.has(name)
      ? 'required'
      : undefined;

  // AMCA Class: legacy REMOVES the row when the select ends up with no real
  // option (js:2885-2897) rather than keying it on the fan type. Before the
  // field's params call has landed it follows the template's initial state —
  // shown only for Annexair (mfg 47, tmpl:248).
  const amcaLoaded = graph.optionIds('amca_class_id') != null;
  const amcaHasOptions = amcaLoaded ? docFieldHasOptions('amca_class_id') : mfg === 47;
  // Options nav item: non-clickable while every select on that pane is
  // (renderSelects js:8796-8798 — `#ahu-Options select:not(.non-clickable)`).
  //
  // ★ THAT GATE IS PREFIX-SCOPED, AND IN HEADER MODE THE PREFIX IS EMPTY.
  // The template ids/classes are `{{prefix}}Options` with
  // `prefix: self.header ? "" : "ahu-"` (js:2619/:3070), so in the header
  // Independent-Rating popup the pane is `#Options` and the nav item is
  // `li.Options`. Both selectors above then match NOTHING — `.length` is 0, so
  // the `addClass` fires on an empty set — and the li keeps the template's
  // clickable state for the whole lifetime of the popup. dev therefore always
  // offers Options there, which is what QA compared against; the port evaluates
  // the gate on every render regardless of host and greyed it out.
  //
  // The five master lists really ARE empty (New York Blower / FA returns `[]`
  // from all five `fan_mfg_series/*` endpoints, and the backing tables hold the
  // same rows on dev and dev3 — checked over SSM), so this is the gate's scope,
  // not the data. The per-select `.non-clickable` marks come from
  // `checkSelectboxOptions`, whose selectors are NOT prefixed, so they still
  // apply in header mode — matching dev, which shows the same greyed controls
  // on a reachable pane.
  // ★ THE GATE IS RESTORED, over ALL SIX rows and in BOTH hosts (Anna,
  // 2026-10-03: "if there is no data for any of the selects in the Options tab,
  // you should disable the entire tab"). dev counts five — it never knew about
  // Finish, whose options come from `fan_mfg_series/finish_id/` — and greying
  // on the five alone would make the Finish pick unreachable for a vendor with
  // no guard/AFS data but a finish of its own. It is computed below, next to
  // `masterHasOptions`, off the same FAN_OPTIONS_ROWS list the pane renders.

  const masterHasOptions = (name: string) =>
    fanMasterOptionList(name, masterLists[name] as any[] | undefined).length > 0;

  /** what the module-scope select controls render from — see `FanFieldCtx`.
   *  Rebuilt every render, so they see the current state and handlers. */
  const fieldCtx: FanFieldCtxValue = {
    s,
    fanDoc,
    masterLists,
    masterFullLists,
    detail,
    allowedMfgIds,
    optionIds: graph.optionIds,
    docFieldHasOptions,
    onSearchField,
  };

  /** The Options rows this Mfg/Series actually offers. Empty ⇒ the pane has
   * nothing in it, and the nav item is greyed rather than opening a blank pane
   * (see the gate's note above). */
  const optionsRows = FAN_OPTIONS_ROWS.filter(([name]) => masterHasOptions(name));
  const optionsTabEnabled = optionsRows.length > 0;

  /** A PLUG fan has no spring isolators, so the Isolation pane is greyed off
   * the same way the Options one is.
   *
   * All six inputs on that pane resolve from `MasterAHUFanIsoMfgSeriesSuppliedBy`
   * (Type / Supplied by / Manufacturer / Series / Desired Deflection) and
   * `MasterAHUFanMfgSeriesIsoMounting` (Mounting), both keyed on the fan's
   * mfg + series. NO row in either table is a plug fan — every row is Plenum,
   * Cube or DWDI — so the pane is unconditionally blank for a plug fan and the
   * tab only ever opened onto six empty selects. Keyed on the fan TYPE rather
   * than the manufacturer because that is what the data says: the vendors who
   * happen to be blank today (Annexair, Rosenberg, ebm-papst, Ziehl-Abegg) are
   * exactly the ones whose whole catalogue is plug. */
  const isolationTabEnabled = fanType !== 'plug';

  // NOTE: the generic DocSelect / MasterSelect / StreamSelect / Num wrappers
  // are gone. Every input tab now renders dev's own row shape (FanRow /
  // MotorRow + a bare control), because the generic `.row > .col-xs-5.label-part
  // + .col-xs-7.input-part` shape matched none of the sizing rules in
  // common.css and left every control shrink-to-fit.

  const tabClass = (t: InputTab) => `tab-pane fade scrollable-content${inputTab === t ? ' in active' : ''}`;

  // ===== the rated-outputs table (fan.selector.output.tmpl:219-310) ==========
  // ★ LEGACY-CSS-SELECTOR MISS, the same class as the energy-wheel wrapper fix.
  // This table used to render through the generic <RatedOutputsTable> kit, whose
  // DOM is `.rated-sortable-table > .thead-part|.tbody-part > .row > .col-xs-N`.
  // Not one of those class names is selected by common.css for the fan pane —
  // every `.thead-part` / `.tbody-part` rule in the sheet is scoped to
  // `#rating_mode_uvc_content` — so the whole of dev's results-table stylesheet
  // (common.css:18381-18500 `.rate-list-inline`, and the `.xlongplus/.lmedium/
  // .long/.sshort` width classes at :18463-18495) was a no-op here. The columns
  // fell back to Bootstrap twelfths — and the column set adds up to THIRTEEN,
  // so the last one wrapped — with no sticky header, no 39px row box, no
  // hover/`.choosen` row background, and no `.fan_rate-list-inline` scroll
  // container. That is the reported "it scrolls / the structure doesn't match".
  //
  // The fix is dev's markup verbatim: a `.row.rate-list-inline.fan_rate-list-
  // inline` host, a sticky `.rate-list-header.rate-list-heading` whose cells
  // carry the legacy width classes, and a `.rate-list-body` of
  // `.rate-list-rows.rated-body.fan_rated_item` rows. No CSS is added.
  //
  // Header and body cells take DIFFERENT class lists in the template, so each
  // column carries both.
  const motorTypeId = Number(s.motor_type_id);
  const motorIsKw = motorTypeId == 2;
  // legacy render context (js:3141-3144) — the three EC/DC-motor vendors whose
  // SE/TE cells take the substitution branches (tmpl:302 / :305).
  const fanType376764 = mfg == 67 || mfg == 37 || mfg == 164;
  // `multiply` (ahu/libs/handlebars/helpers.js:141-148): null in → " " out,
  // otherwise the product to 2dp.
  const hbMultiply = (...args: unknown[]): string => {
    if (args.some((a) => a === null || a === undefined)) return ' ';
    return args.reduce<number>((acc, a) => acc * Number(a), 1).toFixed(2);
  };
  const allRatedColumns: FanRateColumn[] = [
    {
      sort: 'model',
      head: 'model',
      headClass: 'xlongplus left sort-icon-wrapper',
      headStyle: { paddingLeft: 15 },
      cellClass: 'xlongplus number-col params_fan',
      cellName: 'model',
      cellStyle: { paddingLeft: 15, textAlign: 'left' },
      // tmpl:262 reads `model` on a RATED row (`fan_model` is the SELECTED
      // row's key, tmpl:96) — the fallback keeps both shapes rendering.
      render: (r) => <span>{r.model ?? r.fan_model ?? ''}</span>,
    },
    // ★ QTY WIDE x HIGH — immediately after Model, and ONLY for the vendors whose
    // Qty Wide x High selects offer `--` (FAN_QTY_UNSET_MFGS; all of them as of
    // 2026-10-08). The handler rates one fan model against several arrays and reports
    // which ones it is valid for, so this cell is where the user says which of
    // them to carry into the selection — see `chooseWideHigh` for why picking
    // one swaps the whole row.
    //
    // Not sortable (no `data-sort`): there is nothing to order by, and a sort
    // click inside the cell would fight the select. Falls back to plain text
    // for a row with no option list — the STORED rated fan the dialog reopens
    // on is a single historical configuration, not a choice.
    ...(FAN_QTY_UNSET_MFGS.has(mfg)
      ? [{
          // "QTY WIDE x HIGH", matching the input row's own label (Anna,
          // 2026-10-08) — the column is the fan-array quantity, not a size, and
          // the bare "WIDE x HIGH" read as a dimension next to REQUIRED AW W x H.
          head: (
            // inline-block so `offsetWidth` is the width of the TEXT, not of
            // the 185px cell — see `measureQtyHead`.
            <span ref={measureQtyHead} style={{ display: 'inline-block' }}>
              Qty Wide<span style={{ textTransform: 'lowercase' }}> x </span>High
            </span>
          ),
          // `lmedium` (185px), not `long` (107px): the longer "QTY WIDE x HIGH"
          // heading clips at 107px — the cells never needed the room, the label
          // does. Both are existing legacy width classes (common.css:18476,
          // :18497), so no CSS is added.
          headClass: 'lmedium left number-col',
          cellClass: 'lmedium number-col fan_field',
          cellName: 'wide_high',
          render: (r: Record<string, any>, index: number) => {
            const options = (r.wide_high_options ?? []) as WideHighOption[];
            const selected =
              (r.wide_high_selected as string | undefined) ??
              (r.qty_wide != null && r.qty_high != null
                ? `${r.qty_wide}x${r.qty_high}`
                : '');
            // ★ No list ⇒ text, but the SAME colours (Anna, 2026-10-15): the
            // stored row a reopen rebuilds is one historical array, so there is
            // nothing to choose between — and its array can still be the one
            // that does not fit, which is why the text is coloured off the row's
            // own warning flags rather than left black. See `WideHighText`.
            if (!options.length || index < 0) {
              return <WideHighText row={r} />;
            }
            // ★ fit-first, and each number red on its own when THAT dimension
            // needs more airway than the section has — the same verdict the
            // REQUIRED AW cell to the right is already showing per dimension on
            // this row. The control is custom because an `<option>` cannot be
            // half one colour; see FanWideHighSelect.
            //
            // The right-align is the cell's own `text-align: right`
            // (`.number-col`) acting on an INLINE-BLOCK control — it only looked
            // left-aligned before because it filled the column. `margin-left:
            // auto` does not work here: auto margins do nothing on an
            // inline-block (nothing in the legacy sheet overrides them — the
            // computed margins are 0, which is what the control's own negative
            // ones rely on).
            return (
              <FanWideHighSelect
                options={options}
                value={selected}
                width={qtyHeadWidth}
                onChange={(next) => chooseWideHigh(index, next, mfg, fanType)}
              />
            );
          },
        } as FanRateColumn]
      : []),
    // ★ REQUIRED AW W x H sits BEFORE RPI (Anna, 2026-10-08), where dev has it
    // after (fan.selector.output.tmpl:264-270). It belongs next to QTY WIDE x
    // HIGH: the required airway is sized from the array layout
    // (calc_required_aw), so picking a different Qty Wide x High rewrites these
    // two numbers — the pick and its consequence now read left-to-right instead
    // of with RPI between them. Column WIDTHS are untouched (`lmedium` /
    // `sshort`, common.css:18463-18495), and so is the headerMode filter below,
    // which keys off `cellName` rather than position.
    //
    // 5e-sync: dev renders the REQUIRED AW value as two warning-capable spans
    // (fan.selector.output.tmpl:267-270 — sizes_aw_width/sizes_aw_height get
    // `warning` red when the value exceeds the section AW; common.css:18577
    // `.fan_rated_item .fan_field[data-name=aw_width_height] span.warning`).
    // loading-dots mirrors `{{#unless aw_width_height}}loading-dots{{/unless}}`.
    {
      head: (
        <>
          REQUIRED AW W<span style={{ textTransform: 'lowercase' }}> x </span>H{' '}
          <span style={{ textTransform: 'lowercase' }}>(in)</span>
        </>
      ),
      headClass: 'lmedium left number-col',
      cellClass: (r) => `lmedium number-col fan_field${r.aw_width_height ? '' : ' loading-dots'}`,
      cellName: 'aw_width_height',
      render: (r) => (
        <>
          <span className={`sizes_aw_width${r.aw_width_warning ? ' warning' : ''}`}>{r.aw_width ?? ''}</span>
          {' x '}
          <span className={`sizes_aw_height${r.aw_height_warning ? ' warning' : ''}`}>{r.aw_height ?? ''}</span>
        </>
      ),
    },
    // RPI — backend rated value (fan.selector.output.tmpl:264-266, sortable by
    // data-sort="rpi"). Raw, not decimal-formatted, and "--" rather than blank
    // when absent.
    {
      sort: 'rpi',
      head: 'RPI',
      headClass: 'sshort left number-col sort-icon-wrapper',
      cellClass: 'sshort number-col fan_field',
      cellName: 'rpi',
      render: (r) => <>{r.rpi ? String(r.rpi) : '--'}</>,
    },
    // SPEED / MAX is NOT sortable in dev (tmpl:232-233 has no data-sort).
    // `speed-max-col` widens the shared `.long` bucket by RPI's sort-icon
    // overhang so the gap before this heading matches the one after it
    // (Anna, 2026-10-12) — see `.fan_rate-list-inline` in rating_mode.scss.
    {
      head: 'SPEED / MAX',
      headClass: 'long speed-max-col left number-col',
      cellClass: 'long speed-max-col number-col fan_field',
      cellName: 'speed_max',
      render: (r) => <>{`${r.speed ?? ''} / ${r.speed_max ?? ''}`}</>,
    },
    // Op. Pwr — the HP/KW split keys off motor_type_id (tmpl:234, :276-279),
    // NOT off the manufacturer, and the KW branch reads bp_kw / bp_kw_total
    // with the `multiply bp_kw qty 1` fallback (tmpl:278).
    {
      sort: 'bhp',
      head: (
        <>
          Op. Pwr<span> ({motorTypeId == 1 ? 'HP' : 'KW'})</span>
        </>
      ),
      headClass: 'long left number-col sort-icon-wrapper',
      cellClass: 'long number-col fan_field',
      cellName: 'bhp',
      render: (r) => (
        <span data-formatter_type="decimal">
          {motorTypeId == 1
            ? `${r.bhp ?? ''} / ${r.bhp_total ?? ''}`
            : `${r.bp_kw ?? ''} / ${r.bp_kw_total ?? hbMultiply(r.bp_kw, r.qty, 1)}`}
        </span>
      ),
    },
    // Dev-sync H: the cell prefers the DIRECT wheel_dia/width percent values
    // (Annexair carries them; its percents have no CMS id) and falls back to
    // the cms id-lookup. dev sorts this column by `wheel_width_percent`
    // (tmpl:238) — it is the DIA-W header that carries the sort icon.
    {
      sort: 'wheel_width_percent',
      head: (
        <>
          DIA - W<span> (%)</span>
        </>
      ),
      headClass: 'long left number-col sort-icon-wrapper',
      cellClass: 'long number-col',
      render: (r) => {
        const byId = (path: string, id: unknown): string => {
          if (id == null || id === '') return '';
          const row = fanCmsOptionListByPath(fanDoc as FanCmsDoc, path).find(
            (o: any) => String(o.id) == String(id)
          ) as Record<string, any> | undefined;
          return row?.value != null ? String(row.value) : '';
        };
        const dia = r.wheel_dia_percent != null && r.wheel_dia_percent !== ''
          ? String(r.wheel_dia_percent)
          : byId('fan.wheel.dia_percent', r.wheel_dia_percent_id);
        const wdt = r.wheel_width_percent != null && r.wheel_width_percent !== ''
          ? String(r.wheel_width_percent)
          : byId('fan.wheel.width_percent', r.wheel_width_percent_id);
        return (
          <>
            <span className="xshort number-col params_fan" data-name="wheel_dia_percent">{dia}</span>
            {' - '}
            <span className="xshort number-col params_fan" data-name="wheel_width_percent">{wdt}</span>
          </>
        );
      },
    },
    // FEI — NYB (209) / Twin City (237) only, and NOT sortable (tmpl:242-246).
    ...(mfg == 209 || mfg == 237
      ? [{
          head: 'FEI',
          headClass: 'sshort left number-col',
          cellClass: 'sshort left number-col',
          render: (r: Record<string, any>) => (
            <span data-formatter_type="decimal">{r.fei ?? ''}</span>
          ),
        } as FanRateColumn]
      : []),
    // SE / TE render RAW (tmpl:301-306 — no formatter), and the EC/DC vendors
    // take the substitution branches.
    {
      sort: 'static_efficiency',
      head: (
        <>
          SE<span> (%)</span>
        </>
      ),
      headClass: 'sshort left number-col sort-icon-wrapper',
      cellClass: 'sshort number-col fan_field',
      cellName: 'static_efficiency',
      render: (r) => {
        if (!fanType376764) return <>{r.static_efficiency ?? ''}</>;
        const chosen = chosenIndex != null ? (ratedRows ?? [])[chosenIndex] : undefined;
        return (
          <>
            {chosen && r.fan_type == chosen.fan_type
              ? chosen.static_efficiency ?? ''
              : r.static_efficiency ?? ''}
          </>
        );
      },
    },
    {
      sort: 'total_efficiency',
      head: (
        <>
          TE<span> (%)</span>
        </>
      ),
      headClass: 'sshort left number-col sort-icon-wrapper',
      cellClass: 'sshort number-col fan_field',
      cellName: 'total_efficiency',
      render: (r) =>
        fanType376764
          ? <>{r.total_efficiency ? r.total_efficiency : 'N/A'}</>
          : <>{r.total_efficiency ?? ''}</>,
    },
  ];

  // ★ APPROVED DEVIATION FROM DEV (QA 2026-09-14) — the header's Independent
  // Rating popup drops the REQUIRED AW W x H column.
  //
  // dev renders it in both hosts (fan.selector.output.tmpl:82 and :230, no
  // `header` guard), but its whole job is the comparison against the SECTION's
  // available airway: the two spans go red when the fan's minimum exceeds
  // `section.aw_width` / `aw_height` (js:4459-4462, common.css:18577). The
  // Independent Rating popup has no section — no project, no AHU, nothing
  // (component_selector.popup.js:39 passes `{header:true}` and nothing else) —
  // so the comparison can never fire, and the vendor responses come back with
  // `min_aw_width`/`min_aw_height` null, which leaves the cell as a permanent
  // `loading-dots` "x • • •". Reporter asked for it gone rather than blank.
  //
  // Scoped to `headerMode` ONLY: on the AHU board the column carries the red
  // over-airway warning and is tracked, recently-synced behaviour
  // (implementation-notes.html, "Sync E 07-19 — REQUIRED AW W×H").
  //
  // Nothing else needs resizing. Every cell is a fixed legacy width class and
  // the same array drives the heading row and the body, so the remaining
  // columns just reflow left together and the row loses the 185px `.lmedium`
  // took (common.css:18474-18476) — which is 185px less horizontal scroll.
  const ratedColumns = headerMode
    ? allRatedColumns.filter((c) => c.cellName !== 'aw_width_height')
    : allRatedColumns;

  const rows = ratedRows ?? [];

  // legacy `sort_model` (fan.selector.js:2949-2982). Note the inverted names:
  // `desc` sorts a-b (ascending). After a sort the list scrolls back to the top
  // and row 0 becomes the chosen fan — the curve follows the sort, and the
  // highlight can never drift off the row Select will save.
  const sortRated = (key: string) => {
    if (!rows.length) return;
    const order: 'asc' | 'desc' =
      ratedSort.by === key ? (ratedSort.order === 'asc' ? 'desc' : 'asc') : 'desc';
    const next = [...rows];
    if (key === 'model') {
      next.sort((a, b) =>
        order === 'desc'
          ? String(a.model ?? '').localeCompare(String(b.model ?? ''))
          : String(b.model ?? '').localeCompare(String(a.model ?? ''))
      );
    } else {
      next.sort((a, b) =>
        order === 'desc' ? Number(a[key]) - Number(b[key]) : Number(b[key]) - Number(a[key])
      );
    }
    setRatedSort({ by: key, order });
    // ★ fit-first and smallest-array stay the primary keys through a column
    // sort, so the clicked column orders WITHIN one fit verdict and one array
    // size — see `orderRatedRowsFitFirst`. `chooseFan` below has to
    // read the SAME array that is rendered, or the highlighted row 0 and the
    // curve would come from the pre-grouping order.
    const ordered = orderRatedRowsFitFirst(next);
    setRatedRows(ordered);
    rateListRef.current?.scrollTo({ top: 0 });
    if (ordered[0]) chooseFan(0, ordered, mfg, fanType);
  };
  // the `sort-icon` classes dev stamps on the sorted header (tmpl:221-256).
  const sortIcon = (key: string) => (
    <span
      className={`sort-icon${ratedSort.by === key ? ' selected' : ''}${
        ratedSort.by === key && ratedSort.order === 'asc' ? ' rotated' : ''
      }`}
      data-sort={key}
    >
      <i className="icon-arrow-down-1" data-sort={key} />
    </span>
  );

  // ----- VFD tab gating (fan.selector.js:2550-2557) -----
  // non-clickable unless: AC motor (motor_type_id 1 — motorSupportsVfd
  // js:7854-7857), a motor is resolved (motor_choosen||selectedMotorData —
  // F4b: `motorPick` is now the real motor_choosen, so the picked row counts
  // as well as the saved one), and no rate-invalidating change is pending.
  // `self.changed` mapping: every rate-invalidating edit (onSearchField)
  // clears ratedRows, and rate()/open-with-saved-fan repopulates it — while
  // legacy rate() resets self.changed (js:3907). So dirty := ratedRows == null.
  const motorSupportsVfd = Number(s.motor_type_id) == 1;
  // …and whether the field has been answered at all. The motor cascade fills
  // motor_type_id after the dialog mounts, so `motorSupportsVfd === false` means
  // "EC/PMAC" only once this is true — see FanVfdTab's auto-uncheck effect.
  const motorTypeKnown = s.motor_type_id != null && String(s.motor_type_id) !== '';
  const vfdTabDisabled = !motorSupportsVfd || !(motorPick?.row ?? selectedMotor) || ratedRows == null;
  // `headerMode ||` matches the li's own disabled test below, and legacy's
  // isVfdValid, which passes whenever `li.vfd` carries `non-clickable` — the
  // header template always sets it. Without it the header popup paints an
  // unopenable VFD tab red: `detail` is undefined there so FanVfdTab never
  // inits and `vfdRef` stays null, which makes isVfdValidNow false as soon as
  // has_vfd is true — and a Manufacturer change or Reset now ticks it.
  vfdTabDisabledRef.current = headerMode || vfdTabDisabled;
  /**
   * ★ The fan quantity the VFD bank is sized from — legacy's updateVfdQtyTotal
   * fallback chain (fan.selector.js:8562-8565), plus the rated row.
   *
   * With Qty Wide x High left on `--` there is no array in `state` for the
   * first term to multiply, so the chain used to fall through to the STORED
   * `fan_qty` — 1 on a fan that has never been selected — and the VFD tab's
   * Quantity read 1 however many fans the rated model actually needs
   * (reported by Anna, 2026-10-12).
   *
   * The array the numbers on screen belong to is the one riding the CHOSEN
   * rated row as `qty_wide`/`qty_high` (`AnnexairFanHandler._attach_wide_high`
   * for Annexair, `qty_expansion.py` for every other vendor; `chooseWideHigh`
   * swaps the whole row when the user picks a different array). That is
   * already what `buildFanSelectPayload` persists for an unset select, so
   * reading it here makes the drive count agree with what Select would save —
   * and it follows both a fresh rate and a later model/array pick, because
   * `chosenIndex` and `ratedRows` are what change underneath it.
   *
   * Nothing else moves: when the select holds a real value the first term is
   * non-zero and wins, and with no rated row on screen (not rated yet, or a
   * rate-invalidating edit cleared the list) the row term is 0 and the stored
   * `fan_qty` is reached exactly as before.
   */
  const chosenRatedRow = chosenIndex != null ? ratedRows?.[chosenIndex] : null;
  const vfdFanQty =
    (Number(s.qty_wide) || 0) * (Number(s.qty_high) || 0) ||
    (Number(chosenRatedRow?.qty_wide) || 0) * (Number(chosenRatedRow?.qty_high) || 0) ||
    Number(detail?.fan_qty) ||
    1;
  // isVfdValid (js:8649-8655) — drives the li's vfd-tab-invalid class
  // (checkButtonsAvailability js:3651) and the doSelect abort gate (B3).
  const vfdValid = isVfdValidNow();
  // ----- B4: VFD OUTPUT tab gating (fan.selector.output.tmpl:21-22/47-48 —
  // `{{#ifCompare params_fan.has_vfd 'and' vfd.master_vfd_id}}{{else}}
  // non-clickable{{/ifCompare}}`): enabled only when Has-VFD is checked AND a
  // catalog VFD is picked. isVfdNone covers the legacy null/'None'/'' forms.
  const vfdOutputEnabled = Boolean(s.has_vfd) && !isVfdNone(vfdRef.current?.master_vfd_id);
  // the pane's render context (fan.selector.js:2824-2835 — vfd_params=
  // self.vfd_params, vfd=self.vfd, vfd_cms=vfdCms.toJSON(), vfd_qty=
  // self._vfdQtyTotal). Refs read at render time; every writer bump()s.
  const vp = vfdParamsRef.current;
  const vv = vfdRef.current ?? {};
  const vfdConfigurationName = String(
    (vfdCmsDoc?.vfd?.configuration ?? []).find(
      (row) => String(row.id) == String(vv.configuration_id)
    )?.name ?? ''
  );

  // ----- the ISOLATION output pane (fan.selector.output.isolation.tmpl) ------
  // dev renders it from `chooseIsolation` (fan.selector.js:7485-7588) with four
  // context keys: `isoMfg`/`isoSeries` (the mergeIdsWithCMS option lists, walked
  // to find the row whose id matches), `si_rate_data` (the .si_params selects
  // scraped off the Isolation INPUT tab, js:6172-6194 — so `isolator_mfg` /
  // `isolator_series` ARE `s.isolator_mfg_id` / `s.isolator_series_id` here) and
  // `isolatorsData.isolators`, the AHUFan.isolators property
  // (models_component_fan.py:2167 — model + 2dp actual_load + 2dp
  // actual_deflection per location, or None unless all four resolve).
  //
  // dev has THREE branches for `isolatorsData`: `fan_isolators_supplied_by_id
  // == 2` renders `model_data.model` (js:7513) and an already-Selected fan
  // renders `current_model` (js:7580) — both the component-detail record, i.e.
  // `detail` — while a rated-but-not-yet-Selected fan (js:7540) PATCHes the fan
  // with `isolatorSaveData` and renders the RESPONSE. `patchedIsolators` is
  // that third branch's result (see the effect above); it is only ever set on
  // that branch, so the `??` reproduces dev's if/else.
  const isolators = ((patchedIsolators ?? (detail as Record<string, any> | undefined)?.isolators) ??
    null) as
    | Record<string, { isolator?: string; actual_load?: string; actual_deflection?: string }>
    | null;
  const isoMfgName = fanCmsSelectedLabelByPath(
    isolatorDoc as FanCmsDoc,
    'spring_isolator.manufacturer',
    s.isolator_mfg_id as number | string | null
  );
  const isoSeriesName = fanCmsSelectedLabelByPath(
    isolatorDoc as FanCmsDoc,
    'spring_isolator.series',
    s.isolator_series_id as number | string | null,
    'series'
  );

  // ----- the NOTES output pane (fan.selector.output.tmpl:734-787) ------------
  // FOUR independent blocks plus a model-name heading, of which the port only
  // ever rendered the last one. `model_notes` is the live vendor row's own notes
  // (js:3145-3148, `choosenFanData?.notes`) and is what dev shows under a plain
  // "Notes :" label — it is NOT header-gated, which is why dev renders notes in
  // a mode where `self.notes` is folded to ''.
  const notesModelName = String(
    choosenFanData?.model ||
      choosenFanData?.fan_model ||
      selectedRow?.model ||
      selectedRow?.fan_model ||
      ''
  );
  // `selected.rated_notes` — the notes stamped into fan_params at Select time
  // (setOutputs js:3991, `self.fanParams.notes`).
  const apiNotes: string[] = Array.isArray(selectedRow?.rated_notes)
    ? (selectedRow!.rated_notes as unknown[]).map(String).filter(Boolean)
    : [];
  const modelNotes: string[] = Array.isArray(choosenFanData?.notes)
    ? (choosenFanData!.notes as unknown[]).map(String).filter(Boolean)
    : [];
  // ★ HEADER MODE renders NO mfg notes: the NotesCollection is only built in the
  // board branch (`if (!self.header && …)`, fan.selector.js:1249) and the render
  // context folds it to '' (`notes: !self.notes || self.header ? "" : …`,
  // js:2416). The other three blocks are NOT gated that way.
  const mfgNotes = headerMode ? [] : (notesQ.data ?? []);

  // ----- F4: the selected-motor OUTPUT row (fan.selector.output.tmpl 455-607) --
  // motor_type_id 1 → HP + FRAME/SHAFT-GROUNDING columns; type 2 → KW, those
  // hidden. (motorTypeId / motorIsKw are declared with the rated columns above.)
  const motorQty = Number(s.fan_qty) || (Number(s.qty_wide) || 1) * (Number(s.qty_high) || 1) || 1;
  // F4b — legacy's `motorOutputRows` (render_outputs js:3040-3046): the rated
  // motor LIST when it has rows, else the single `motor_choosen ||
  // selectedMotorData` row. The two branches share these columns.
  const motorRow = motorPick?.row ?? selectedMotor;
  const motorOutputRows: Array<Record<string, any>> = motorModels?.length
    ? motorModels
    : motorRow
      ? [motorRow]
      : [];
  const motorListMode = Boolean(motorModels?.length);
  const mp1 = (v: unknown) => (v == null || v === '' ? '' : formatDecimalDisplay(v));
  // Same legacy-CSS-selector miss as the fan table above: the motor sheet is
  // dev's `.rate-list-inline.motor-rate-list` (common.css:18394-18406, which is
  // what supplies `.long-input { min-width:140px }` and `.model-col
  // { min-width:150px }`), NOT a Bootstrap grid. The `hide` class on the FRAME
  // and SHAFT GROUNDING cells is dev's own gating (tmpl:462/465).
  // FRAME hides when NO displayed row has one, and MODEL widens when ANY of
  // them is a Domel AZ — both are `motorOutputRows.some(...)` on dev
  // (js:3047-3060), not a property of the single row.
  const motorHideFrame = motorIsKw || !motorOutputRows.some((m) => m && m.nema_frame);
  const motorIsDomelAz = motorOutputRows.some((m) => m && m.mfg === 'Domel' && m.series === 'AZ');
  const motorColumns: FanRateColumn[] = [
    { head: 'MANUFACTURER', headClass: 'meidum_plus left KKK', headStyle: { paddingLeft: 15 },
      cellClass: 'meidum_plus number-col params_fan', cellName: 'mfg_id',
      cellStyle: { paddingLeft: 15, textAlign: 'left' },
      render: (r) => <>{r.mfg ?? ''}</> },
    { head: 'SERIES', headClass: 'sshort left', headStyle: { textAlign: 'left' },
      cellClass: 'sshort number-col params_fan', cellName: 'series_id',
      cellStyle: { textAlign: 'left' },
      render: (r) => <>{r.series ?? ''}</> },
    { head: 'MODEL', headClass: `long${motorIsDomelAz ? ' model-col' : ''} left`,
      headStyle: { textAlign: 'left' },
      cellClass: `long${motorIsDomelAz ? ' model-col' : ''} number-col fan_field`, cellName: 'model',
      cellStyle: { textAlign: 'left' },
      render: (r) => <>{r.model ?? ''}</> },
    { head: `POWER (${motorIsKw ? 'KW' : 'HP'})`, headClass: 'meidum_plus left number-col',
      cellClass: 'meidum_plus number-col fan_field', cellName: 'power',
      render: (r) => {
        // dev's list branch keys the KW conversion off the ROW's own type_id
        // (tmpl:553) where the single-row branch uses the state's
        // motor_type_id (tmpl:487); both multiply by motor_Qty for the total.
        const kw = motorListMode ? Number(r.type_id) == 2 : motorIsKw;
        const per = kw ? Number(r.power) * 0.7457 : Number(r.power);
        const tot = kw ? Number(r.power) * motorQty * 0.7457 : Number(r.power) * motorQty;
        return <span data-formatter_type="decimal">{`${mp1(per)} / ${mp1(tot)}`}</span>;
      } },
    { head: 'SPEED', headClass: 'sshort left number-col',
      cellClass: 'sshort number-col params_fan', cellName: 'speed',
      render: (r) => <>{r.speed_nominal ?? r.speed ?? ''}</> },
    { head: 'FRAME', headClass: `sshort left${motorHideFrame ? ' hide' : ''}`,
      cellClass: `sshort fan_field${motorHideFrame ? ' hide' : ''}`, cellName: 'frame',
      render: (r) => <>{r.nema_frame ?? ''}</> },
    { head: 'ENCLOSURE', headClass: 'long left',
      cellClass: 'long fan_field', cellName: 'enclosure',
      render: (r) => <>{r.enclosure ?? ''}</> },
    { head: 'HOUSING', headClass: 'long left',
      cellClass: 'long fan_field', cellName: 'housing',
      render: (r) => <>{r.housing ?? ''}</> },
    { head: 'SHAFT GROUNDING', headClass: `long-input left${motorIsKw ? ' hide' : ''}`,
      cellClass: `long-input fan_field${motorIsKw ? ' hide' : ''}`, cellName: 'shaft_grounding',
      render: (r) => <>{r.shaft_grounding ?? ''}</> },
    { head: 'FL EFF', headClass: 'sshort left number-col',
      cellClass: 'sshort number-col fan_field', cellName: 'fl_eff',
      render: (r) => <span data-formatter_type="decimal">{mp1(r.fl_efficiency)}</span> },
    { head: 'FLA', headClass: 'medium left number-col',
      cellClass: 'medium number-col fan_field', cellName: 'fla',
      render: (r) => {
        const tot = r.fla_total ?? (r.fla != null ? Number(r.fla) * motorQty : null);
        return <span data-formatter_type="decimal">{r.fla != null ? `${mp1(r.fla)} / ${mp1(tot)}` : ''}</span>;
      } },
    { head: 'ROTOR', headClass: 'sshort left',
      cellClass: 'sshort fan_field', cellName: 'rotor',
      render: (r) => <>{r.rotor_location ?? ''}</> },
    { head: 'IP RATING', headClass: 'width-80 right number-col',
      cellClass: 'width-80 right number-col fan_field', cellName: 'ip_rating',
      render: (r) => <>{r.ip_rating ?? ''}</> },
  ];

  return (
    /* ⚠ DO NOT re-wrap this in dev's outer chain without a live check.
       dev nests the nav + tab host as
         #rating_mode_fan_content (padding:16px 24px 38px 24px, common.css:13769)
           > .coil_new > .row.full-height.for-rating
             > .col-xs-12.fan-data-input > .row.input-part
       (component_selector.tmpl:93, fan.selector.tmpl:7-9), and reproducing it
       here is worth ~14px of offset and ~8px of control width. It was tried and
       REVERTED on 2026-09-01: the two `.row` layers put the tab strip 15px LEFT
       of `.rating-mode-wrapper`'s origin (common.css:13979, `overflow-x:auto`),
       and left overflow is not reachable by scrolling — the tab names came out
       cut off down the left edge ("Input" -> "ut", "Performance" -> "rformance")
       with a horizontal scrollbar across the dialog. Restoring
       #rating_mode_fan_content on its own did not save it and added a second,
       vertical scrollbar, because it also switches on
       `#rating_mode_fan_content .fan-data-input { height:49%; min-height:363px }`
       (:19278) against a React shell whose height model differs from dev's.
       A headless probe of both DOM chains showed NO overflow at any width, so
       the trigger is something the static model does not capture — treat this as
       needing a real dev3 render, not a measurement, before trying again. */
    <FanFieldCtx.Provider value={fieldCtx}>
    <div
      ref={rootRef}
      id="fan-selector"
      className="fan input-part rating coil_new per-fan fan-rating-mode"
    >
      {/* input tab nav */}
      <div className="col-xs-1 nav nav-tabs input-menu">
        {/* ★ dev-sync F Stage 2 (legacy-CSS-selector miss) — the `.row` wrapper
            and `ul.col-xs-12` are restored to match fan.selector.tmpl:10-12
              <div class="col-xs-1 nav nav-tabs"><div class="row"><ul class="col-xs-12">
            because dev's new rating_mode.scss (→ common.css) keys on exactly
            that shape:
              #rating_mode_fan_content .nav.nav-tabs .row { height:100% }
              #rating_mode_fan_content .nav.nav-tabs ul.col-xs-12 {
                height:100%; display:flex; flex-direction:column }
            which is what makes `.fan-how-to-menu { margin-top:auto }` push the
            How-to item to the bottom of the tab column. Without the wrapper +
            class the new rules silently no-op. Same precedent as
            FilterSelector.tsx:1162 (`<ul className="col-xs-12">`).
            ⚠ This re-introduces Bootstrap gutters (.row negative margins,
            .col-xs-12 padding) into this column — correct for parity. */}
        <div className="row">
        <ul className="col-xs-12">
          <li className="cursor-default">Input</li>
          {(['properties', 'performance', 'pressure_drop', 'motor', 'vfd', 'isolation', 'mounting', 'options', 'pricing'] as InputTab[]).map((t) => {
            // the VFD li mirrors the legacy `input_label vfd` + non-clickable
            // gating (tmpl:26 / js:2550-2557).
            // ★ Phase 6.5 — in HEADER mode the li is UNCONDITIONALLY
            // non-clickable with an inline `pointer-events:none;opacity:.6`
            // (fan.selector.tmpl:29 `{{#if header}}`): the whole VFD branch is
            // AHU-derived (its section dropdown loader bails without an AHU,
            // js:8634-8638, and prefetchVfdByMotor early-returns, js:8062).
            // 5e-sync: the pressure_drop li restored per dev 5fd0695113
            // (fan.selector.tmpl:23-25 — `input_label pressure_drop`).
            // the Options li greys off exactly like dev's: non-clickable while
            // every select on that pane is (js:8796-8798). The Isolation li
            // follows the same shape for a PLUG fan, which has no spring
            // isolators at all — see `isolationTabEnabled`.
            const disabled =
              (t === 'vfd' && (headerMode || vfdTabDisabled)) ||
              (t === 'options' && !optionsTabEnabled) ||
              (t === 'isolation' && !isolationTabEnabled);
            const extra =
              t === 'vfd'
                ? `${disabled ? ' non-clickable' : ''}${!vfdValid ? ' vfd-tab-invalid' : ''}`
                : (t === 'options' || t === 'isolation') && disabled
                  ? ' non-clickable'
                  : '';
            return (
              <li
                key={t}
                className={`input_label ${t}${inputTab === t ? ' selected active' : ''}${extra}`}
                style={
                  t === 'vfd' && headerMode
                    ? { pointerEvents: 'none', opacity: 0.6 }
                    : undefined
                }
              >
                <a
                  className={inputTab === t ? 'active' : undefined}
                  onClick={() => {
                    if (disabled) return;
                    setInputTab(t);
                  }}
                >
                  {INPUT_TAB_LABELS[t]}
                </a>
              </li>
            );
          })}
          {/* dev-sync F Stage 2 — the eleventh <li>, fan.selector.tmpl:49-52:
                <li class="fan-how-to-menu {{#ifCompare params_fan.mfg_id '!=' 209}}hide{{/ifCompare}}"
                    data-name="how_to">
                  <a href="javascript:void(0)">How to</a>
                </li>
              209 = New York Blower. Legacy renders the li unconditionally and
              toggles `.hide` (js:4822-4824), so it is mirrored that way rather
              than conditionally rendered. */}
          <li
            className={`fan-how-to-menu${mfg != 209 ? ' hide' : ''}`}
            data-name="how_to"
            onClick={openHowTo}
          >
            <a>How to</a>
          </li>
        </ul>
        </div>
      </div>

      {/* ★ NO `full-height` here — see ahuBoardOverrides.scss §4. dev's tab host
          is `col-xs-11 each-tabs` with a PLAIN inline `height:100%`
          (fan.selector.tmpl:55), resolved against `.fan-data-input`'s
          `height:49%; min-height:363px` (common.css:19278). React has no
          `.fan-data-input` wrapper, so §4 puts that 49% on the nav column and on
          this host directly — but `.full-height { height:100% !important }`
          (common.css:17377) outranks it, so this one child stayed at 100% while
          the nav beside it obeyed the 49%. Measured on dev3 at 1920x1080, a
          824px host: nav 403.75px, this host 824px; dev is 403.76px for both. */}
      <div className="col-xs-11 tab-content content-data right">
        {/* ★ dev-parity CONTROL GEOMETRY (legacy-CSS-selector miss). Every select
            in dev's dialog is sized by
              .per-fan .each-tabs select { width: calc(100% - 24px); padding-left: 5px }
              .per-fan .each-tabs .scrollable-content .right-column { padding-right: 0 !important }
            (common.css:17822/17830). The port's tab host carries `tab-content
            content-data`, never `each-tabs`, so NOT ONE of those rules matched
            and every control fell back to shrink-to-fit — measured 34-124px
            against dev's 234px (full) / 103px (half). `each-tabs` is added HERE,
            on the Fan pane, rather than on the shared tab host: with it on the
            host, `.coil_new .content-data .input-part input { width: 40px }`
            stops applying to the OTHER tabs and Performance's inputs jump
            40px -> 173px with ISP|ESP|APD wrapping onto three lines (measured).
            `scrollable-content` moves onto the inner wrapper so the
            `.each-tabs .scrollable-content .right-column` chain still resolves.
            Verified against a headless render of both DOMs over the real
            stylesheets: every control within 1px of dev, row heights exact. */}
        {/* `row` is dev's (tmpl:57 `<div class="row tab-pane fade …">`) and is
            load-bearing: without its -15px gutters the whole grid sits 15px in
            and each control is 30px narrower. */}
        <div className={`row ${tabClass('properties')} each-tabs`} id="ahu_fan_properties">
          {/* ----- Properties — the Input > "Fan" tab -----
              dev-parity rebuild against fan.selector.tmpl:57-347. The rows use
              dev's own wrapper shape — `col-xs-12 left-column|right-column` >
              `col-xs-5` > `.row.label-part` + `col-xs-7`, with a nested
              `.row > .col-xs-6` for the HALF-width controls — because that
              nesting is what makes Type / Wheel Type / Wheel Width / Wheel
              Material narrower than the rest on dev. */}
          {/* ★ `mt-0` — see the VFD pane below for the full note. In short:
              every pane's inner `.scrollable-content` wrapper is its pane's
              `:first-child`, so it matches the coil-dialog rule
                `.coil_new .content-data .scrollable-content:first-child
                   { margin-top: 33px }`                     (common.css:15869)
              which only fires because the React tab host carries
              `content-data`; dev's `#report-outputs-group` does not. The pane
              is a BFC (`.scrollable-content` is `overflow-y:auto`), so the 33px
              does NOT collapse away — it stacks on top of the 23px in
              ahuBoardOverrides §5 that reproduces dev's host padding, and every
              input page rendered 33px lower than dev. Measured on dev3
              (1920x1080, header rating popup): host top 95.5px → first label row
              152.5px on Fan / Performance / Isolation / Mounting / Options /
              Pricing, against dev's single 23px + 1px border for all nine tabs.
              Motor is unaffected — its first child is the sub-tab menu, so the
              rule never matched there, which is why §5 was written from the
              Motor pane and this went unnoticed.
              `mt-0` is dev's OWN opt-out for that rule (common.css:15872). */}
          <div id="fan-tab-properties" className="fan-tab-subtub fan-tab-properties scrollable-content mt-0">
            <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
              <FanSelectRow side="left" name="mfg_id" label="Manufacturer" cls="params_fan" />
              {/* Type and "Housing" share ONE row (tmpl:76-99): the Housing half
                  and its `| Housing` label suffix are revealed only for a Cube
                  fan (type 3), via `.hide_housing` (js:2783-2787). */}
              <FanRow side="left" label={
                <>
                  <span data-name="type_id">Type</span>
                  {typeId === 3 && <span data-name="cube_housing_id">{' | Housing'}</span>}
                </>
              }>
                <div className="row">
                  <div className="col-xs-6">
                    <BareDocSelect name="type_id" cls="search_field params_fan settings_fan" />
                  </div>
                  {typeId === 3 && (
                    <div className="col-xs-6">
                      <BareDocSelect name="cube_housing_id" cls="search_field params_fan" />
                    </div>
                  )}
                </div>
              </FanRow>
              <FanSelectRow side="left" name="series_id" label="Series" labelKey="series"
                cls="search_field params_fan has_changable_default" />
              {/* Style is ALWAYS rendered, and greyed by the DATA — i.e. by the
                  shared `rows.length === 0` rule every other params select uses
                  (BareDocSelect/FanSelectRow), not by a manufacturer list.

                  Legacy also carries a hardcoded un-grey for Rosenberg (164) /
                  EBM-Papst (37) / Ziehl-Abegg (67) (js:2763-2773), written
                  before Annexair (237) was a fan vendor. Style is a
                  `MasterAHUFanPlugArr4` column and the params call
                  (`…/fans/params/style_id/?mfg_id&type_id&series_id`) answers
                  from exactly that table, so the id list is empty for every
                  vendor with no plug-arr4 rows — 23 / 47 / 64 / 209 / 221 all
                  have none — and those stay greyed on the data rule alone.
                  The four vendors that DO have rows are 37 / 67 / 164 and
                  Annexair, whose 120 plug rows are all Spider. Dropping the
                  list therefore un-greys Annexair and nothing else, and it also
                  greys those three correctly on a non-plug fan type, where the
                  hardcode used to un-grey an empty select (Anna, 2026-10-07). */}
              <FanSelectRow side="left" name="style_id" label="Style"
                cls="search_field params_fan hide_style" />
              {/* Wheel Diameter is New-York-Blower-only (`only-for-ny-blower`,
                  non-clickable when mfg != 209 — tmpl:126). */}
              <FanSelectRow side="left" name="wheel_dia_nominal_id" label="Wheel Diameter"
                labelKey="value" rowClass="only-for-ny-blower"
                cls="search_field params_fan" disabled={mfg !== 209} />
              {!isAnnexair && (
                <>
                  <FanSelectRow side="left" name="wheel_type_id" label="Wheel Type" half
                    cls="search_field params_fan has_changable_default" />
                  <FanSelectRow side="left" name="wheel_width_percent_id" label="Wheel Width"
                    labelKey="value" unit="%" half
                    cls="search_field params_fan has_changable_default" />
                </>
              )}
            </div>

            <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
              {/* Arrangement is fixed for Rosenberg (164) — legacy renders NO
                  options at all and marks the select non-clickable (tmpl:202-209). */}
              {!isAnnexair && (
                <>
                  <FanSelectRow side="right" name="arr_id" label="Arrangement"
                    cls="search_field params_fan right-select settings_fan has_changable_default"
                    disabled={mfg === 164} />
                  <FanSelectRow side="right" name="wheel_mtl_id" label="Wheel Material" half
                    cls="search_field params_fan has_changable_default" />
                  <FanSelectRow side="right" name="rotation_id" label="Rotation"
                    cls="search_field right-select params_fan first-small has_changable_default" />
                </>
              )}
              {/* AMCA Class: the whole ROW is removed when the field's params call
                  comes back with nothing, rather than gated on fan type — legacy
                  `.amca-class-row` toggle, js:2885-2897. */}
              {amcaHasOptions && (
                <FanSelectRow side="right" name="amca_class_id" label="AMCA Class"
                  rowClass="amca-class-row"
                  cls="search_field params_fan right-select settings_fan has_changable_default getMotorCount" />
              )}
              {/* Qty Wide X High — ONE select over the 1..max_qty_wide x
                  1..max_qty_high combinations (Anna, 2026-10-08). dev has two
                  selects over the two ranges (updateQty, js:3837-3849) in a
                  col-xs-5 / col-xs-1 / col-xs-5 split (tmpl:277-299); the single
                  select takes the full width instead.

                  ⚠ It sits DIRECTLY in the row's col-xs-7 and carries
                  `right-select`, with no nested columns and no inline width, so
                  it is laid out exactly like AMCA Class / Arrangement above it
                  (`calc(100% - 24px)` of the same box). The nesting it used to
                  have — `row > col-xs-12 > row > col-xs-11` with
                  `calc(100% - 10px)` — cost it a twelfth of the column and gave
                  back only 14px, leaving it ~15px short of every other select
                  in the column (Anna, 2026-10-14).
                  The pair is still stored as qty_wide + qty_high — see
                  FAN_QTY_PAIR_FIELD. */}
              <FanRow side="right" label={
                <>
                  {/* JSX WHITESPACE COLLAPSE — dev's three spans sit on their own
                      lines (tmpl:271-273), so HTML leaves a space either side of
                      the X and the label reads "Qty Wide x High". JSX drops
                      newline-only whitespace between elements entirely, which is
                      why dev3 rendered "Qty WidexHigh". */}
                  <span data-name="qty_wide">Qty Wide</span>{' '}
                  <span className="unit">X</span>{' '}
                  <span data-name="qty_high">High</span>
                </>
              }>
                <select name={FAN_QTY_PAIR_FIELD} data-name={FAN_QTY_PAIR_FIELD}
                  className="search_field fan_field number_field right-select"
                  value={fanQtyPairValue(s.qty_wide, s.qty_high)}
                  onChange={(e) => onSearchField(FAN_QTY_PAIR_FIELD, e.target.value)}>
                  {qtyPairOptions.map((o) => <option key={o.value} value={o.value} data-value={o.value}>{o.label}</option>)}
                </select>
              </FanRow>
              {/* Available Airway W X H — read-only echo of the SECTION's airway,
                  hidden in the header/sizer host where legacy's `prefix` is ''
                  (tmpl:302 `{{#unless prefix}}hide{{/unless}}`, js:2619). */}
              {!headerMode && (
                <FanRow side="right" unit="in" label={
                  <>
                    <span data-name="aw_width">Available Airway W</span>
                    <span className="unit">X</span>
                    <span data-name="aw_height">H</span>
                  </>
                }>
                  <div className="row"><div className="col-xs-12"><div className="row">
                    <div className="col-xs-5">
                      <input name="aw_width" readOnly disabled tabIndex={-1}
                        style={{ width: 'calc(100% + 15px)' }}
                        value={sectionAw(0)} onChange={() => undefined} />
                    </div>
                    <div className="col-xs-1" style={{ marginTop: 12 }}><span className="unit">X</span></div>
                    <div className="col-xs-5">
                      <input name="aw_height" readOnly disabled tabIndex={-1}
                        style={{ width: 'calc(100% + 15px)' }}
                        value={sectionAw(1)} onChange={() => undefined} />
                    </div>
                  </div></div></div>
                </FanRow>
              )}
            </div>
          </div>
        </div>

        {/* ----- Performance -----
            dev-parity rebuild against fan.selector.tmpl:349-666, same treatment
            as the Fan tab: dev's row wrappers (`col-xs-12 left-column|
            right-column` > `col-xs-5` > `.row.label-part` + `col-xs-7`) and
            `each-tabs` on the pane so the sizing rules match.
            The port had the field set spread across the wrong columns in the
            wrong order under invented labels — "TSP" for Total Static Pressure,
            "Airflow Actual"/"Airflow per Fan" as two rows where dev pairs them
            as "Airflow per fan | total", and ISP / ESP / APD collapsed into one
            "ISP | ESP | APD" row where dev gives each its own (Internal Static
            Pressure, ESP Available / Desired, Fan Pressure Loss), interleaved
            with the editable ones. Required-field asterisks were never drawn. */}
        <div className={`row ${tabClass('performance')} each-tabs`} id="ahu_fan_performance">
          <div className="scrollable-content mt-0">
          <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
            {/* "Airflow per fan | total" is ONE row of two inputs (tmpl:351-372). */}
            <FanRow side="left" unit="CFM" label={
              <>
                <span data-name="airflow_per_fan" className={reqCls('airflow_per_fan')}>Airflow per fan</span>
                {' | '}
                <span data-name="airflow_actual" className={reqCls('airflow_actual')}>total</span>
              </>
            }>
              <div className="row">
                <div className="col-xs-6">
                  <FanNumberInput data-type="number" autoComplete="off" name="airflow_per_fan"
                    formatter="integer"
                    className="col-xs-12 search_field fan_field fan_field_key_up"
                    value={s.airflow_per_fan}
                    onCommit={(v) => onSearchField('airflow_per_fan', v)} />
                </div>
                <div className="col-xs-6">
                  <FanNumberInput data-type="number" autoComplete="off" name="airflow_actual"
                    id="airflow_actual" formatter="integer"
                    className="search_field fan_field fan_field_key_up"
                    value={s.airflow_actual}
                    onCommit={(v) => onSearchField('airflow_actual', v)} />
                </div>
              </div>
            </FanRow>
            <FanRow side="left" unit="in WC" label={
              <span data-name="tsp" className={reqCls('tsp')}>Total Static Pressure</span>
            }>
              <div className="row"><div className="col-xs-6">
                {/* `.get_isp_esp` — ESP Available is the server's TSP − ISP, so
                    dev re-pulls the trio whenever TSP is committed
                    (`change .get_isp_esp`, js:582). The class was ported but
                    not the handler, so the box would have gone stale. */}
                <FanNumberInput data-type="number" autoComplete="off" name="tsp"
                  className="search_field fan_field get_isp_esp fan_field_key_up"
                  value={s.tsp}
                  onCommit={(v) => onGetIspField('tsp', v)} />
              </div></div>
            </FanRow>
            {/* ISP / ESP / APD are three SEPARATE read-only rows on dev
                (tmpl:390-442), not one combined echo. */}
            <FanRow side="left" unit="in WC" label={<span data-name="isp">Internal Static Pressure</span>}>
              <div className="row"><div className="col-xs-6">
                <input type="text" data-type="number" name="isp" readOnly tabIndex={-1}
                  className="col-xs-12 fan_field search_field"
                  value={formatDecimalDisplay(s.isp)} onChange={() => undefined} />
              </div></div>
            </FanRow>
            <FanRow side="left" unit="in WC" label={
              <>
                <span data-name="esp">ESP Available /</span>
                <span data-name="esp_desired"> Desired </span>
              </>
            }>
              <div className="row">
                <div className="col-xs-3 dash-symbol-wrap">
                  <input type="text" data-type="number" id="esp" name="esp" readOnly tabIndex={-1}
                    className="col-xs-12 fan_field search_field"
                    value={formatDecimalDisplay(s.esp)} onChange={() => undefined} />
                  <span className="dash-symbol"> / </span>
                </div>
                <div className="col-xs-3">
                  <FanNumberInput data-type="number" autoComplete="off" id="esp_desired"
                    name="esp_desired"
                    className="col-xs-12 fan_field search_field"
                    value={s.esp_desired}
                    onCommit={(v) => onSearchField('esp_desired', v)} />
                </div>
              </div>
            </FanRow>
            <FanRow side="left" unit="in WC" label={<span data-name="apd">Fan Pressure Loss</span>}>
              <div className="row"><div className="col-xs-6">
                <input type="text" data-type="number" name="apd" readOnly tabIndex={-1}
                  className="col-xs-12 fan_field search_field"
                  value={formatDecimalDisplay(s.apd)} onChange={() => undefined} />
              </div></div>
            </FanRow>
            {/* Air stream — dev's own `- -` row, and no unit (tmpl:443-461).
                The port labelled it "Air Stream" and hung a stray CFM off it. */}
            <FanRow side="left" label={<span data-name="air_stream_id">Air stream</span>}>
              <div className="row"><div className="col-xs-6">
                <select name="air_stream_id" data-name="air_stream_id" required
                  className="medium-input fan_field getAHUAirflow get_isp_esp"
                  value={s.air_stream_id == null ? '' : String(s.air_stream_id)}
                  onChange={(e) => {
                    // also a `.get_isp_esp` driver (tmpl:452) — the air stream
                    // picks WHICH tunnel's pressure drops make up the ISP.
                    onModelField('air_stream_id', e.target.value);
                    void doIsp(true);
                  }}>
                  <option value="None">- -</option>
                  {/* dev prints `{{name}}` (tmpl:455) — "Supply", not the slug.
                      `ksdm_master_ahu_air_stream` carries both (1|Supply|supply),
                      and the port was labelling from `value`, so dev3 read
                      "supply" / "return". The option's VALUE is the row id
                      either way, and collectData resolves `air_stream` from the
                      row itself (:2728-2735), so only the visible text moves. */}
                  {streams.map((r) => (
                    <option key={String(r.id)} value={String(r.id)}>{optionLabel(r, 'name')}</option>
                  ))}
                </select>
              </div></div>
            </FanRow>
            {/* dev's own 20px break before Max % of Peak (tmpl:462). */}
            <FanRow side="left" unit="%" rowStyle={{ marginTop: 20 }}
              label={<span data-name="max_peak_pressure_percent">Max % of Peak</span>}>
              <div className="row"><div className="col-xs-12">
                {/* dev's `data-step="1"` puts this box on validateNumberField's
                    INTEGER branch (js:8477-8482 `input.data("step") == 0.01 ?
                    "decimal" : "integer"`), so the stored 90 renders "90", not
                    "90.00". */}
                <FanNumberInput data-type="number" autoComplete="off"
                  name="max_peak_pressure_percent" formatter="integer"
                  className="search_field fan_field"
                  value={s.max_peak_pressure_percent}
                  onCommit={(v) => onSearchField('max_peak_pressure_percent', v)} />
              </div></div>
            </FanRow>
            <FanRow side="left" unit="in WC" label={
              <span data-name="fan_inlet_pressure" className={reqCls('fan_inlet_pressure')}>Inlet Pressure</span>
            }>
              <div className="row"><div className="col-xs-12">
                <FanNumberInput data-type="number" autoComplete="off" name="fan_inlet_pressure"
                  className="search_field fan_field"
                  value={s.fan_inlet_pressure}
                  onCommit={(v) => onSearchField('fan_inlet_pressure', v)} />
              </div></div>
            </FanRow>
            {/* Dev-sync H — ANNEXAIR row swap (tmpl:496-513 `.only-for-annexair`
                vs :514-531 `.hide-for-annexair`, toggled live by js:4900-4906):
                mfg 237 shows "Temp. Inlet DB / WB", everyone else
                "Temp. Inlet / Design". Neither carries a unit on dev. */}
            {mfg == 237 ? (
              <FanRow side="left" rowClass="only-for-annexair" label={
                <>
                  <span data-name="fan_inlet_temp" className={reqCls('fan_inlet_temp')}>Temp. Inlet DB</span>
                  {' / '}
                  <span data-name="fan_inlet_temp_wb" className={reqCls('fan_inlet_temp_wb')}>WB</span>
                </>
              }>
                <div className="row">
                  <div className="col-xs-6">
                    <FanNumberInput data-type="number" autoComplete="off" name="fan_inlet_temp"
                      className="search_field fan_field has_changable_default"
                      value={s.fan_inlet_temp}
                      onCommit={(v) => onSearchField('fan_inlet_temp', v)} />
                  </div>
                  <div className="col-xs-6">
                    <FanNumberInput data-type="number" autoComplete="off" name="fan_inlet_temp_wb"
                      className="search_field fan_field has_changable_default"
                      value={s.fan_inlet_temp_wb}
                      onCommit={(v) => onSearchField('fan_inlet_temp_wb', v)} />
                  </div>
                </div>
              </FanRow>
            ) : (
              <FanRow side="left" rowClass="hide-for-annexair" label={
                <>
                  <span data-name="fan_inlet_temp" className={reqCls('fan_inlet_temp')}>Temp. Inlet</span>
                  {' / '}
                  <span data-name="fan_inlet_design">Design</span>
                </>
              }>
                <div className="row">
                  <div className="col-xs-6">
                    <FanNumberInput data-type="number" autoComplete="off" name="fan_inlet_temp"
                      className="search_field fan_field"
                      value={s.fan_inlet_temp}
                      onCommit={(v) => onSearchField('fan_inlet_temp', v)} />
                  </div>
                  <div className="col-xs-6">
                    <FanNumberInput data-type="number" autoComplete="off" name="fan_inlet_design"
                      className="search_field fan_field"
                      value={s.fan_inlet_design}
                      onCommit={(v) => onSearchField('fan_inlet_design', v)} />
                  </div>
                </div>
              </FanRow>
            )}
          </div>

          <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
            {/* Plenum Discharge / System Effect — get_isp_esp drivers gated by
                fan type: discharge goes non-clickable for DWDI (type 4) and
                System Effect is hidden for everything else (tmpl:534/548). */}
            <FanRow side="right" rowId="discharge_id"
              rowClass={typeId === 4 ? 'non-clickable' : undefined}
              label={<span data-name="discharge_id"> Plenum Discharge</span>}>
              <select name="discharge_id" data-name="discharge_id"
                className="medium-input fan_field get_isp_esp"
                value={s.discharge_id == null ? '' : String(s.discharge_id)}
                disabled={typeId === 4}
                onChange={(e) => onGetIspField('discharge_id', e.target.value)}>
                {(s.discharge_id == null || s.discharge_id === '') && <option value="" />}
                {fanCmsOptionListByPath(fanDoc as FanCmsDoc, 'fan.plenum_discharge').map((r) => (
                  <option key={String(r.id)} value={String(r.id)}>{String(r.label ?? '')}</option>
                ))}
              </select>
            </FanRow>
            {typeId === 4 && (
              <FanRow side="right" rowId="system_effect_id"
                label={<span data-name="system_effect_id"> System Effect</span>}>
                <select name="system_effect_id" data-name="system_effect_id"
                  className="medium-input fan_field get_isp_esp"
                  value={s.system_effect_id == null ? '' : String(s.system_effect_id)}
                  onChange={(e) => onGetIspField('system_effect_id', e.target.value)}>
                  <option className="hide" value="" />
                  {systemEffect.map((r) => (
                    <option key={String(r.id)} value={String(r.id)}>{String((r as any).name ?? (r as any).value ?? r.id)}</option>
                  ))}
                </select>
              </FanRow>
            )}
            <FanSelectRow side="right" name="redundancy_id" label=" Redundancy"
              cls="search_field params_fan right-select" />
            <FanSelectRow side="right" name="speed_condition_id" label=" Speed Condition"
              cls="search_field params_fan right-select" />
            {/* dev's own 54px break before Altitude (tmpl:593). */}
            <FanRow side="right" unit="ft" rowStyle={{ marginTop: 54 }}
              label={<span data-name="elevation"> Altitude</span>}>
              <input type="text" name="elevation" data-name="elevation" data-type="number"
                data-formatter_type="decimal" autoComplete="off" tabIndex={-1}
                className="search_field project_field"
                readOnly={headerMode}
                value={elevationDisplay}
                onChange={(e) => onGetIspField('elevation', e.target.value)} />
            </FanRow>
            {/* Dev-sync J6 — the fan season block (fan.selector.tmpl:604-666):
                Season select (fan_section_field, per-CMS Fan options, no None
                row) + the Summer/Winter EADB/EAWB groups. Legacy renders both
                groups and toggles a `hide` class (summer hidden iff season 2,
                winter iff 1 — js:5035-5043); JSX gates the same predicate,
                keeping the season-group-* classes. Edits stage on
                sectionDraft; Select persists (commitFanChain). */}
            {(() => {
              const fanSeasons = cmsSeasonsByComponent(
                globalDoc as ICmsAhuDoc | undefined,
                'Fan'
              );
              const seasonSelectedId = getSeasonSelectedId();
              const showSummerGroup = seasonSelectedId !== 2;
              const showWinterGroup = seasonSelectedId !== 1;
              // tmpl:622-663 — the EADB/EAWB pair is two col-xs-6 halves, and
              // the unit is `unit right temp` so the global `.temp:before`
              // (common.css:21828) draws the degree mark: "°F".
              const pair = (season: 'summer' | 'winter', label: string) => (
                <div className={`season-group-${season}`}>
                  <FanRow side="right" unit="F" unitClass="unit right temp" label={
                    <>
                      <span data-name={`eadb_temp_${season}`}>{label} EADB</span>
                      {' / '}
                      <span data-name={`eawb_temp_${season}`}>EAWB</span>
                    </>
                  }>
                    <div className="row">
                      {/* Both halves floor at -40, not legacy's 0 for EAWB:
                          onFanSectionField clamps on every blur, edited or
                          not, and also nulls that season's LADB/LAWB — so a 0
                          floor turned a stored sub-zero wet bulb into 0.00. */}
                      {(
                        [
                          [`eadb_temp_${season}`, MIN_AIR_TEMP_F],
                          [`eawb_temp_${season}`, MIN_AIR_TEMP_F],
                        ] as Array<[string, number]>
                      ).map(([name, minV]) => (
                        <div className="col-xs-6" key={name}>
                          <input
                            type="text"
                            data-type="number"
                            autoComplete="off"
                            name={name}
                            data-min={minV}
                            data-step={0.01}
                            data-max={150}
                            className="search_field fan_section_field"
                            /* Decimal display, like every other
                               data-step="0.01" box in the dialog: legacy's
                               validateNumbers() formats these on render and
                               validateNumberField() writes the formatted text
                               back on `change` (js:8444-8500), so a stored 66
                               reads "66.00". The port showed the raw number. */
                            defaultValue={formatDecimalDisplay(sectionDraft.current[name])}
                            onBlur={(e) => {
                              const v = onFanSectionField(name, e.target.value, minV, 150);
                              e.currentTarget.value = formatDecimalDisplay(v);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </FanRow>
                </div>
              );
              return (
                <>
                  <FanRow side="right" label={<span data-name="season_id">Season</span>}>
                    <div className="row"><div className="col-xs-12">
                      <select
                        name="season_id"
                        className="medium-input fan_section_field"
                        value={seasonSelectedId == null ? '' : String(seasonSelectedId)}
                        onChange={(e) => {
                          sectionDraft.current = {
                            ...sectionDraft.current,
                            season_id: parseInt(e.target.value, 10) || null,
                          };
                          bump();
                        }}
                      >
                        {fanSeasons.map((r) => (
                          <option key={String(r.id)} value={String(r.id)}>
                            {String(r.name ?? '')}
                          </option>
                        ))}
                      </select>
                    </div></div>
                  </FanRow>
                  {showSummerGroup && pair('summer', 'Summer')}
                  {showWinterGroup && pair('winter', 'Winter')}
                </>
              );
            })()}
          </div>
          </div>
        </div>

        {/* ----- Pressure Drop (restored per dev 5fd0695113 —
             fan.selector.tmpl:603-627; data via loadPressureDrops js:7723.
             Pane classes mirror the Backbone tmpl so the legacy
             rating_mode.scss `.pressure-drop` styles apply). ----- */}
        <div
          className={`row ${tabClass('pressure_drop')} pressure-drop`}
          id="ahu_fan_pressure_drop"
          style={{ height: '100%' }}
        >
          <div
            className={`preloader dotted-preloader pressure-drop-preloader${pressureDropsLoading ? ' active' : ''}`}
            style={{ height: '100%', backgroundColor: '#f7f7f7' }}
          />
          {/* ★ THE PANE IS STATIC MARKUP ON DEV, NOT A RENDER OF THE RESPONSE.
              `loadPressureDrops` (js:8847-8903) mutates a table that the
              template already emitted (tmpl:667-689): it fills `.pressure-drop-
              rows`, then either `$empty.show()` + `$table/$summary.hide()` or
              the inverse. So the three states are:
                • never loaded  → table head + summary VISIBLE with blank cells,
                                  `.pressure-drop-empty` hidden (its CSS default,
                                  common.css:13697)
                • rows          → table + summary, populated
                • loaded, none  → table + summary hidden, empty message shown
              The port collapsed the FIRST state into the third's else-branch and
              then gated that on `pressureDrops != null`, so "never loaded"
              rendered nothing at all. That state is not an edge case: it is the
              whole of the HEADER rating popup, where `modelId` is 0 and dev's
              own `if (!fan_id) return` (js:8850) bails before touching the DOM
              — which is exactly the blank pane QA reported against dev's
              populated one. It is also every board fan before its fetch lands.
              `pdLoaded` is therefore the ONLY thing allowed to hide the table. */}
          <div className="col-xs-12">
            <div className="pressure-drop-air-stream-name">{pressureDrops?.name ?? ''}</div>
            {(() => {
              const pdRows = pressureDrops?.rows ?? [];
              const pdLoaded = pressureDrops != null;
              // js:8879 — `if (!rows.length || !resp.air_stream)` is the hide
              // test, and it is only ever reached once a response has landed.
              const pdEmpty = pdLoaded && !(pdRows.length && pressureDrops?.air_stream);
              const hidden = pdEmpty ? ({ display: 'none' } as const) : undefined;
              return (
                <>
                  <table className="pressure-drop-table" style={hidden}>
                    <thead>
                      <tr>
                        <th>TUNNEL : SECTION</th>
                        <th>COMPONENT / ACCESSORY : TAG</th>
                        <th>APD (in WC)</th>
                      </tr>
                    </thead>
                    <tbody className="pressure-drop-rows">
                      {pdRows.map((r, i) => (
                        <tr key={i}>
                          <td>{`${r.tunnel_index ?? ''} : ${r.section_name ?? ''}`}</td>
                          <td>{`${r.name ?? ''}${r.tag ?? ''}`}</td>
                          <td style={{ textAlign: 'right' }}>{pdFmt(r.apd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* js:8901-8903 writes ISP/ESP/TSP OUTSIDE the if/else, so the
                      summary always carries the latest values — blank until a
                      response lands. */}
                  <div className="pressure-drop-summary" style={hidden}>
                    <table className="pressure-drop-summary-table">
                      <tbody>
                        <tr><td>Internal Static Pressure</td><td className="pressure-drop-isp">{pdFmt(pressureDrops?.isp)}</td></tr>
                        <tr><td>External Static Pressure</td><td className="pressure-drop-esp">{pdFmt(pressureDrops?.esp)}</td></tr>
                        <tr><td>Total Static Pressure</td><td className="pressure-drop-tsp">{pdFmt(pressureDrops?.tsp)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  {/* `.pressure-drop-empty` is `display:none` by default and is
                      only `$empty.show()`n on the no-rows path. */}
                  {pdEmpty && (
                    <div className="pressure-drop-empty" style={{ display: 'block' }}>
                      No air stream assigned to any component/accessory.
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* ----- Motor (fan.selector.tmpl:963-1238 — "motor" + "motor base"
             sub-tabs) ----- */}
        <div className={`row ${tabClass('motor')} motor each-tabs`} id="ahu_fan_motor">
          {/* "Available Motors: N" (tmpl:964-974) — dev nests it in its own
              sub-tab-menu block and hangs `no-avaible-data` (dev's spelling) on
              it at zero, which turns it red. The port kept the span but never
              filled it; the count now comes from the same motors/all/count
              query legacy's getMotorCount runs. */}
          <div className="col-xs-12 sub-tab-menu nav nav-tabs">
            <div className="">
              <div className="col-xs-12">
                <ul className="col-xs-12">
                  <div className={`available_motors${availableMotorCount === 0 ? ' no-avaible-data' : ''}`}>
                    Available Motors: <span id="available_motors_count">
                      {availableMotorCount == null ? '' : availableMotorCount}
                    </span>
                  </div>
                </ul>
              </div>
            </div>
          </div>
          {/* Selector-parity FAN restore — the sub-tab menu. Shown only when the
              motor-base applies (mtr_base_series has options); legacy carries
              `invisible-tabs` otherwise (js:2013-2023). The `.motor-menu` +
              `invisible-tabs` classes ALSO drive the motorBaseVisible save gate
              (doSelect reads `.motor-menu.invisible-tabs`, js:7183). */}
          <div
            className={`col-xs-12 sub-tab-menu nav nav-tabs motor-menu${motorBaseApplicable ? '' : ' invisible-tabs'}`}
          >
            {/* dev nests the list two divs deep, the inner one capped at 14px
                with the `ul` pushed 20px down (tmpl:976-978). Both wrappers and
                both inline heights are what make the hidden state occupy dev's
                spacing — `invisible-tabs` is opacity/pointer-events only
                (common.css:19549), so the row still takes its place in flow.
                Do NOT swap it for `display: none`: that collapses the gap and
                the Motor fields ride up relative to dev. */}
            <div className="">
              <div className="col-xs-12" style={{ height: '14px' }}>
                <ul className="col-xs-12 text-right more_tabing" style={{ marginTop: '20px' }}>
                  <li className={`properties_more properties-tab${motorSubTabActive === 'motor' ? ' active in' : ''}`}>
                    <a tabIndex={-1} onClick={() => setMotorSubTab('motor')}>{'motor\u00a0\u00a0'}</a>
                  </li>
                  <li className={`motor_base_more motor_base-tab${motorSubTabActive === 'motor_base' ? ' active in' : ''}`}>
                    <a tabIndex={-1} onClick={() => setMotorSubTab('motor_base')}>{'motor base\u00a0'}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="more-tab-content">
            {/* --- "motor" sub-tab (#properties_more) --- */}
            <div className={`tab-pane fade scrollable-content float-left${motorSubTabActive === 'motor' ? ' in active' : ''}`}
              id="properties_more">
              {/* LEFT — dev labels these plainly: Type / Manufacturer / Series,
                  not "Motor Type" / "Motor Manufacturer" / "Motor Series"
                  (tmpl:992-1063). Rows are `left-column hiddenIfNecessary`. */}
              <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
                {(([
                  ['motor_type_id', 'Type', undefined],
                  ['motor_mfg_id', 'Manufacturer', undefined],
                  ['motor_series_id', 'Series', 'series'],
                  // Annexair: Efficiency / Enclosure / Housing come with the
                  // catalogued motor, so the rows are not rendered (see
                  // `isAnnexair`). The state values are untouched.
                  ['motor_efficiency_id', 'Efficiency', undefined],
                  ['motor_enclosure_id', 'Enclosure', undefined],
                  ['motor_housing_id', 'Housing', undefined],
                ] as Array<[string, string, string | undefined]>).filter(
                  ([name]) =>
                    !isAnnexair ||
                    !['motor_efficiency_id', 'motor_enclosure_id', 'motor_housing_id'].includes(name)
                )).map(([name, label, lk]) => (
                  <MotorRow key={name} rowClass="left-column hiddenIfNecessary"
                    label={
                      <span data-name={name} className={docFieldHasOptions(name) ? undefined : 'non-clickable'}>
                        {label}
                      </span>
                    }>
                    <BareDocSelect name={name} labelKey={lk}
                      cls="search_field params_fan has_changable_default motor_params" />
                  </MotorRow>
                ))}
              </div>
              {/* RIGHT — `right-column` sits on the COLUMN and the rows split
                  col-xs-4 / col-xs-8 (tmpl:1067-1178). */}
              <div className="col-lg-6 col-xs-12 right-column" style={{ height: '100%', float: 'left' }}>
                {!isAnnexair && (
                  <MotorRow narrow
                    label={<span data-name="motor_power">Power</span>}
                    unit={
                      /* dev swaps HP/KW by motor type (tmpl:1072-1075). */
                      <>
                        <span className={motorIsKw ? 'for-AC hide' : 'for-AC'}>HP</span>
                        <span className={motorIsKw ? 'for-EC' : 'for-EC hide'}>KW</span>
                      </>
                    }>
                    <BareMasterSelect name="motor_power"
                      cls="getMotorCount motor_params search_field params_fan has_changable_default" />
                  </MotorRow>
                )}
                {/* Dev-sync H — the ANNEXAIR-only motor Safety % input
                    (tmpl:1084-1095 `.only-for-annexair`, right after Power). */}
                {mfg == 237 && (
                  <MotorRow narrow rowClass="only-for-annexair" unit="%"
                    label={<span data-name="mtr_safety">Safety</span>}>
                    {/* data-type="number" — dev-sync I: the merged common.css adds
                        `input[data-type=number][name=mtr_safety] { text-align:
                        left; }`; the legacy input carries the attribute. */}
                    <FanNumberInput name="mtr_safety" data-name="mtr_safety" data-type="number"
                      formatter="integer" autoComplete="off"
                      data-min={0} data-max={20} data-step={1}
                      className="search_field fan_field"
                      value={s.mtr_safety}
                      onCommit={(v) => onSearchField('mtr_safety', v)} />
                  </MotorRow>
                )}
                {!isAnnexair && (
                  <MotorRow narrow unit="RPM"
                    label={<span data-name="motor_speed_nominal_id">Nominal Speed</span>}>
                    <BareDocSelect name="motor_speed_nominal_id" labelKey="value"
                      cls="params_fan has_changable_default getMotorCount motor_params search_field" />
                  </MotorRow>
                )}
                {/* Voltage (tmpl:1110-1128) — a `.fan_field` feeding the motor
                    rating; options are the FULL fans-doc fan.motor.voltage list
                    (no cascade narrowing). */}
                <MotorRow narrow label={<span data-name="motor_voltage_id">Voltage</span>}>
                  <DocPathSelect bare doc={fanDoc as FanCmsDoc} s={s} graph={graph}
                    name="motor_voltage_id" path="fan.motor.voltage"
                    className="search_field fan_field getMotorCount" onChange={onSearchField} />
                </MotorRow>
                {!isAnnexair && (
                  <MotorRow narrow
                    label={
                      <span data-name="motor_shaft_grounding_id"
                        className={masterHasOptions('motor_shaft_grounding_id') ? undefined : 'non-clickable'}>
                        Shaft Grounding
                      </span>
                    }>
                    <BareMasterSelect name="motor_shaft_grounding_id"
                      cls="search_field params_fan has_changable_default motor_params getMotorCount" />
                  </MotorRow>
                )}
                {/* Position (tmpl:1141-1152) — the type-dependent fans-doc
                    fan.motor_position.{dwdi_arr3|plenum_arr3} list. Greys itself
                    when that list comes back empty, as it does on dev. */}
                {!isAnnexair && (
                  <MotorRow narrow
                    label={
                      <span data-name="motor_position_id"
                        className={
                          graph.optionIds('motor_position_id')?.length === 0 ? 'non-clickable' : undefined
                        }>
                        Position
                      </span>
                    }>
                    <DocPathSelect bare doc={fanDoc as FanCmsDoc} s={s} graph={graph}
                      name="motor_position_id"
                      path={fanMotorPositionPath(s.type_id as number | string | null | undefined)}
                      labelKey="value" className="search_field params_fan motor_params"
                      onChange={onSearchField} />
                  </MotorRow>
                )}
                <MotorRow narrow
                  label={
                    <span data-name="mtr_controller_id"
                      className={masterHasOptions('mtr_controller_id') ? undefined : 'non-clickable'}>
                      Controller
                    </span>
                  }>
                  <BareMasterSelect name="mtr_controller_id"
                    cls="search_field fan_field has_changable_default" />
                </MotorRow>
                {!isAnnexair && (
                  <MotorRow narrow
                    label={
                      <span data-name="mtr_supplied_by_id"
                        className={masterHasOptions('mtr_supplied_by_id') ? undefined : 'non-clickable'}>
                        Supplied by
                      </span>
                    }>
                    <BareMasterSelect name="mtr_supplied_by_id"
                      cls="search_field fan_field has_changable_default motor_params" />
                  </MotorRow>
                )}
              </div>
            </div>

            {/* --- "motor base" sub-tab (#motor_base_more) — Selector-parity FAN
                 restore of the WHOLE sub-tab (fan.selector.tmpl:1180-1236),
                 absent in the port. mtr_base_* resolve from the motor-base CMS
                 doc (loader collection/motor_base.js); Supplied by is a master
                 list. Saved via motor_base_params (buildFanSelectPayload). --- */}
            <div className={`tab-pane fade scrollable-content float-left${motorSubTabActive === 'motor_base' ? ' in active' : ''}`}
              id="motor_base_more">
              {/* tmpl:1181-1235 — same column split as the motor sub-tab:
                  col-xs-5/7 on the left, col-xs-4/8 in the right column. */}
              <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
                {([
                  ['mtr_base_mfg_id', 'motor_base.manufacturer', 'Manufacturer', undefined],
                  ['mtr_base_series_id', 'motor_base.series', 'Series', 'series'],
                  ['mtr_base_mtl_id', 'motor_base.mtl', 'Material', undefined],
                ] as Array<[string, string, string, string | undefined]>).map(([name, path, label, lk]) => (
                  <MotorRow key={name} rowClass="left-column hiddenIfNecessary"
                    label={<span data-name={name}>{label}</span>}>
                    <DocPathSelect bare doc={motorBaseDoc as FanCmsDoc} s={s} graph={graph}
                      name={name} path={path} labelKey={lk}
                      className="search_field params_fan motor_base_params" onChange={onSearchField} />
                  </MotorRow>
                ))}
              </div>
              <div className="col-lg-6 col-xs-12 right-column" style={{ height: '100%', float: 'left' }}>
                <MotorRow narrow
                  label={
                    <span data-name="mtr_base_supplied_by_id"
                      className={masterHasOptions('mtr_base_supplied_by_id') ? undefined : 'non-clickable'}>
                      Supplied by
                    </span>
                  }>
                  <BareMasterSelect name="mtr_base_supplied_by_id" cls="search_field fan_field" />
                </MotorRow>
              </div>
            </div>
          </div>
        </div>

        {/* ----- VFD (fan.selector.tmpl 1148-1565; pane classes mirror the
             Backbone template so the global legacy CSS styles it) ----- */}
        {/* ★ LEGACY-CSS-SELECTOR MISS — the VFD pane was the one fan pane that
            never got the inner `.scrollable-content` wrapper the other eight
            carry, so every rule in dev's VFD stylesheet was a no-op here.
            dev's chain is `.per-fan .each-tabs .scrollable-content …`
            (common.css:17826-18009): `each-tabs` is the tab HOST
            (`#report-outputs-group`, fan.selector.tmpl:55) and
            `scrollable-content` is the PANE, so they are ancestor/descendant.
            The port puts `each-tabs` on the pane itself (see the Fan pane above
            for why it cannot go on the host), which collapses the two onto ONE
            element — `.each-tabs .scrollable-content` then matches nothing.
            Only `.per-fan .each-tabs select` (:17822) survived, which is why the
            selects looked right and everything else did not: the left/right
            column row padding (:17826-17837), `.right-select` width (:17838)
            and the whole `.vfd-rating-right` block (:17841-18009) were all
            dead. Visible symptoms: Bank W×H, Housed-in-Enclosure W×H, To
            Floor|Roof and Spacing Hor×Ver stacked vertically instead of sitting
            inline (`.vfd-paired-fields` / `.vfd-enclosure-row` /
            `.vfd-enc-dims` are all `display:flex` in that block), which
            overflowed the pane's 363px cap.
            `mt-0` is dev's own opt-out for
            `.coil_new .content-data .scrollable-content:first-child
             { margin-top: 33px }` (:15869) — a coil-dialog rule that only
            matches because the React tab host carries `content-data`, which
            dev's `#report-outputs-group` does not. Without it the wrapper's
            33px collapses through the pane and beats the 23px in
            ahuBoardOverrides §5 that reproduces dev's host padding. */}
        <div className={`row ${tabClass('vfd')} each-tabs`} id={`${idPrefix}VFD`} style={{ maxHeight: 363 }}>
          <div className="scrollable-content mt-0">
          {/* In HEADER mode `detail` is undefined (no current_model), so
              FanVfdTab's init effect (`if (!detail || !vfdCms) return`,
              FanVfdTab.tsx:633) never runs and every downstream effect
              early-returns on `initedFor` — an inert pane with an empty section
              dropdown, exactly like legacy (js:8634-8638).

              `selectedMotor={motorRow}` is the motor the VFD is sized against:
              `motor_choosen || selectedMotorData`, the same pair
              `vfdTabDisabled` gates the tab on and the prop has always
              documented. Passing the SAVED motor alone meant that on the normal
              rate → pick a motor → configure the VFD flow there was no motor row
              yet, so the smartlist asked by_motor with no `motor_fla` and the
              backend applied no sizing floor at all — offering drives rated
              below the motor (reported 2026-10-03). */}
          <FanVfdTab
            refs={{
              vfd: vfdRef,
              vfdPosition: vfdPositionRef,
              vfdParams: vfdParamsRef,
              vfdQtyTotal: vfdQtyTotalRef,
            }}
            ahuId={(ahu?.id as number | undefined) ?? null}
            idPrefix={idPrefix}
            fanComponentId={modelId}
            detail={detail as Record<string, any> | undefined}
            state={state}
            selectedMotor={motorRow}
            section={(activate.section as { id?: number; label?: string | null; tag?: string | null } | null) ?? null}
            hasVfd={Boolean(s.has_vfd)}
            setHasVfd={setHasVfd}
            motorSupportsVfd={motorSupportsVfd}
            motorTypeKnown={motorTypeKnown}
            tabDisabled={vfdTabDisabled}
            isActive={inputTab === 'vfd'}
            // qty_wide*qty_high || the chosen rated row's array || model
            // fan_qty || 1 — see `vfdFanQty` above; always >0 so the by_motor
            // call carries fan_qty even for a 1x1 fan.
            fanQty={vfdFanQty}
            bump={bump}
          />
          </div>
        </div>

        {/* ----- Isolation (fan.selector.tmpl:1658-1742) -----
            dev's order is Type · Supplied by · Manufacturer · Series · Desired
            Deflection · Mounting, all in the LEFT column (dev's right column is
            an empty placeholder). The port led with Supplied by, called the last
            row "Mounting Method", and dropped both the `in` unit on Desired
            Deflection and the `top-margin` break above Mounting. */}
        <div className={`row ${tabClass('isolation')} each-tabs`} id="ahu_fan_isolation">
          <div className="scrollable-content mt-0">
            <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
              <FanRow side="left" label={<span data-name="isolator_type_id">Type</span>}>
                <DocPathSelect bare doc={isolatorDoc as FanCmsDoc} s={s} graph={graph}
                  name="isolator_type_id" path="spring_isolator.type"
                  className="search_field fan_field si_params has_changable_default"
                  onChange={onSearchField} />
              </FanRow>
              <FanRow side="left" label={
                <span data-name="fan_isolators_supplied_by_id"
                  className={masterHasOptions('fan_isolators_supplied_by_id') ? undefined : 'non-clickable'}>
                  Supplied by
                </span>
              }>
                <BareMasterSelect name="fan_isolators_supplied_by_id"
                  cls="si_params search_field has_changable_default fan_field" />
              </FanRow>
              <FanRow side="left" label={<span data-name="isolator_mfg_id">Manufacturer</span>}>
                <DocPathSelect bare doc={isolatorDoc as FanCmsDoc} s={s} graph={graph}
                  name="isolator_mfg_id" path="spring_isolator.manufacturer"
                  className="search_field fan_field si_params has_changable_default"
                  onChange={onSearchField} />
              </FanRow>
              <FanRow side="left" label={<span data-name="isolator_series_id">Series</span>}>
                <DocPathSelect bare doc={isolatorDoc as FanCmsDoc} s={s} graph={graph}
                  name="isolator_series_id" path="spring_isolator.series" labelKey="series"
                  className="search_field fan_field si_params has_changable_default"
                  onChange={onSearchField} />
              </FanRow>
              <FanRow side="left" unit="in"
                label={<span data-name="specified_deflection">Desired Deflection</span>}>
                <DocPathSelect bare doc={isolatorDoc as FanCmsDoc} s={s} graph={graph}
                  name="specified_deflection" path="spring_isolator.specified_deflection"
                  className="si_params search_field params_fan has_changable_default right-select settings_fan"
                  onChange={onSearchField} />
              </FanRow>
              <FanRow side="left" rowClass="top-margin" label={
                <span data-name="mounting_method_id"
                  className={masterHasOptions('mounting_method_id') ? undefined : 'non-clickable'}>
                  Mounting
                </span>
              }>
                <BareMasterSelect name="mounting_method_id"
                  cls="si_params search_field params_fan has_changable_default fan_field" />
              </FanRow>
            </div>
            <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
              <div className="col-xs-12 right-column" />
            </div>
          </div>
        </div>

        {/* ----- Mounting (fan.selector.tmpl:692-859) -----
            LEFT  Mounting · Orientation · Housing Discharge
            RIGHT the spacing pair and the four assembly/bulkhead distances.
            dev packs the two spacing rows and Assembly Height into ONE
            `col-xs-12 right-column` as loose col-xs-5/col-xs-7 halves
            (tmpl:744-804) rather than three rows — reproduced as-is, because
            that is what makes the spacing selects sit 8/4 against dev's grid. */}
        <div className={`row ${tabClass('mounting')} each-tabs`} id="ahu_fan_mounting">
          <div className="scrollable-content mt-0">
          <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
            {/* Mounting renders from the `fan_mountings` collection (a
                MasterSelect over the rows loadParams returns) so the option text
                is "Horizontal", not the raw id — legacy's
                CUSTOM_COLLECTIONS.mounting_id (js:2099). `mounting_id` is NOT in
                FAN_CMS_PATHS, so the doc select this used to be on the board had
                an EMPTY option list: it greyed itself out and fell through to the
                saved-value fallback option, printing the bare id. */}
            <FanRow side="left" label={<span data-name="mounting_id"> Mounting</span>}>
              <BareMasterSelect name="mounting_id" cls="search_field fan_field right-select" />
            </FanRow>
            <FanRow side="left" rowId="orientation"
              label={<span data-name="orientation_id"> Orientation</span>}>
              <BareDocSelect name="orientation_id" cls="search_field fan_field right-select" />
            </FanRow>
            {/* dev-parity — "Housing Discharge" belongs to Mounting, not the Fan
                tab, and dev nests it one level deeper (tmpl:725-736). */}
            <FanRow side="left" label={
              <span data-name="housed_discharge_id"
                className={docFieldHasOptions('housed_discharge_id') ? undefined : 'non-clickable'}>
                Housing Discharge
              </span>
            }>
              <div className="row"><div className="col-xs-12">
                <BareDocSelect name="housed_discharge_id"
                  cls="search_field params_fan has_changable_default" />
              </div></div>
            </FanRow>
          </div>

          <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
            {/* ===== the spacing pair + Assembly Height, all inside ONE row div
                (tmpl:744-804). Spacing shows for plenum/plug (type 1/2 —
                checkSpacing js:8896-8910) and goes non-clickable off mounting 1;
                the U/D number is editable only when the select is "User"
                (id 8, tmpl:764/789). ===== */}
            <div className="col-xs-12 right-column">
              {(typeId === 1 || typeId === 2) && (
                <>
                  <div className="col-xs-5 for-special-type">
                    <div className="row label-part">
                      <span data-name="fan_spacing_sides_id"> Spacing on Sides </span>
                      <span data-name="fan_spacing_ud_sides" />
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 for-special-type">
                    <div className="row"><div className="col-xs-12"><div className="row">
                      <div className="col-xs-8">
                        <select name="fan_spacing_sides_id" data-name="fan_spacing_sides"
                          data-connected-input="fan_spacing_ud_sides"
                          className="fan_field search_field" disabled={mountingId !== 1}
                          value={s.fan_spacing_sides_id == null ? '' : String(s.fan_spacing_sides_id)}
                          onChange={(e) => onSearchField('fan_spacing_sides_id', e.target.value)}>
                          {(s.fan_spacing_sides_id == null || s.fan_spacing_sides_id === '') && <option value="" />}
                          {spacingSides.map((r) => (
                            <option key={String(r.id)} value={String(r.id)} data-value={String((r as any).value ?? r.id)}>
                              {String((r as any).name ?? (r as any).value ?? r.id)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xs-4">
                        <FanNumberInput name="fan_spacing_ud_sides" data-min={0} data-max={100}
                          autoComplete="off" style={{ maxWidth: '100%' }}
                          className={`fan_field search_field${Number(s.fan_spacing_sides_id) !== 8 ? ' non-clickable' : ''}`}
                          disabled={Number(s.fan_spacing_sides_id) !== 8}
                          value={s.fan_spacing_ud_sides}
                          onCommit={(v) => onSearchField('fan_spacing_ud_sides', v)} />
                      </div>
                    </div></div></div>
                  </div>
                  <div className="col-xs-5 for-special-type">
                    <div className="row label-part">
                      <span data-name="fan_spacing_top_btm_id"> Spacing Top &amp; Bottom </span>
                      <span data-name="fan_spacing_ud_top_btm" />
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className={`col-xs-7 for-special-type${mountingId === 3 ? ' hide' : ''}`}>
                    <div className="row"><div className="col-xs-12"><div className="row">
                      <div className="col-xs-8">
                        <select name="fan_spacing_top_btm_id" data-name="fan_spacing_top_btm"
                          data-connected-input="fan_spacing_ud_top_btm"
                          className="fan_field search_field" disabled={mountingId !== 1}
                          value={s.fan_spacing_top_btm_id == null ? '' : String(s.fan_spacing_top_btm_id)}
                          onChange={(e) => onSearchField('fan_spacing_top_btm_id', e.target.value)}>
                          {(s.fan_spacing_top_btm_id == null || s.fan_spacing_top_btm_id === '') && <option value="" />}
                          {spacingTopBtm.map((r) => (
                            <option key={String(r.id)} value={String(r.id)} data-value={String((r as any).value ?? r.id)}>
                              {String((r as any).name ?? (r as any).value ?? r.id)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xs-4">
                        <FanNumberInput name="fan_spacing_ud_top_btm" data-min={0} data-max={100}
                          autoComplete="off" style={{ maxWidth: '100%' }}
                          className={`fan_field search_field${Number(s.fan_spacing_top_btm_id) !== 8 ? ' non-clickable' : ''}`}
                          disabled={Number(s.fan_spacing_top_btm_id) !== 8}
                          value={s.fan_spacing_ud_top_btm}
                          onCommit={(v) => onSearchField('fan_spacing_ud_top_btm', v)} />
                      </div>
                    </div></div></div>
                  </div>
                </>
              )}
              <div className={`col-xs-5${mountingId === 3 ? ' hide' : ''}`} id="assy_height_from_floor">
                <div className="row label-part">
                  <span data-name="assy_height_from_floor"> Assembly Height from floor</span>
                  <span className="unit right">in</span>
                </div>
              </div>
              <div className="col-xs-7">
                <FanNumberInput autoComplete="off" className="col-xs-12 fan_field search_field"
                  name="assy_height_from_floor"
                  value={s.assy_height_from_floor}
                  onCommit={(v) => onSearchField('assy_height_from_floor', v)} />
              </div>
            </div>

            {/* the three remaining distances are each their own right-column row
                (tmpl:807-857), gated by updateMounting (js:4684-4705) +
                updateOrientation (js:4707-4716). */}
            <FanRow side="right" unit="in" rowId="assy_distance_from_ceiling"
              rowClass={mountingId === 3 ? undefined : 'hide'}
              label={<span data-name="assy_distance_from_ceiling">Distance from Ceiling</span>}>
              <FanNumberInput autoComplete="off" className="col-xs-12 fan_field search_field"
                name="assy_distance_from_ceiling"
                value={s.assy_distance_from_ceiling}
                onCommit={(v) => onSearchField('assy_distance_from_ceiling', v)} />
            </FanRow>
            <FanRow side="right" unit="in" rowId="assy_distance_from_section_ups"
              rowClass={
                (typeId === 4
                  ? Number(s.housed_discharge_id) === 3 || Number(s.housed_discharge_id) === 4
                  : mountingId !== 1)
                  ? undefined
                  : 'hide'
              }
              label={<span data-name="assy_distance_from_section_ups">Distance from Section Ups</span>}>
              <FanNumberInput autoComplete="off" className="col-xs-12 fan_field search_field"
                name="assy_distance_from_section_ups"
                value={s.assy_distance_from_section_ups}
                onCommit={(v) => onSearchField('assy_distance_from_section_ups', v)} />
            </FanRow>
            <FanRow side="right" unit="in" rowId="bulkhead_wall_to_ahu_side_wall"
              rowClass={orientationId !== 1 && orientationId !== 3 ? undefined : 'hide'}
              label={<span data-name="bulkhead_wall_to_ahu_side_wall">Distance to Side Wall</span>}>
              <FanNumberInput autoComplete="off" className="col-xs-12 fan_field search_field"
                name="bulkhead_wall_to_ahu_side_wall"
                value={s.bulkhead_wall_to_ahu_side_wall}
                onCommit={(v) => onSearchField('bulkhead_wall_to_ahu_side_wall', v)} />
            </FanRow>
            {/* ⚠ NO "Available Combinations" button here. dev's Mounting pane
                ends at Distance to Side Wall (fan.selector.tmpl:857-859) — the
                word "combinations" does not appear in its template at all, and
                the JS that would have mounted the popup is COMMENTED OUT
                (fan.selector.js:8427-8440). The port added an opener, so dev3
                showed a control dev does not have. The popup component itself
                is kept (see CombinationsPopup below) but is now unreachable,
                exactly as on dev. */}
          </div>
          </div>
        </div>

        {/* ----- Options (fan.selector.tmpl:860-962) -----
            Restored as its own pane; the port had folded these five into
            Mounting and dropped the nav item. Inlet Vanes and Discharge
            Diffuser stay out — they are commented out on dev (tmpl:902-927). */}
        <div className={`row ${tabClass('options')} each-tabs`} id="ahu_fan_options">
          <div className="scrollable-content mt-0">
            {(() => {
              // ★ A select with NO available values is DROPPED from the pane,
              // not greyed (Anna, 2026-10-03). dev greys them — its
              // `checkSelectboxOptions` adds `.non-clickable` to the label and
              // the control — which left New York Blower with five dead rows,
              // since it has no data in ANY of the five `fan_mfg_series/*`
              // tables. `optionsRows` is FAN_OPTIONS_ROWS in dev's own order
              // with the unavailable ones removed; they are then dealt down the
              // LEFT column first, so the order reads the same and neither
              // column is left with a hole where a hidden row was. With all six
              // available the split is dev's own 3 | 3, and when NONE are the
              // nav item is greyed so the pane is never reached empty.
              //
              // Finish is the one of the six that is not a guard/AFS list: its
              // options are the MasterAHUFanMfgSeriesFinish rows for the picked
              // Mfg + Series, and each vendor's handler narrows KSDM's own
              // catalog rows by the pick (integrated_parts/fan/finish.py).
              const half = Math.ceil(optionsRows.length / 2);
              const columns: Array<['left' | 'right', Array<[string, string]>]> = [
                ['left', optionsRows.slice(0, half)],
                ['right', optionsRows.slice(half)],
              ];
              return columns.map(([side, items]) => (
                <div key={side} className="col-lg-6 col-xs-12"
                  style={{ height: '100%', float: 'left' }}>
                  {items.map(([name, label]) => (
                    <FanRow key={name} side={side}
                      label={<span data-name={name}>{label}</span>}>
                      <BareMasterSelect name={name}
                        cls="search_field params_fan right-select options-select" />
                    </FanRow>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>

        {/* ----- Pricing (fan.selector.tmpl:1744-1830 + the two injected
             *_pricing_method_inputs.tmpl partials) -----
             dev stacks BOTH methods in the SINGLE left column, labelled "Fan
             Pricing Method" / "Motor Pricing Method", each a col-xs-4 / col-xs-8
             split; the port put them side by side under invented "Fan : Pricing
             Method" labels. The options come from the real pricing_methods
             collection, not three hardcoded rows. When a method is not "By
             Application" dev injects a Mtl | Freight PAIR on one row (labelled
             "Fan :" and "Mtr :"), where the port emitted two separate rows. */}
        <div className={`row ${tabClass('pricing')} pricing_fan each-tabs`} id="ahu_fan_pricing">
          <div className="scrollable-content mt-0">
          <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
            {(() => {
              const methods = (pricingMethodsQ.data as OptionRow[] | undefined) ?? [];
              const methodRow = (name: string, label: string, cls: string) => (
                <MotorRow narrow label={<span data-name={name}>{label}</span>}>
                  <select name={name} data-name={name} className={`col-xs-11 fan_field ${cls}`}
                    value={s[name] == null ? '' : String(s[name])}
                    onChange={(e) => onModelField(name, e.target.value)}>
                    {methods.map((r) => (
                      <option key={String(r.id)} value={String(r.id)} data-value={String((r as any).value ?? r.id)}>
                        {String((r as any).name ?? (r as any).value ?? r.id)}
                      </option>
                    ))}
                  </select>
                </MotorRow>
              );
              /** the injected Mtl | Freight pair — method 2 writes the COST
               *  columns, anything else the NET PRICE ones. */
              const pair = (prefix: 'Fan' | 'Mtr', method: number, costA: string, costB: string,
                            netA: string, netB: string) => {
                const [a, b] = method === 2 ? [costA, costB] : [netA, netB];
                return (
                  <div className="col-xs-12">
                    <div className="left-column">
                      <div className="col-xs-4">
                        <div className="row label-part">
                          <span>{prefix} : <span data-name={a}>Mtl</span> | <span data-name={b}>Freight</span>
                            <span className="unit right">US$</span>
                          </span>
                        </div>
                      </div>
                      <div className="col-xs-8">
                        <div className="row">
                          {[a, b].map((n) => (
                            <div className="col-xs-6" key={n}>
                              <FanNumberInput data-type="number" autoComplete="off"
                                style={{ width: '100%' }} data-min={1} data-max={1000000} data-step={0.01}
                                className="model_field fan_field" name={n}
                                value={s[n]}
                                onCommit={(v) => onModelField(n, v)} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              };
              const fanMethod = Number(s.pricing_method_id);
              const motorMethod = Number(s.motor_pricing_method_id);
              return (
                <>
                  {methodRow('pricing_method_id', 'Fan Pricing Method', 'fan_pricing_method_field')}
                  <div className="col-xs-12 fanPricingInputs">
                    {fanMethod !== 1 && pair('Fan', fanMethod,
                      'cost_product_user', 'cost_freight_user',
                      'net_price_product_user', 'net_price_freight_user')}
                  </div>
                  {methodRow('motor_pricing_method_id', 'Motor Pricing Method', 'motor_pricing_method_field')}
                  <div className="col-xs-12 motorPricingInputs">
                    {motorMethod !== 1 && pair('Mtr', motorMethod,
                      'motor_cost_product_user', 'motor_cost_freight_user',
                      'motor_net_price_product_user', 'motor_net_price_freight_user')}
                  </div>
                </>
              );
            })()}
          </div>
          </div>
        </div>
      </div>

      {/* ===== OUTPUT (rated outputs + FAN CURVE CANVAS) =====
          dev ships this div with `class="hidden"` (fan.selector.tmpl:1845 —
          `.hidden { display:none !important }`, common.css:29073) and only
          `removeClass("hidden")` once a rating has actually rendered
          (js:3220). Every path that invalidates the rating puts it straight
          back — a params change, a reset, a manufacturer swap
          (js:2483 / 4736 / 5049 / 5322 / 5361).

          The port rendered it unconditionally, so an UNRATED fan showed an
          empty output section under the inputs, and its height is what made
          the dialog scroll. `ratedRows == null` is the port's own "no rating
          rendered" signal — it already gates the VFD tab, and every
          rate-invalidating edit clears it — so it toggles on the same
          condition dev does. */}
      {/* ★ `fan-rating-output` is dev's own class on this div
          (fan.selector.tmpl:1845 — `class="hidden col-xs-12 fan-rating-output"`)
          and it is the OTHER half of the dialog's height model:
            #for-output-rendering               { height: calc(100% - 295px) !important }  (:21738)
            #for-output-rendering.fan-rating-output
                                                { height: calc(100% - 373px) !important;
                                                  max-height: 49% }                        (:21759)
          Without it the fan output fell through to the generic 295px rule and
          came out 529px against dev's 403.76px, which — stacked under an input
          host that was itself the full 824px — put the whole rated table 10px
          BELOW the dialog's bottom edge. `fan` is an inert hook here: there is
          no `#for-output-rendering.fan` rule (only `.gas_heater` and
          `.fan-rating-output`), so it is kept and the real one added.

          ⚠ `output-part` is deliberately NOT in this list, and re-adding it
          brings back a scrollbar on every rated fan. dev's class list is
          exactly `hidden col-xs-12 fan-rating-output` (tmpl:1845) — the
          `output-part` in this subtree belongs to the ROW three levels down
          (output.tmpl:39, rendered below). With it on the container as well,
          `.per-fan .output-part { margin-top: 10px }` (common.css:18037)
          matched TWICE, and dev's height model has no slack for the second
          one: the `373px` in the rule above is the input pane's
          `min-height: 363px` plus exactly that one 10px margin, so
          363 + 10 + (100% - 373) fills the host to the pixel. The extra
          margin pushed the output 10px down, the content ran past
          `#fan-selector`'s bottom edge — it is `overflow: auto`
          (`.ahu-rate-content-item #rating_mode_fan_content > div`, :13825) —
          and the dialog grew a vertical scrollbar the moment a rating drew.
          Measured on dev3 at 1600x1000: scrollHeight 890 against clientHeight
          888 with the class, 888/888 without, and nothing else in the output
          subtree moves but that 10px. */}
      <div
        id="for-output-rendering"
        className={`fan fan-rating-output col-xs-12${ratedRows == null ? ' hidden' : ''}`}
      >
        {/* ★ dev's OUTPUT WRAPPER CHAIN (fan.selector.output.tmpl:6-7, :39).
            Same defect class as the energy-wheel fix: a Backbone view's
            template root is a wrapper, and collapsing it turns every
            descendant rule into a silent no-op.
            ┌ #for-output-rendering
            │ └ .coil_new.per-fan.fan-rating-mode.full-height   (tmpl:6)
            │   └ .full-height.for-rating.for_rating            (tmpl:7)
            │     └ .output-part.row.choosen                    (tmpl:39)
            │       ├ .col-xs-1.nav.nav-tabs > .row > ul.col-xs-12.ouptut_tubs
            │       └ .col-xs-11.output-with-border             (tmpl:70)
            The strip and the pane are SIBLINGS inside that one row — tmpl:39
            opens `.output-part.row.choosen` and never closes it before :70.

            This is what makes them sit side by side, and NOT via Bootstrap:
            the AHU board loads header/skeleton/common/styles only, none of
            which defines a single `.col-xs-N` width, so every `col-xs-*` here
            is an inert hook. The two columns come from

              #for-output-rendering .coil_new.full-height .output-with-border
                { margin:0; width:91.66666667% !important }   (common.css:19251)
              .nav-tabs { min-width:110px }                             (:18760)
              .nav-tabs ~ .col-xs-11 { max-width:calc(100% - 110px) }   (:18765)

            The port had `col-xs-12` on the nav, no `.coil_new…full-height`
            wrapper, and a bare `.row.output-with-border` that is not a
            `.col-xs-11` sibling of the strip — so NONE of the three matched
            and the pane rendered as a full-width block underneath the tab
            list. `.output-part.choosen.row { margin-left/right: 0 !important }`
            (:15092) is dev's own neutraliser for the row gutters, so this
            cannot repeat the tab-clipping that reverted the INPUT-side wrapper
            attempts (see the note on the component root above). */}
        <div className="coil_new per-fan fan-rating-mode full-height">
        <div className="full-height for-rating for_rating">
        <div className="output-part row choosen">
        <div className="col-xs-1 nav nav-tabs">
          <div className="row">
          <ul className="col-xs-12 ouptut_tubs">
            <li className="cursor-default">Output</li>
            {(['fan', 'performance', 'motor', 'vfd', 'isolation', 'notes'] as OutputTab[]).map((t) => {
              // B4: the VFD li mirrors the Backbone non-clickable gating
              // (fan.selector.output.tmpl:21-22/47-48).
              const disabled = t === 'vfd' && !vfdOutputEnabled;
              return (
                <li
                  key={t}
                  className={`output_label output_label_${t}${outputTab === t ? ' selected' : ''}${disabled ? ' non-clickable' : ''}`}
                >
                  <a
                    onClick={() => {
                      if (disabled) return;
                      setOutputTab(t);
                    }}
                  >
                    {t === 'vfd' ? 'VFD' : t.charAt(0).toUpperCase() + t.slice(1)}
                  </a>
                </li>
              );
            })}
          </ul>
          </div>
        </div>

        {/* ★ the EMPTY-RESULT state (fan.selector.output.tmpl:808-836). dev
            alternates on `choosen`: a rate that returned no rows leaves
            `self.choosen = null` with `self.error = true` (js:4523-4533 for the
            zero-models case, :4513-4522 when the vendor also sent errors, and
            :4419-4427 when `ok` is false), so the whole `.output-with-border`
            pane is REPLACED by an `.output-error` box — no Fan/Performance/
            Motor pane renders at all. The box is the tab rail's sibling so
            `.nav-tabs ~ .col-xs-11` still sizes it, and `.per-fan
            .output-with-border.output-error` (common.css:18076-18082) exists
            precisely for this state.

            The message is dev's, verbatim: a `warning-notes` "Errors :" label
            and "No suitable fan found.", with each API error listed under it.
            dev prints that line whenever the rate came back with nothing —
            errors or not — so it is NOT gated on an empty `errors` array. The
            port had its own wording ("Unable to find a fan that satisfies all
            inputted parameters."), rendered inside the normal pane and hidden
            the moment the vendor DID return an error string.

            The "Rated on" line follows in dev's own wrapper for this branch
            (`.col-xs-11.col-xs-offset-1`, tmpl:826-833 — NOT the
            `position:absolute; bottom:0` wrapper the pane branch uses). */}
        {ratedRows && !ratedRows.length ? (
          <>
            <div className="col-xs-11 output-with-border output-error">
              <div className="col-xs-12 output-part">
                <div className="clearfix row nots_and_errors">
                  <div className="col-xs-2">
                    <p className="warning-notes">Errors :</p>
                  </div>
                  <div className="col-xs-10">
                    <p className="warning-notes">No suitable fan found.</p>
                    {rateErrors.filter(Boolean).map((e, i) => (
                      <p className="warning-notes" key={i}>{e}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {ratedDatetime.current ? (
              <div className="col-xs-11 col-xs-offset-1">
                <div className="">
                  <div className="row rating-time">Rated on : {ratedDatetime.current}</div>
                </div>
              </div>
            ) : null}
          </>
        ) : (
        <>
        {/* tmpl:70 — the pane, `.col-xs-11` so the `.nav-tabs ~ .col-xs-11`
            width rule can see it, with dev's own inline padding-top. */}
        <div className="col-xs-11 output-with-border" style={{ paddingTop: 7 }}>
        {/* tmpl:72 — the active tab-pane host. `active in` is load-bearing:
            `.fade { height:0; display:none }` / `.active.in.fade { height:auto;
            display:block }` (common.css:18497-18504). */}
        <div className="col-xs-12 tab-pane fade active in full-height">
        {/* ★ dev has TWO Fan-pane layouts and switches on `selected`
            (output.tmpl:74 `{{#if selected}}`) — the STORED rated fan the
            dialog opens on (`selectedRow`, js:3924 `self.selected`) vs a fresh
            rate's candidate list. The `selected` branch wraps both columns in
              .row.full-height.no-right-padding
                > .col-xs-12.full-height [display:flex]     (tmpl:74-75)
            where the list branch puts them straight in the row (:217). */}
        <PaneColumns wrapped={outputTab === 'fan' && !!selectedRow}>
          {/* ★ dev splits 8/4 ONLY inside the Fan pane (output.tmpl:218 + :312).
              Every other output pane is a full-width `col-xs-12` (:384, :433,
              :635, :730, :735) with no curve beside it — which is what the
              13-column motor sheet needs to have room for its own widths. */}
          <div className={`col-xs-12 full-height${outputTab === 'fan' ? ' col-lg-8' : ''}`}>
            {outputTab === 'fan' && (
              <>
                {/* ★ no error renderer here. dev's Fan pane has none: warnings
                    live in the Notes pane's `{{#if warnings}}` block
                    (output.tmpl:743-752), and errors only ever render in the
                    `.output-error` branch above. `rateErrors` is non-empty ONLY
                    when `runRate` took its no-models else-branch — the same
                    branch that does `setRatedRows([])` — so `ratedRows.length`
                    is 0 there and that branch owns the render. The
                    `<RatedMessageBlock errors={rateErrors}>` that used to sit
                    here became unreachable the moment the `.output-error` box
                    landed.

                    dev's results table verbatim (fan.selector.output.tmpl:219-310).
                    The class list is the whole point — see the note on
                    `ratedColumns`. The zero-row case never reaches here either,
                    for the same reason. */}
                  <div
                    ref={rateListRef}
                    className="row rate-list-inline full-height fan_rate-list-inline"
                  >
                    <div className="col-xs-12 rate-list-header rate-list-heading">
                      {ratedColumns.map((c, ci) => (
                        <div
                          key={ci}
                          className={c.headClass}
                          style={c.headStyle}
                          data-sort={c.sort}
                          onClick={c.sort ? () => sortRated(c.sort as string) : undefined}
                        >
                          {c.head}
                          {c.sort ? sortIcon(c.sort) : null}
                        </div>
                      ))}
                    </div>
                    <div className="col-xs-12 rate-list-body">
                      {rows.map((r, i) => (
                        <div
                          key={i}
                          data-id={i}
                          className={`row rate-list-rows rate-list-header rated_item rated-body fan_rated_item${
                            i === chosenIndex ? ' choosen' : ''
                          }`}
                          // legacy `click .fan_rated_item → chooseFan` (js:521) —
                          // the user's candidate pick. Without it only the
                          // auto-chosen row 0 could ever be Selected (P1b-verify
                          // fix, 2026-07-09). Gated on an in-session Rate: the
                          // stored-row (read-only) render must not clobber the
                          // stored curve with the row's empty curve keys.
                          onClick={() => {
                            if (!sessionRated.current || !ratedRows?.length) return;
                            chooseFan(i, ratedRows, mfg, fanType);
                          }}
                        >
                          {ratedColumns.map((c, ci) => (
                            <FanRateCell key={ci} col={c} row={r} index={i} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
              </>
            )}
            {/* J6 — the Performance output pane (fan.selector.output.tmpl
                :383-433): per-season "LADB / LAWB" DISABLED mirrors of the
                staged EADB/EAWB (a fan's real leaving adds motor heat rise,
                computed only for the psych chart and never persisted — the
                display mirrors entering, same as filter/eliminator/
                silencer). formatTemp = js:5025-5029 ("--" for blank,
                toFixed(2) otherwise); groups gate on the same
                season_selected_id predicate as the input side. */}
            {outputTab === 'performance' && (() => {
              const seasonSelectedId = getSeasonSelectedId();
              const formatTemp = (v: unknown): string => {
                const n = v !== null && v !== undefined && v !== '' ? Number(v) : NaN;
                return Number.isNaN(n) ? '--' : n.toFixed(2);
              };
              const group = (season: 'summer' | 'winter', heading: string) => (
                <div className={`season-group-${season}`}>
                  <div className="row">
                    <div className="col-xs-12"><h4>{heading}</h4></div>
                  </div>
                  <div className="row">
                    <div className="col-xs-5 label-part">
                      LADB / LAWB
                      <span
                        className="unit right"
                        style={{ border: 'none', background: 'none', width: 'auto', fontSize: 11 }}
                      >
                        &deg;F
                      </span>
                    </div>
                    <div className="col-xs-7 input-part" style={{ padding: 0 }}>
                      <div className="row">
                        <div className="col-xs-6">
                          <input
                            disabled
                            style={{ width: '100%', border: 'none', opacity: 1 }}
                            type="text"
                            autoComplete="off"
                            name={`ladb_temp_${season}`}
                            data-name={`ladb_temp_${season}_output`}
                            className="filter_section_field search_field"
                            value={`${formatTemp(sectionDraft.current[`eadb_temp_${season}`])} / ${formatTemp(sectionDraft.current[`eawb_temp_${season}`])}`}
                            onChange={() => undefined}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
              return (
                <div className="col-xs-6" style={{ marginTop: 15 }}>
                  {seasonSelectedId !== 2 && group('summer', 'Summer')}
                  {seasonSelectedId !== 1 && group('winter', 'Winter')}
                </div>
              );
            })()}
            {/* ★ NO wrapper element here. dev's motor sheet hangs straight off
                its `.col-xs-12…full-height` column (output.tmpl:453-455), and
                the port's extra `.motor_output` div — which exists in no
                stylesheet and in no legacy template — broke the height chain:
                `.rate-list-inline.motor-rate-list` is `overflow:auto` +
                `.full-height` (`height:100%!important`, common.css:18394 /
                :17377), so with an auto-height parent between it and the pane
                the 100% resolved to auto and the box shrank to its content.
                Measured in a static harness over the real stylesheets, pane
                404px: the scroll box came out 78px (header 39 + one row 39) —
                which is why the horizontal scrollbar sat immediately under the
                Motor row instead of at the bottom of the table. Without the
                wrapper it is 395px, i.e. the full pane, as on dev.

                It also un-breaks paging: at 30 rows the wrapped table measured
                1248px and overflowed the pane by 852px with no internal
                scroll, so `onMotorListScroll` could never fire. */}
            {outputTab === 'motor' && (
              motorOutputRows.length ? (
                  <>
                    {/* dev's motor sheet (fan.selector.output.tmpl:455-604):
                        ONE table, two row sources — the rated motor list when
                        it loaded (`{{#each motorModels}}`, :539) and the single
                        saved/chosen motor otherwise (`{{#unless
                        motorModels.length}}`, :472). */}
                    <div
                      className="row rate-list-inline rate-list-heading motor-rate-list full-height"
                      onScroll={motorListMode ? onMotorListScroll : undefined}
                    >
                      <div className="col-xs-12 rate-list-header motor-rate-list-header">
                        {motorColumns.map((c, ci) => (
                          <div key={ci} className={c.headClass} style={c.headStyle}>
                            {c.head}
                          </div>
                        ))}
                      </div>
                      <div className="rate-list-body">
                        {motorOutputRows.map((row, ri) => (
                          <div
                            key={ri}
                            data-id={motorListMode ? ri : undefined}
                            // dev's single motor row is NOT highlighted
                            // (tmpl:473 has no `choosen`); the list rows are,
                            // via chooseMotor (js:5799-5805).
                            className={`col-xs-12 rate-list-header rate-list-rows rated_item rated-body motor_rated_item${
                              motorListMode && motorPick?.index === ri ? ' choosen' : ''
                            }`}
                            onClick={
                              motorListMode
                                ? () => {
                                    // chooseMotor (js:5780-5812): pick, then
                                    // persist the index into the header-mode
                                    // storage entry like legacy does.
                                    setMotorPick({ index: ri, row });
                                    saveInStorage();
                                  }
                                : undefined
                            }
                          >
                            {motorColumns.map((c, ci) => (
                              <FanRateCell key={ci} col={c} row={row} />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
              ) : (
                <div className="rate-list-header label">No selected motor.</div>
              )
            )}
            {/* ----- B4: VFD output pane (fan.selector.output.tmpl 567-657).
                 The pane div carries the same non-clickable gate class as its
                 li (tmpl :567) — a stale-open pane grays out, like legacy. */}
            {outputTab === 'vfd' && (
              <div
                id={`${idPrefix}VFD_Output`}
                className={`col-xs-12 full-height${vfdOutputEnabled ? '' : ' non-clickable'}`}
              >
                <div className="row full-height no-right-padding">
                  <div className="col-xs-12 full-height" style={{ display: 'flex', paddingTop: 19 }}>
                    {/* left column (tmpl 571-606) */}
                    <div className="col-xs-6 full-height">
                      <VfdOutputRow label="Manufacturer" dataName="mfg">{vTxt(vp.mfg_name)}</VfdOutputRow>
                      <VfdOutputRow label="Series" dataName="series">{vTxt(vp.series_name)}</VfdOutputRow>
                      <VfdOutputRow label="Model" dataName="model">{vTxt(vp.model)}</VfdOutputRow>
                      {/* data-name="model" mirrors the tmpl quirk (:586). */}
                      <VfdOutputRow label="Manufacturer's Part #" dataName="model">{vTxt(vp.mpn)}</VfdOutputRow>
                      <VfdOutputRow label="Description" dataName="description">{vTxt(vp.description_name)}</VfdOutputRow>
                      <VfdOutputRow label="Power" unit="HP" dataName="power">
                        {/* Dev-sync I (fan.selector.output.tmpl:607 on merged
                            dev): `{{get_one_decimal vfd_params.power}}` —
                            truthy numeric → toFixed(2), else '' (the helper,
                            ahu/libs/handlebars/helpers.js:154-157). */}
                        <span data-formatter_type="decimal">
                          {vp.power && Number(vp.power) ? Number(vp.power).toFixed(2) : ''}
                        </span>
                      </VfdOutputRow>
                      {/* Dev-sync H (fan.selector.output.tmpl:610-620 on merged
                          dev): NEMA renders only when the VFD has a NEMA name;
                          IP Rating is UN-hidden, renders only when ip_rating is
                          set, label back to col-xs-5, data-name ip_rating. */}
                      {Boolean(vp.nema_encl_type_name) && (
                        <VfdOutputRow label="NEMA" dataName="nema_encl_type">{vTxt(vp.nema_encl_type_name)}</VfdOutputRow>
                      )}
                      {Boolean(vp.ip_rating) && (
                        <VfdOutputRow label="IP Rating" dataName="ip_rating">
                          {vTxt(vp.ip_rating)}
                        </VfdOutputRow>
                      )}
                    </div>
                    {/* right column (tmpl 607-654) */}
                    <div className="col-xs-6 full-height" style={{ paddingLeft: 45 }}>
                      <VfdOutputRow label="Voltage" dataName="voltage">{vTxt(vp.voltage)}</VfdOutputRow>
                      <VfdOutputRow label="FLA Input" unit="Amp" dataName="fla_input">
                        <span data-formatter_type="decimal">{getOneDecimal(vp.fla_input)}</span>
                      </VfdOutputRow>
                      <VfdOutputRow label="FLA Output" unit="Amp" dataName="fla_output">
                        <span data-formatter_type="decimal">{getOneDecimal(vp.fla)}</span>
                      </VfdOutputRow>
                      <VfdOutputRow label="Frame" dataName="frame">{vTxt(vp.frame)}</VfdOutputRow>
                      {/* W x H read the vfd MODEL (master-synced dims), NOT
                          vfd_params (tmpl :632-633). */}
                      <VfdOutputRow label="Width x Height" unit="in">
                        <span data-formatter_type="decimal" data-name="width">{getOneDecimal(vv.width)}</span> x{' '}
                        <span data-formatter_type="decimal" data-name="height">{getOneDecimal(vv.height)}</span>
                      </VfdOutputRow>
                      <VfdOutputRow label="Depth" unit="in" dataName="depth">
                        <span data-formatter_type="decimal">{vTxt(vp.depth)}</span>
                      </VfdOutputRow>
                      <VfdOutputRow
                        label="VFD-to-Motor Configuration"
                        dataName="vfd_motor_configuration"
                        labelStyle={{ whiteSpace: 'nowrap' }}
                      >
                        {vfdConfigurationName}
                      </VfdOutputRow>
                      <VfdOutputRow label="Quantity" dataName="qty">
                        {vfdQtyTotalRef.current != null ? String(vfdQtyTotalRef.current) : ''}
                      </VfdOutputRow>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {outputTab === 'isolation' && (
              /* fan.selector.output.isolation.tmpl in full — dev appends the
                 sub-template into `#isolationOutput` (js:7499-7585), so the id
                 stays on the host and the `.col-xs-12.col-lg-12.full-height`
                 root is the sub-template's own (isolation.tmpl:1). The four
                 Model rows carry `hide` on the DATA half only when there are no
                 isolators (:20/:28/:36/:44) — the labels always show, and
                 `.output-part .hide { display:none }` (common.css:15096) is what
                 acts on it. */
              <div id="isolationOutput" className="col-xs-12 col-lg-12 full-height">
                {/* dev shows this while the isolator PATCH is in flight and
                    removes it when the response lands (js:7536-7546). */}
                {isolatorsPatching && (
                  <div className="preloader dotted-preloader isolation-preloader active" />
                )}
                <div className="row left-column">
                  <div className="col-xs-4 label-part">Manufacturer</div>
                  <div className="col-xs-8 output-data">{isoMfgName}</div>
                </div>
                <div className="row left-column">
                  <div className="col-xs-4 label-part">Series</div>
                  <div className="col-xs-8 output-data">{isoSeriesName}</div>
                </div>
                {(['1/A', '2/B', '3/C', '4/D'] as const).map((suffix, i) => {
                  const iso = isolators?.[`isolator_${i + 1}`];
                  return (
                    <div className="row left-column" key={suffix}>
                      <div className="col-xs-4 label-part">Model - {suffix}</div>
                      <div className={`col-xs-8 output-data${isolators ? '' : ' hide'}`}>
                        {iso?.isolator ?? ''} / {iso?.actual_load ?? ''} lb /{' '}
                        {iso?.actual_deflection ?? ''} in
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {outputTab === 'notes' && (
              /* fan.selector.output.tmpl:735-786 — the model-name heading and
                 the four note blocks, each its own col-xs-2 label + col-xs-10
                 list inside ONE `.nots_and_errors` row. */
              <div className="col-xs-12 col-md-12 full-height">
                <div className="row">
                  <div className="fan-model-name">{notesModelName}</div>
                </div>
                <div className="row fan-notes-wrapper">
                  <div className="">
                    <div className="clearfix row nots_and_errors">
                      {rateWarnings.length > 0 && (
                        <>
                          <div className="col-xs-2">
                            <p className="warning-notes">Warning :</p>
                          </div>
                          <div className="col-xs-10">
                            {rateWarnings.map((w, i) => (
                              <p className="warning-msg" key={i}>{w}</p>
                            ))}
                          </div>
                        </>
                      )}
                      {apiNotes.length > 0 && (
                        <>
                          <div className="col-xs-2">
                            <p className=" label">Api Notes :</p>
                          </div>
                          <div className="col-xs-10">
                            {apiNotes.map((n, i) => (
                              <p className=" label" key={i}>{n}</p>
                            ))}
                          </div>
                        </>
                      )}
                      {modelNotes.length > 0 && (
                        <>
                          <div className="col-xs-2">
                            <p className="label">Notes :</p>
                          </div>
                          <div className="col-xs-10">
                            {modelNotes.map((n, i) => (
                              <p className="label notes" key={i}> {n}</p>
                            ))}
                          </div>
                        </>
                      )}
                      {mfgNotes.length > 0 && (
                        <>
                          <div className="col-xs-2">
                            <p className="label">Mfg&apos;s Notes :</p>
                          </div>
                          <div className="col-xs-10">
                            {mfgNotes.map((n, i) => (
                              <p className=" label notes" key={i}> {n.note}</p>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ★ the FAN CURVE CANVAS (4 of 12 cols) — Fan pane only, like dev
              (output.tmpl:312, inside `#…Properties_Performance_Output`). */}
          {outputTab === 'fan' && (
            <FanCurveCanvas
              choosen={chosenCurve}
              bands={choosenFanData}
              mfgId={mfg}
              airflowPerFan={Number(s.airflow_per_fan) || Number(selectedRow?.airflow_per_fan) || 0}
              selected={!!selectedRow}
              loading={curveLoading}
            />
          )}
        </PaneColumns>
        </div>
        {/* ★ the "Rated on : <datetime>" line, dev's markup and dev's PLACE in
            the tree (fan.selector.output.tmpl:802-808):

              .col-xs-11.output-with-border          (tmpl:70)
                ├ .col-xs-12.tab-pane…               (the active output pane)
                └ .col-xs-11 [position:absolute; bottom:0]
                    └ .row
                        └ .row.rating-time#rating_time

            `.rating-time` is `position:fixed` with NO offsets (common.css:
            18509), so the browser leaves it at its *static* position — which
            means the wrapper is the only thing that places it. dev's wrapper is
            `position:absolute; bottom:0`, pinning the line to the bottom of the
            bordered output panel. The port had no wrapper and rendered the line
            INSIDE the pane, after a `full-height` results table, so its static
            position was wherever that table happened to end.

            It is a SIBLING of the tab-pane, not a child of it: dev renders the
            line once for the whole output panel, so it shows on every output
            tab. The port emitted it twice (Fan and Motor panes only), which is
            also why it disappeared on Performance / VFD / Isolation / Notes.

            Text node, not a `<span>` — dev has none, and the shared <RatedOn>
            kit component adds one, so this is written out inline instead. */}
        {ratedDatetime.current ? (
          <div className="col-xs-11" style={{ position: 'absolute', bottom: 0 }}>
            <div className="row">
              <div className="row rating-time">Rated on : {ratedDatetime.current}</div>
            </div>
          </div>
        ) : null}
        </div>
        </>
        )}
        </div>
        </div>
        </div>
      </div>

      {/* ★ the RATE overlay — a SIBLING of `#for-output-rendering`, never a
          child of it. It used to render inside that div, which carries
          `hidden` (`display:none !important`) until a rating exists — so on the
          FIRST rate of a fan the spinner was display:none for the whole vendor
          round-trip and the dialog looked frozen. Legacy has the same
          hidden-until-rated output div (fan.selector.tmpl:1847) and for exactly
          that reason appends its preloader to the always-visible
          `.fan-data-input` instead (js:4405-4409), positioning it over the
          output region with `top:100%; height:92%` (common.css:19289).
          React has no `.fan-data-input` wrapper — the nav column and the tab
          host are sibling floats under `#fan-selector` — so the overlay hangs
          off the root and ahuBoardOverrides.scss §6 re-derives dev's geometry
          from the input row's own height. ⚠ dev's `:19289` rule DOES match here
          (the chassis renders `#rating_mode_fan_content`), and it out-specified
          §6 until 2026-09-02 — `top:100%` of this full-height root put the
          overlay a whole dialog-height DOWN, which is what made the dialog
          scroll for the length of every rate.

          ★ `rating` ONLY — not `isRating`. `isRating` is the shared
          `usePostRatingMutation` loading flag, so it is also true for the
          SECOND, per-row `/rating/` POST that chooseFan → getFanCurve fires
          when a rated row is picked (Comefri/ebm-papst/ZA/Rosenberg). That
          raised this whole-output veil on every row click: the rated table
          stays in the DOM and nothing is re-rated, but `.dotted-preloader`'s
          `rgba(255,255,255,.7)` washes the rows out to blank, so picking a
          model looked like the output was reloading (Anna, 2026-10-12).
          Legacy scopes the curve fetch's spinner to the CURVE COLUMN —
          `.fan-getting-preloader` appended into `.curve_loader`
          (fan.selector.js:4055-4059), `.canvas-curve_loader` for mfg 67 — and
          FanCurveCanvas already renders both off `loading={curveLoading}`, so
          the fetch keeps its own progress indicator. The Select/Print disarm
          that goes with it is gated on `curveLoading` too, not on this. */}
      {rating && (
        <div className="fan-preloader preloader dotted-preloader active" />
      )}

      {/* available-combinations popup (read-only browse) */}
      {combinationsOpen && (
        <CombinationsPopup
          mfgId={mfg}
          state={s}
          onApply={applyCombination}
          onClose={() => setCombinationsOpen(false)}
        />
      )}

      {/* dev-sync F Stage 2 — the NYB "How to Rate a Fan" popup (how_to.tmpl).
          ★ MUST be portalled OUT of the selector's own subtree, exactly like
          legacy's `$('#ksdm-content').append(this.howToTemplate())`
          (fan.selector.js:6573). `.fan-how-to-popup .popup-content` is
          `position:fixed`, but the selector is mounted inside
          `#component_rating_mode.rating-mode-wrapper` / `.rotate-card`, BOTH of
          which carry the board's card-flip `transform: matrix3d(...)`. A
          transformed ancestor becomes the containing block for `position:fixed`
          descendants (and `#component_rating_mode` is `overflow:auto hidden`),
          so rendering the popup inline puts it ~490px to the right of legacy
          and clips its right-hand side off. Portalling to `#ksdm-content`
          reproduces legacy's DOM position and therefore its geometry. */}
      {howToOpen && <FanHowToPopup onClose={() => setHowToOpen(false)} />}

      {/* ---- the PRINT SHEET (fan.selector.js:8123 `$('body').append('<div
          class="print-content"></div>')` + :8351 `.html(templatePrint(data))`).
          A body-level portal is that append: `.print-content` has to be a
          DIRECT child of <body>, because the print stylesheet's
          `body > *:not([class*=print])` rule only spares direct children
          (common.css:22140). It is `display:none` on screen (:22119), so the
          mount costs nothing until the print dialog opens. */}
      {printing &&
        createPortal(
          <div className="print-content">
            <FanPrintSheet
              reportColor={reportColor}
              reportCo={printProject.report_co as string | undefined}
              reportLogo={printProject.report_logo as string | undefined}
              year={new Date().getFullYear()}
              headerMode={headerMode}
              projectName={printProject.name as string | undefined}
              jobNumber={printProject.job_number as string | undefined}
              quoteNumber={printProject.quote_number as string | undefined}
              elevation={projectElevation}
              ahuTag={
                (ahu as { tag?: string } | null | undefined)?.tag ??
                (activate.ahu as { tag?: string } | undefined)?.tag ??
                ''
              }
              sectionTag={(activate.section as { tag?: string } | undefined)?.tag ?? ''}
              ratingDatetime={ratedDatetime.current}
              fanDoc={fanDoc as FanCmsDoc}
              s={s}
              choosen={choosenFanData}
              selected={selectedRow}
              motor={motorRow}
              masterLists={masterLists as Record<string, any[]>}
              masterFullLists={masterFullLists as Record<string, any[]>}
              notes={mfgNotes as Array<{ note?: string }>}
              motorPowerTotal={
                (detail as Record<string, unknown> | undefined)?.motor_power_total ??
                s.motor_power_total
              }
              curve={chosenCurve}
              mfgId={mfg}
              airflowPerFan={
                Number(s.airflow_per_fan) || Number(selectedRow?.airflow_per_fan) || 0
              }
            />
          </div>,
          document.body
        )}
    </div>
    </FanFieldCtx.Provider>
  );
};

/**
 * dev-sync F Stage 2 — `fan/tmpl/how_to.tmpl` (new, 25 lines), verbatim.
 *
 * DRAG DEVIATION (deliberate, follows the shipped React precedent): legacy
 * calls `.draggable()` + `.resizable({handles:"n,e,s,w,ne,se,sw,nw"})` on
 * #fan-how-to-floating-popup (fan.selector.js:6575-6579, jQuery-UI). React's
 * already-shipped filter how-to (FilterSelector.tsx:2132) is neither draggable
 * nor resizable, and this popup is its twin — matching it keeps the two how-tos
 * consistent. `cursor:move` on the heading/body/buttons (common.css) is
 * therefore cosmetic here, as it already is on the filter one.
 */
const FanHowToPopup = ({ onClose }: { onClose: () => void }) => createPortal(
  <div className="fan-how-to-popup popup-wrapper active">
    <div className="popup-content floating-popup" id="fan-how-to-floating-popup">
      <h2 className="popup-heading">
        <i className="icon-info" />
        How to Rate a Fan
      </h2>
      <div className="popup-content-part fan-how-to-body">
        <p><b>Standard Rating</b></p>
        <ul>
          <li>All three Wheel parameters; Wheel Diameter, Wheel Type and Wheel Width % are not selected.</li>
          <li>Rating provides all optimally available fans, based on selected parameters.</li>
        </ul>
        <p><b>Standard Rating w/Filtration</b></p>
        <ul>
          <li>One or two of the three wheel parameters; Wheel Diameter, Wheel Type and Wheel Width % are defined.</li>
          <li>Rating provides all optimally available fans, based on selected parameters, including the one or two out of three parameters Wheel Diameter, Wheel Type and Wheel Width %.</li>
        </ul>
        <p><b>Expert Rating</b></p>
        <ul>
          <li>All three wheel parameters; Wheel Diameter, Wheel Type and Wheel Width % are selected.</li>
          <li>Rating provides the exact performance of the specified fan, based on all defined parameters, including Wheel Diameter, Wheel Type and Wheel Width %.</li>
        </ul>
      </div>
      <div className="popup-control-buttons">
        <button className="fan-how-to-close" name="fan_how_to_close" onClick={onClose}>
          CLOSE
        </button>
      </div>
    </div>
  </div>,
  // legacy target; `#ksdm-content` exists on the AHU board and on every page
  // that renders the shared header shell. document.body is the safety net.
  document.getElementById('ksdm-content') ?? document.body,
);

// helper: is this mfg one whose envelope carries a function_name (so the
// missing-type bail is keyed differently)? (the TCF-family carries no
// function_name, so they need the resolved type — legacy 3307).
function FAN_FUNCTION_NAME_HAS(mfg: number): boolean {
  return mfg == 164 || mfg == 47 || mfg == 37 || mfg == 67 || mfg == 23;
}

// ---------------------------------------------------------------------------
// Dev-sync H — Annexair catalog-key helpers (fan.selector.js on merged dev:
// normalizeAnnexairCatalogValue :5806, getAnnexairCatalogVariantKey :5810,
// getAnnexairCatalogFamilyKey :5821, findAnnexairCatalogFanMatch :5827).
// ---------------------------------------------------------------------------
function normalizeAnnexairCatalogValue(value: unknown): string {
  if (value == null) return '';
  return String(value).replace(/\s+/g, '').toUpperCase();
}

function annexairCatalogVariantKey(ratedFan: Record<string, any>): string {
  return normalizeAnnexairCatalogValue(
    ratedFan?.ksdm_fan_id ||
      ratedFan?.mapped_fan_id ||
      ratedFan?.annexair_catalog_spn ||
      ratedFan?.nomenclature ||
      ratedFan?.spn ||
      ratedFan?.model
  );
}

function annexairCatalogFamilyKey(ratedFan: Record<string, any>): string {
  const variantKey = annexairCatalogVariantKey(ratedFan);
  if (!variantKey) return '';
  return variantKey.replace(/-\d+$/, '');
}

/** Keep the catalog rows that answer a request for `finishId` — the client side
 * of `integrated_parts/fan/finish.py::narrow_by_finish`, with the same
 * resolution order: rows recording the requested finish win; rows with NO
 * finish recorded are the finish-agnostic fallback (every Twin City Fan / New
 * York Blower / Greenheck / Comefri / Lau row today, so those picks are
 * untouched); and — unlike the backend, which drops the candidate — a family
 * stocked only in another finish falls through UNNARROWED here, because this
 * list is only being re-matched to find the row the rating already accepted and
 * losing it would blank master_fan_id for a fan that rated fine. */
function narrowCatalogRowsByFinish(
  rows: Array<Record<string, any>>,
  finishId: unknown
): Array<Record<string, any>> {
  if (finishId == null || finishId === '' || rows.length < 2) return rows;
  const matched = rows.filter(
    (r) => r?.finish_id != null && String(r.finish_id) === String(finishId)
  );
  if (matched.length) return matched;
  const unrecorded = rows.filter((r) => r?.finish_id == null);
  return unrecorded.length ? unrecorded : rows;
}

/** the EXACT variant match over the catalog rows (spn/fan_type/model compared
 * against the normalized variant key). */
function findAnnexairCatalogRowMatch(
  ratedFan: Record<string, any>,
  rows: Array<Record<string, any>>
): Record<string, any> | null {
  const variantKey = annexairCatalogVariantKey(ratedFan);
  if (!variantKey) return null;
  return (
    rows.find((fanData) => {
      const candidateKeys = [fanData?.spn, fanData?.fan_type, fanData?.model].map(
        normalizeAnnexairCatalogValue
      );
      return candidateKeys.includes(variantKey);
    }) ?? null
  );
}

// ---------------------------------------------------------------------------
// B4: VFD output pane helpers (fan.selector.output.tmpl 567-657).
// ---------------------------------------------------------------------------
/** Handlebars text semantics — null/undefined render as ''. */
const vTxt = (x: unknown): string => (x == null ? '' : String(x));

/** the Handlebars get_one_decimal helper (ahu/libs/handlebars/helpers.js:
 * 154-157): first truthy non-object arg → Number → toFixed(2); falsy/NaN → ''.
 * (Despite the name it renders TWO decimals — mirror the code, not the name.) */
function getOneDecimal(...args: unknown[]): string {
  const found = args.find((el) => el && typeof el !== 'object');
  const n = Number(found);
  return n ? n.toFixed(2) : '';
}

/** one display row: .row.left-column > .label-part + .output-data.fan_field. */
const VfdOutputRow = ({ label, unit, dataName, labelClass = 'col-xs-5', labelStyle, hidden, children }: {
  label: string;
  unit?: string;
  dataName?: string;
  labelClass?: string;
  labelStyle?: React.CSSProperties;
  hidden?: boolean;
  children: React.ReactNode;
}) => (
  <div className={`row left-column${hidden ? ' hide' : ''}`}>
    <div className={`${labelClass} label-part`} style={labelStyle}>
      {label}
      {unit && <span className="unit right">{unit}</span>}
    </div>
    <div className="col-xs-7 output-data fan_field" data-name={dataName}>
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// build the stored "selected" output row (legacy setOutputs, 2868-2929).
// ---------------------------------------------------------------------------
function buildSelectedRow(
  m: Record<string, any>,
  fanParams: Record<string, any>,
  // 5e-sync: the section AW pair for the warning flags (dev setOutputs,
  // fan.selector.js:3634-3646 — section.get('aw_width'/'aw_height')).
  section?: Record<string, any> | null
): Record<string, any> {
  const f2 = (v: any) => (v == null ? null : (Math.round(Number(v) * 100) / 100).toFixed(2));
  const sectionAwWidth = parseFloat(String(section?.aw_width));
  const sectionAwHeight = parseFloat(String(section?.aw_height));
  return {
    aw_width: m.min_aw_width ?? '',
    aw_height: m.min_aw_height ?? '',
    aw_width_height: m.min_aw_width != null ? `${m.min_aw_width} x ${m.min_aw_height}` : '',
    aw_width_warning:
      m.min_aw_width != null && !isNaN(sectionAwWidth) && parseFloat(String(m.min_aw_width)) > sectionAwWidth,
    aw_height_warning:
      m.min_aw_height != null && !isNaN(sectionAwHeight) && parseFloat(String(m.min_aw_height)) > sectionAwHeight,
    fan_model: m.fan_model,
    airflow_actual: m.airflow_actual,
    airflow_per_fan: m.airflow_per_fan,
    bhp: f2(m.bhp),
    bhp_total: f2(m.bhp_total_all_fans),
    bp_kw: f2(m.bp_kw),
    bp_kw_total: m.bp_kw_total ? f2(m.bp_kw_total) : null,
    speed: m.speed,
    speed_max: m.speed_max ?? m.rated_fan_speed_max,
    static_efficiency: m.static_efficiency,
    total_efficiency: m.total_efficiency,
    fei: m.fei,
    // legacy setOutputs includes the persisted rpi (fan.selector.js:3645) —
    // the reopen row was rendering a blank RPI cell (P1-verify fix, 2026-07-09).
    rpi: m.rpi,
    // Dev-sync H: setOutputs gained the DIRECT percent values from the model
    // (fan.selector.js:3763-3764 on merged dev). On a reopen the AHUFan row has
    // no such columns → undefined → the cell's cms id-lookup fallback renders,
    // exactly like legacy's `{{else}}` branch.
    wheel_dia_percent: m.wheel_dia_percent,
    wheel_width_percent: m.wheel_width_percent,
    wheel_dia_percent_id: fanParams.wheel_dia_percent_id,
    wheel_width_percent_id: fanParams.wheel_width_percent_id,
    series_id: fanParams.series_id,
    // The five setOutputs keys the PRINT SHEET reads and nothing on screen did
    // (dev fan.selector.js:3957 / :3972-3975 / :3984). `wheel_dia_nominal` and
    // `amca_class` are the fan_params ids the sheet's `{{#if selected}}` branch
    // looks up in the CMS (print.tmpl:213-243, :330-361); `density` backs
    // "Inlet Density" (:483) and is the model's `air_density`; `wheel_mtl_id`
    // and `redundancy_id` back Wheel Material (:262) and Redundancy (:399).
    density: m.air_density,
    amca_class: fanParams.amca_class_id,
    wheel_dia_nominal: fanParams.wheel_dia_nominal_id,
    wheel_mtl_id: fanParams.wheel_mtl_id,
    redundancy_id: fanParams.redundancy_id,
    // ★ the array the fan was SELECTED with, so reopening shows it in the
    // Wide x High column. Read-only there: a stored row is one historical
    // configuration, not a pick list, which is the column's text fallback.
    qty_wide: m.qty_wide,
    qty_high: m.qty_high,
    // the vendor notes stamped into fan_params at Select time — dev's
    // `rated_notes: self.fanParams.notes` (setOutputs js:3991), read by the
    // Notes pane's "Api Notes :" block (output.tmpl:753-762).
    rated_notes: fanParams.notes,
    tsp: m.tsp,
    outlet_vel: m.outlet_vel,
    // the persisted octave-band sound levels (dev setOutputs,
    // fan.selector.js:3941-3964) — the band rows under the curve read them off
    // the stored row on a reopen, exactly as dev does via `choosen = selected`.
    dbi_63: m.dbi_63,
    dbi_125: m.dbi_125,
    dbi_250: m.dbi_250,
    dbi_500: m.dbi_500,
    dbi_1000: m.dbi_1000,
    dbi_2000: m.dbi_2000,
    dbi_4000: m.dbi_4000,
    dbi_8000: m.dbi_8000,
    dbo_63: m.dbo_63,
    dbo_125: m.dbo_125,
    dbo_250: m.dbo_250,
    dbo_500: m.dbo_500,
    dbo_1000: m.dbo_1000,
    dbo_2000: m.dbo_2000,
    dbo_4000: m.dbo_4000,
    dbo_8000: m.dbo_8000,
    in_lwa: m.in_lwa,
    out_lwa: m.out_lwa,
  };
}

// ---------------------------------------------------------------------------
// the available-combinations popup (fan.selector.available_combinations.js 92).
// CombinationsCollection = GET cms/1/.../fans/params/available/combinations;
// dblclick row stamps the params + fires params_fan:change_combinations.
// Read-only browse: the GET is safe; applying is userArmed (onApply).
// ---------------------------------------------------------------------------
const CombinationsPopup = ({ mfgId, state, onApply, onClose }: {
  mfgId: number;
  state: Record<string, unknown>;
  onApply: (params: Record<string, unknown>) => void;
  onClose: () => void;
}) => {
  const [combos, setCombos] = useState<Array<Record<string, any>> | null>(null);
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (state.mfg_id != null) params.set('mfg_id', String(state.mfg_id));
    if (state.type_id != null) params.set('type_id', String(state.type_id));
    if (state.series_id != null) params.set('series_id', String(state.series_id));
    void fetch(
      `/api/v1.0/cms/1/v1.0.0/ahu/fans/params/available/combinations?${params.toString()}`,
      { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((body) => {
        if (cancelled) return;
        setCombos(Array.isArray(body) ? body : ((body as any)?.results ?? []));
      })
      .catch(() => { if (!cancelled) setCombos([]); });
    return () => { cancelled = true; };
  }, [state.mfg_id, state.type_id, state.series_id]);

  void mfgId;
  return (
    <div className="popup-wrapper available-combinations-popup" id="available_combinations">
      <div className="popup-content">
        <div className="popup-header">
          <span>Available Combinations</span>
          <button type="button" className="close_combinations" onClick={onClose}>×</button>
        </div>
        <div className="popup-body combinations-table">
          {combos == null ? (
            <div className="preloader dotted-preloader active" />
          ) : combos.length === 0 ? (
            <div className="label">No combinations available.</div>
          ) : (
            <table className="table">
              <tbody>
                {combos.map((c, i) => (
                  <tr key={i} className="combination-row"
                    onDoubleClick={() => onApply(c)}>
                    {Object.entries(c).map(([k, v]) => (
                      <td key={k} data-name={k}>{String(v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// isolator-doc select (the isolation tab resolves from the spring-isolator CMS
// doc, not the fans doc / master list).
// ---------------------------------------------------------------------------
// A select whose options resolve from an ARBITRARY CMS-doc dot-path (narrowed by
// the dependency-graph ids; empty → full list). Used by the isolation tab
// (spring-isolator doc) AND — Selector-parity FAN restore — the motor-base
// sub-tab (motor-base doc) and the type-dependent motor Position field (fans
// doc, fanMotorPositionPath). `className` matches the legacy element's classes.
const DocPathSelect = ({
  doc, s, graph, name, path, label, labelKey, unit, className = 'search_field si_params',
  onChange, bare,
}: {
  doc: FanCmsDoc | undefined;
  s: Record<string, unknown>;
  graph: { optionIds: (f: string) => Array<number | string> | null };
  name: string;
  path: string;
  label?: string;
  labelKey?: string;
  unit?: string;
  className?: string;
  onChange: (name: string, raw: string) => void;
  /** render the <select> ALONE, for dev's own row wrappers on the Motor tab. */
  bare?: boolean;
}) => {
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  // resolve via the doc path. As in DocSelect, a committed EMPTY id list means
  // "the params call returned nothing" and must stay empty; only a never-loaded
  // (null) field falls back to the full doc list.
  const ids = graph.optionIds(name);
  const rows = ids
    ? fanCmsOptionListByPath(doc, path, ids, labelKey)
    : fanCmsOptionListByPath(doc, path, null, labelKey);
  const hasRow = rows.some((r) => String(r.id) === valStr);
  const selLabel = valStr ? fanCmsSelectedLabelByPath(doc, path, value as any, labelKey) : '';
  const greyed = rows.length === 0;
  const control = (
    <select name={name} data-name={name}
      className={`${className}${greyed ? ' non-clickable' : ''}`} value={valStr}
      onChange={(e) => onChange(name, e.target.value)}>
      {valStr === '' && <option value="" />}
      {rows.map((r) => (
        <option key={String(r.id)} value={String(r.id)}>{String(r.label ?? '')}</option>
      ))}
      {valStr !== '' && !hasRow && <option value={valStr}>{selLabel || valStr}</option>}
    </select>
  );
  if (bare) return control;
  return (
    <SelectRow label={label ?? name} dataName={name} unit={unit} nonClickable={greyed}>
      {control}
    </SelectRow>
  );
};

// ---------------------------------------------------------------------------
// `nonClickable` greys the LABEL as well as the control — legacy adds the class
// to `span[name=<field>]` and to the select together (js:2907-2922).
const SelectRow = ({ label, dataName, unit, nonClickable, children }: {
  label: string; dataName: string; unit?: string; nonClickable?: boolean;
  children: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <span data-name={dataName} className={nonClickable ? 'non-clickable' : undefined}>{label}</span>
      {unit && <span className="percent right">{unit}</span>}
    </div>
    <div className="col-xs-7 input-part">{children}</div>
  </div>
);

export default FanSelector;
